/**
 * Organization Dashboard Component
 * Handles organization-specific dashboard access with authentication and view routing
 */

import React, { useState, useEffect, useRef, useCallback, useMemo, startTransition } from 'react';
import { useParams, useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Stack,
  Menu,
  Slider,
  FormControlLabel,
  Checkbox,
  Switch,
  Paper,
  Tabs,
  Tab,
  Tooltip,
  Avatar,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  InputAdornment,
  ButtonGroup,
  Fade,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  DialogContentText
} from '@mui/material';
import { 
  Business as BusinessIcon, 
  Public as PublicIcon, 
  VpnKey as TokenIcon, 
  Settings as SettingsIcon, 
  Share as ShareIcon, 
  ContentCopy as CopyIcon, 
  Code as CodeIcon, 
  Link as LinkIcon, 
  Visibility as VisibilityIcon, 
  VisibilityOff as VisibilityOffIcon,
  ZoomIn as ZoomInIcon, 
  ZoomOut as ZoomOutIcon, 
  Refresh as RefreshIcon,
  Restore as RestoreIcon,
  Language as LanguageIcon, 
  Palette as PaletteIcon, 
  Tune as TuneIcon, 
  Check as CheckIcon,
  SupervisorAccount as SuperAdminIcon,
  SwapHoriz as SwitchIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  ViewSidebar as PanelIcon,
  ColorLens as ColorLensIcon,
  ViewColumn as ViewColumnIcon,
  People as PeopleIcon,
  Info as InfoIcon,
  Help as HelpIcon,
  ErrorOutline as ErrorOutlineIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Campaign as CampaignIcon,
  WarningAmber as WarningAmberIcon,
  Search as SearchIcon,
  SelectAll as SelectAllIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  ExpandLess as ExpandLessIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  VpnKey as VpnKeyIcon,
  IndeterminateCheckBox as IndeterminateCheckBoxIcon,
  Warning as WarningIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  AccountTree as AccountTreeIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Article as ArticleIcon,
  Dashboard as DashboardIcon,
  Group as GroupIcon,
  Category as CategoryIcon,
  Label as LabelIcon,
  DragHandle as DragHandleIcon,
  AccountBox as AccountBoxIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import '../App.css'; // Import dashboard styling
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { debounce } from 'lodash';
import SchedulerStatusIndicator from '../shared/components/SchedulerStatusIndicator';
import { isEqual } from 'lodash';
import useOrganizationPillars from '../hooks/useOrganizationPillars';
import { 
  OrganizationThemeGenerator, 
  organizationThemeManager,
  organizationThemePresets
} from '../themes/organizationThemes';
import { loadOrganizationFont, preloadOrganizationFonts } from '../themes/fontLoader';
import { themes } from '../themes';
import AuthNavItem from './AuthNavItem';
import '../themes/organizationDashboard.css';

// Sortable Content Visibility Manager Component
const ContentVisibilityManager = ({ availablePillars, visiblePillars, onPillarToggle, selectedView, organizationData, onPillarOrderChange, onExpandedPillarsChange, setOrganizationData, sendSettingsUpdateToIframe, setVisiblePillars, iframeRef, handleResetToAdminDefault, orgSlug }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragDirection, setDragDirection] = useState(null); // 'up' or 'down'
  const [loading, setLoading] = useState(true);
  const [expandedPillars, setExpandedPillars] = useState(new Set());
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null);
  const [selectedPillarForColor, setSelectedPillarForColor] = useState(null);
  const [pillarColors, setPillarColors] = useState({});
  
  // Color palette for pillars - harmonized with design system
  const colorPalette = [
    '#005F94', // Primary - Deep UN Blue
    '#006D87', // Secondary - Deep teal
    '#5BB8A7', // Success - Teal 
    '#5B9CD1', // Info - Light primary
    '#D19C5B', // Warning - Warm orange
    '#D15B5B', // Error - Muted red
    '#49B3A8', // Actor civilians - Bright teal
    '#5B7BA5', // Battles - Clear blue
    '#4F97D1', // Strategic - Vibrant blue
    '#55A39E', // Riots - Bright teal
    '#7C95D1', // Actor identity - Periwinkle blue
    '#E6A845', // Actor external - Golden yellow
    '#D17C4F', // Actor political - Bright terra
    '#C15670', // Actor rebel - Bright rose
    '#E66A6A', // Explosions - Bright coral
    '#D4845C'  // Violence - Warm orange
  ];

  // Color picker handlers
  const handleColorPickerOpen = (event, pillar) => {
    setColorPickerAnchor(event.currentTarget);
    setSelectedPillarForColor(pillar);
  };

  const handleColorPickerClose = () => {
    setColorPickerAnchor(null);
    setSelectedPillarForColor(null);
  };

  const handleColorSelect = (color) => {
    if (selectedPillarForColor) {
      const newColors = {
        ...pillarColors,
        [selectedPillarForColor.id]: color
      };
      setPillarColors(newColors);
      
      // Update organization data with new colors
      setOrganizationData(prev => ({
        ...prev,
        pillarColors: newColors
      }));
      
      // Send color update to iframe immediately
      setTimeout(() => {
        sendSettingsUpdateToIframe({
          pillarColors: newColors
        });
      }, 50);
    }
    handleColorPickerClose();
  };

  const resetPillarColor = () => {
    if (selectedPillarForColor) {
      const newColors = { ...pillarColors };
      delete newColors[selectedPillarForColor.id];
      setPillarColors(newColors);
      
      // Update organization data
      setOrganizationData(prev => ({
        ...prev,
        pillarColors: newColors
      }));
      
      // Send color update to iframe immediately
      setTimeout(() => {
        sendSettingsUpdateToIframe({
          pillarColors: newColors
        });
      }, 50);
    }
    handleColorPickerClose();
  };

  // Get pillar color (custom or default)
  const getPillarColor = (pillar) => {
    return pillarColors[pillar.id] || pillar.color || pillar.pillar_color || '#005F94';
  };

  // Get pillar color with background opacity (matches KPI box background)
  const getPillarColorWithOpacity = (pillar) => {
    const hexColor = getPillarColor(pillar);
    try {
      // Convert hex to RGB
      const r = parseInt(hexColor.substring(1,3), 16);
      const g = parseInt(hexColor.substring(3,5), 16);
      const b = parseInt(hexColor.substring(5,7), 16);
      // Return with same opacity as KPI background (0.1)
      return `rgba(${r}, ${g}, ${b}, 0.1)`;
    } catch (error) {
      console.error('Error processing color for preview:', error);
      return 'rgba(0, 95, 148, 0.1)'; // Fallback primary blue with opacity
    }
  };




  // Set loading to false when availablePillars are loaded
  useEffect(() => {
    if (availablePillars.length > 0) {
      setLoading(false);
    }
  }, [availablePillars]);

  // Initialize pillar colors from organization data
  useEffect(() => {
    if (organizationData?.pillarColors) {
      setPillarColors(organizationData.pillarColors);
    }
  }, [organizationData?.pillarColors]);



  // Toggle pillar expansion
  const togglePillarExpansion = (pillarId) => {
    setExpandedPillars(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pillarId)) {
        newSet.delete(pillarId);
      } else {
        newSet.add(pillarId);
      }
      
      // Notify parent component about the change
      if (onExpandedPillarsChange) {
        onExpandedPillarsChange(newSet);
      }
      
      return newSet;
    });
  };

  // Since we now load admin-visible pillars directly as availablePillars, no additional filtering needed
  const allowedPillars = useMemo(() => {
    // Reduced logging - only log when there are issues
    
    // SAFETY CHECK: Filter out any React components that might have slipped through
    const safeAvailablePillars = availablePillars.filter(pillar => {
      if (!pillar || typeof pillar !== 'object') {
        console.warn('❌ ContentVisibilityManager: Filtering out non-object pillar:', typeof pillar);
        return false;
      }
      if (pillar.$$typeof || pillar._owner || pillar.type || pillar.props || pillar.ref) {
        console.error('❌ ContentVisibilityManager: React component detected in availablePillars:', {
          name: pillar.name || 'Unknown',
          hasReactProps: true
        });
        return false;
      }
      return true;
    });
    if (safeAvailablePillars.length !== availablePillars.length) {
      console.warn('⚠️ ContentVisibilityManager: Filtered out', availablePillars.length - safeAvailablePillars.length, 'contaminated pillars');
    }
    
    // Fallback to current visible pillars while admin baseline still loading
    if (safeAvailablePillars.length === 0 && Array.isArray(visiblePillars) && visiblePillars.length > 0) {
      return visiblePillars;
    }

    return safeAvailablePillars; // Admin-visible pillars are already filtered in fetchPillars
  }, [availablePillars, visiblePillars]);



  // Hierarchical pillars with collapsible folders
  const flattenedPillars = useMemo(() => {
    if (!allowedPillars || allowedPillars.length === 0) {
      // Fallback: use current visible pillars if admin-derived list is not ready
      if (Array.isArray(visiblePillars) && visiblePillars.length > 0) {
        return visiblePillars.map(p => ({
          ...p,
          level: 0,
          isParent: !p.parent_pillar_id,
          hasSubPillars: false,
          isExpanded: false
        }));
      }
      return [];
    }

    // CRITICAL FIX: Content Visibility Manager should show ALL pillars in admin default order
    // Visibility checkboxes indicate which are enabled, but order stays constant
    const orderedAllowedPillars = [...allowedPillars].sort((a, b) => {
      // Primary sort: custom_order (admin default order)
      const aCustom = typeof a.custom_order === 'number' ? a.custom_order : Infinity;
      const bCustom = typeof b.custom_order === 'number' ? b.custom_order : Infinity;
      if (aCustom !== bCustom) return aCustom - bCustom;
      
      // Secondary sort: order_index
      const aOrderIndex = typeof a.order_index === 'number' ? a.order_index : Infinity;
      const bOrderIndex = typeof b.order_index === 'number' ? b.order_index : Infinity;
      if (aOrderIndex !== bOrderIndex) return aOrderIndex - bOrderIndex;
      
      // Tertiary sort: name
      return (a.name || '').localeCompare(b.name || '');
    });

    // First, separate parent and child pillars
    const parentPillars = orderedAllowedPillars.filter(pillar => !pillar.parent_pillar_id);
    const childPillars = orderedAllowedPillars.filter(pillar => pillar.parent_pillar_id);

    // Group children by parent
    const childrenByParent = childPillars.reduce((acc, child) => {
      if (!acc[child.parent_pillar_id]) {
        acc[child.parent_pillar_id] = [];
      }
      acc[child.parent_pillar_id].push(child);
      return acc;
    }, {});

    // Apply search filter
    const filteredParents = parentPillars.filter(pillar => {
        if (!searchTerm) return true;
      
      // Include parent if it matches search
      if (pillar.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (pillar.description && pillar.description.toLowerCase().includes(searchTerm.toLowerCase()))) {
        return true;
      }
      
      // Include parent if any of its children match search
      const children = childrenByParent[pillar.id] || [];
      return children.some(child => 
        child.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (child.description && child.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    });

    // Build the hierarchical structure
    const result = [];
    
    filteredParents.forEach(parent => {
      const children = childrenByParent[parent.id] || [];
      const hasSubPillars = children.length > 0;
      const isExpanded = expandedPillars.has(parent.id);

      // Add parent pillar
      const parentItem = {
        ...parent,
        level: 0,
        isParent: true,
        hasSubPillars,
        isExpanded
      };

      // Apply search filter to parent name
      if (!searchTerm || 
          parent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (parent.description && parent.description.toLowerCase().includes(searchTerm.toLowerCase()))) {
        result.push(parentItem);
      }

      // Add children if parent is expanded or if we're searching
      if ((isExpanded || searchTerm) && hasSubPillars) {
        const filteredChildren = children.filter(child => {
          if (!searchTerm) return true;
          return child.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                 (child.description && child.description.toLowerCase().includes(searchTerm.toLowerCase()));
        });

        filteredChildren.forEach(child => {
          result.push({
            ...child,
            level: 1,
            isParent: false,
            hasSubPillars: false,
        isExpanded: false
          });
        });
      }
    });

    // Only log if there are issues or when debugging specific cases
    if (result.length === 0 && allowedPillars.length > 0) {
      console.warn('⚠️ FlattenedPillars: No pillars after filtering - possible issue');
    }
    
    return result;
  }, [allowedPillars, searchTerm, expandedPillars]);

  // Content Visibility shows all pillars for management

  // Drag and drop handlers for Content Visibility
  const handleDragStart = (e, visualIndex) => {
    const pillar = flattenedPillars[visualIndex];
    
    if (pillar.level === 1) {
      return; // Don't allow dragging sub-pillars
    }
    
    setDraggedIndex(visualIndex); // Store visual index for consistency
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
    
    // Add visual drag image
    const dragImage = e.currentTarget.cloneNode(true);
    dragImage.style.transform = 'rotate(3deg)';
    dragImage.style.opacity = '0.8';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const handleDragOver = (e, visualIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedIndex === null || draggedIndex === visualIndex) return;
    
    const targetPillar = flattenedPillars[visualIndex];
    if (targetPillar.level === 1) return; // Don't allow dropping on sub-pillars
    
    setDragOverIndex(visualIndex);
    setDragDirection(visualIndex > draggedIndex ? 'down' : 'up');
  };

  const handleDragLeave = (e) => {
    // Only clear if we're actually leaving the drop zone
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
      setDragDirection(null);
    }
  };

  const handleDrop = (e, dropVisualIndex) => {
    e.preventDefault();
    
    
    if (draggedIndex === null || draggedIndex === dropVisualIndex) {
      resetDragState();
      return;
    }

    // Get pillars directly from flattenedPillars
    const draggedPillar = flattenedPillars[draggedIndex];
    const targetPillar = flattenedPillars[dropVisualIndex];
    
    
    // Only allow dropping on parent pillars
    if (targetPillar.level === 1 || draggedPillar.level === 1) {
      resetDragState();
      return;
    }

    // Find the actual positions in the available pillars array
    const draggedParentIndex = allowedPillars.findIndex(p => p.id === draggedPillar.id);
    const targetParentIndex = allowedPillars.findIndex(p => p.id === targetPillar.id);
    
    
    if (draggedParentIndex === -1 || targetParentIndex === -1) {
      resetDragState();
      return;
    }

    // Reorder the pillars in the main state
    const newPillarOrder = [...allowedPillars];
    const [movedPillar] = newPillarOrder.splice(draggedParentIndex, 1);
    newPillarOrder.splice(targetParentIndex, 0, movedPillar);
    
    
    // SAFETY CHECK: Ensure no React components are passed
    const safeNewPillarOrder = newPillarOrder.filter(pillar => {
      if (!pillar || typeof pillar !== 'object') return false;
      if (pillar.$$typeof || pillar._owner || pillar.type || pillar.props || pillar.ref) {
        console.error('❌ ContentVisibilityManager: Filtered React component from newPillarOrder:', pillar.name || 'Unknown');
        return false;
      }
      return true;
    });
    
    if (safeNewPillarOrder.length !== newPillarOrder.length) {
      console.warn('⚠️ ContentVisibilityManager: Removed React components from pillar order change');
    }
    
    // Apply the new order with safe data
    onPillarOrderChange(safeNewPillarOrder);
    
    resetDragState();
  };

  const handleDragEnd = () => {
    resetDragState();
  };

  const resetDragState = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragDirection(null);
  };



  // Bulk actions
  const handleSelectAll = () => {
    availablePillars.forEach(pillar => {
      if (!visiblePillars.some(p => p.id === pillar.id)) {
        onPillarToggle(pillar);
      }
    });
  };

  const handleSelectNone = () => {
    visiblePillars.forEach(pillar => {
      onPillarToggle(pillar);
    });
  };

  // MOVED: handleResetToAdminDefault function moved below to access setPendingChanges state

  // Check if current settings differ from admin default
  const hasChangesFromAdminDefault = useMemo(() => {
    // RACE CONDITION FIX: Wait for both datasets to be properly loaded
    if (!availablePillars || availablePillars.length === 0) {
      return false;
    }
    
    // RACE CONDITION FIX: Don't calculate if visible pillars are still loading
    // During initial load, visiblePillars starts as empty array even when org has pillar settings
    if (visiblePillars.length === 0 && organizationData?.pillarSettings) {
      return false;
    }
    
    // Compare visibility: check if visible pillars match available pillars exactly
    const visibilityChanged = visiblePillars.length !== availablePillars.length ||
      !visiblePillars.every(vp => availablePillars.some(ap => ap.id === vp.id)) ||
      !availablePillars.every(ap => visiblePillars.some(vp => vp.id === ap.id));
    
    // Compare order: check if visible pillars are in the same order as available pillars
    const orderChanged = visiblePillars.length > 0 && 
      visiblePillars.some((vp, index) => {
        const expectedPillar = availablePillars.find(ap => ap.id === vp.id);
        const expectedIndex = availablePillars.findIndex(ap => ap.id === vp.id);
        return expectedIndex !== index;
      });
    
    const hasChanges = visibilityChanged || orderChanged;

    
    return hasChanges;
  }, [visiblePillars, availablePillars, organizationData?.pillarSettings]);

  return (
    <Stack spacing={1.5}>
      {/* Header with description and stats */}
      <Box>
        <Typography className="settings-form-caption" sx={{ mb: 1 }}>
          Control which content sections are visible in the dashboard for{' '}
          <strong>{selectedView?.name || 'this view'}</strong>. 
          Drag to reorder sections and toggle visibility.
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          p: 1.5, 
          bgcolor: 'action.hover',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <VisibilityIcon color="primary" sx={{ fontSize: '1rem' }} />
          <Typography variant="body2" sx={{ flex: 1 }}>
            <strong>{visiblePillars.length}</strong> of <strong>{allowedPillars.length}</strong> sections visible
            {allowedPillars.length < availablePillars.length && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                ({availablePillars.length - allowedPillars.length} sections restricted by master settings)
              </Typography>
            )}
          </Typography>
          {loading && (
            <CircularProgress size={16} sx={{ mr: 1, color: '#005F94' }} />
          )}
          {visiblePillars.length === 0 && !loading && (
            <Chip 
              size="small" 
              label="None visible" 
              color="warning" 
              variant="outlined"
            />
          )}
          {allowedPillars.length === 0 && !loading && (
            <Chip 
              size="small" 
              label="No sections available" 
              color="error" 
              variant="outlined"
            />
          )}
        </Box>
      </Box>

      {/* Search and Controls */}
      <Stack spacing={1}>
        <TextField
          size="small"
          placeholder="Search content sections..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchTerm('')}
                >
                  <ClearIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <ButtonGroup size="small" variant="outlined">
            <Button
              onClick={handleSelectAll}
              startIcon={<SelectAllIcon sx={{ fontSize: '0.875rem' }} />}
              sx={{ fontSize: '0.75rem', py: 0.5 }}
            >
              All
            </Button>
            <Button
              onClick={handleSelectNone}
              startIcon={<ClearIcon sx={{ fontSize: '0.875rem' }} />}
              sx={{ fontSize: '0.75rem', py: 0.5 }}
            >
              None
            </Button>
          {hasChangesFromAdminDefault && (
            <Button
              onClick={handleResetToAdminDefault}
              startIcon={<RestoreIcon sx={{ fontSize: '0.875rem' }} />}
              data-testid="reset-to-admin-default-button"
              sx={{ 
                fontSize: '0.75rem', 
                py: 0.5,
                color: 'warning.main',
                borderColor: 'warning.light',
                '&:hover': {
                  borderColor: 'warning.main',
                  backgroundColor: 'warning.light',
                  color: 'warning.contrastText'
                }
              }}
            >
              Reset to Admin Default
            </Button>
          )}
          </ButtonGroup>
        </Box>
      </Stack>

      {/* Sortable Content List */}
      <Box>
        {flattenedPillars.length === 0 ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4, 
            color: 'text.secondary',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1
          }}>
            <SearchIcon sx={{ fontSize: '2rem', mb: 1, opacity: 0.5 }} />
            <Typography variant="body2">
              No content sections found for "{searchTerm}"
            </Typography>
          </Box>
        ) : (
          <Box sx={{ 
            border: '1px solid', 
            borderColor: 'divider', 
            borderRadius: 1, 
            overflow: 'hidden',
            bgcolor: 'transparent'
          }}>
            {flattenedPillars.map((pillar, flatIndex) => {
              // Calculate if this pillar is visible (in the visiblePillars prop)
              // FIXED: Both parent and sub-pillars should check their own visibility status
              const isVisible = visiblePillars.some(vp => vp.id === pillar.id && vp.framework_id === pillar.framework_id);
              
              // Visual index is the same as flat index in Content Visibility
              const visualIndex = flatIndex;
              const isDragging = visualIndex >= 0 && draggedIndex === visualIndex;
              const isDragOver = visualIndex >= 0 && dragOverIndex === visualIndex;
              const isSubPillar = pillar.level === 1;
              const canDrag = !isSubPillar; // All parent pillars can be dragged regardless of visibility
              
              // All parent pillars can be dragged, sub-pillars inherit parent visibility
              
              // Reduced debug logging - only for first VISIBLE pillar when debugging
              const isFirstVisiblePillar = isVisible && visualIndex === 0;
              
              if (isFirstVisiblePillar && draggedIndex !== null) {
                console.log(`🚀 [DRAG] First visible pillar debug:`, {
                  name: pillar.name,
                  canDrag,
                  isVisible,
                  draggedVisualIndex: draggedIndex,
                  currentVisualIndex: visualIndex,
                  flatIndex
                });
              }

              return (
                <Box
                  key={`${pillar.id}-${pillar.framework_id}-${flatIndex}`}
                  draggable={canDrag}
                  onDragStart={(e) => canDrag && visualIndex >= 0 && handleDragStart(e, visualIndex)}
                  onDragOver={(e) => canDrag && visualIndex >= 0 && handleDragOver(e, visualIndex)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => canDrag && visualIndex >= 0 && handleDrop(e, visualIndex)}
                  onDragEnd={handleDragEnd}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    pl: isSubPillar ? 5 : 1.5,
                    borderBottom: flatIndex === flattenedPillars.length - 1 ? 'none' : '1px solid',
                    borderColor: 'divider',
                    bgcolor: (() => {
                      if (isDragging) return 'action.selected';
                      if (isDragOver) {
                        return dragDirection === 'up' ? 'primary.50' : 'secondary.50';
                      }
                      return 'background.paper';
                    })(),
                    opacity: isDragging ? 0.3 : (isVisible ? 1 : 0.6),
                    transform: isDragging ? 'rotate(2deg) scale(1.02)' : 'none',
                    cursor: canDrag ? 'grab' : 'default',
                    position: 'relative',
                    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      bgcolor: isDragging ? 'action.selected' : 
                               canDrag ? 'action.hover' : 'background.paper',
                      transform: isDragging ? 'rotate(2deg) scale(1.02)' : 
                                canDrag ? 'translateX(2px)' : 'none'
                    },
                    '&:active': {
                      cursor: canDrag ? 'grabbing' : 'default',
                      transform: canDrag ? 'scale(0.98)' : 'none'
                    },
                    // Drop indicator
                    ...(isDragOver && dragDirection && {
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        height: '3px',
                        bgcolor: 'primary.main',
                        borderRadius: 1,
                        ...(dragDirection === 'up' ? { top: -1.5 } : { bottom: -1.5 }),
                        zIndex: 1,
                        boxShadow: '0 0 6px rgba(25, 118, 210, 0.6)'
                      }
                    })
                  }}
                >
                  {/* Expand/Collapse Button for Parent Pillars */}
                  {pillar.hasSubPillars && !isSubPillar && (
                    <IconButton
                      size="small"
                      onClick={() => togglePillarExpansion(pillar.id)}
                      sx={{ 
                        mr: 1,
                        color: 'text.secondary',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          color: 'primary.main',
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      {pillar.isExpanded ? (
                        <ExpandLessIcon sx={{ fontSize: '1.1rem' }} />
                      ) : (
                        <ExpandMoreIcon sx={{ fontSize: '1.1rem' }} />
                      )}
                    </IconButton>
                  )}

                  {/* Drag Handle */}
                  {canDrag && (
                    <DragHandleIcon 
                      sx={{ 
                        color: isDragging ? 'primary.main' : 'text.secondary',
                        fontSize: '1.25rem',
                        mr: 2,
                        cursor: 'grab',
                        transition: 'color 0.2s ease',
                        '&:hover': {
                          color: 'primary.main'
                        }
                      }} 
                    />
                  )}



                  {/* Label and Description */}
                  <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: isSubPillar ? 400 : 500,
                      color: (() => {
                        if (isDragging) return 'primary.main';
                        if (isSubPillar) return isVisible ? 'primary.main' : 'text.secondary';
                        return 'text.primary';
                      })(),
                      fontSize: isSubPillar ? '0.8125rem' : '0.875rem',
                      transition: 'color 0.2s ease',
                        userSelect: 'none',
                        lineHeight: 1.2
                    }}
                  >
                    {pillar.name}
                  </Typography>
                    
                    {pillar.description && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: '-webkit-box',
                          color: 'text.secondary',
                          fontSize: isSubPillar ? '0.75rem' : '0.8125rem',
                          lineHeight: 1.3,
                          mt: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          WebkitLineClamp: pillar.isExpanded ? 4 : 2, // More lines when expanded
                          WebkitBoxOrient: 'vertical',
                          whiteSpace: 'normal',
                          maxWidth: '400px',
                          opacity: 0.8
                        }}
                      >
                        {pillar.description}
                      </Typography>
                    )}
                  </Box>

                  {/* Sub-pillar count for parent pillars */}
                  {!isSubPillar && (() => {
                    const subCount = flattenedPillars.filter(p => p.parentId === pillar.id).length;
                    const visibleSubCount = flattenedPillars.filter(p => 
                      p.parentId === pillar.id && 
                      visiblePillars.some(vp => vp.id === p.id && vp.framework_id === p.framework_id)
                    ).length;
                    
                    return subCount > 0 ? (
                      <Chip
                        size="small"
                        label={`${visibleSubCount}/${subCount}`}
                        sx={{ 
                          height: 18,
                          fontSize: '0.6875rem',
                          bgcolor: 'action.selected',
                          color: 'text.primary',
                          mr: 1,
                          '& .MuiChip-label': {
                            px: 0.75
                          }
                        }}
                      />
                    ) : null;
                  })()}

                  {/* Color Picker Button - Only for parent pillars */}
                  {!isSubPillar && (
                    <IconButton
                      size="small"
                      onClick={(e) => handleColorPickerOpen(e, pillar)}
                      sx={{
                        width: 24,
                        height: 24,
                        mr: 1,
                        bgcolor: getPillarColorWithOpacity(pillar),
                        border: '2px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <PaletteIcon sx={{ fontSize: '0.75rem', color: 'text.primary', opacity: 0.7 }} />
                    </IconButton>
                  )}

                  {/* Toggle Switch */}
                  <Switch
                    checked={isVisible}
                    onChange={() => onPillarToggle(pillar)}
                    size="small"
                    data-testid={`pillar-toggle-${pillar.key || pillar.name.toLowerCase().replace(/\s+/g, '-')}`}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: 'primary.main',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: 'primary.main',
                      },
                      '& .MuiSwitch-track': {
                        opacity: 0.5
                      }
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Color Picker Popover */}
      <Popover
        open={Boolean(colorPickerAnchor)}
        anchorEl={colorPickerAnchor}
        onClose={handleColorPickerClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ p: 2, maxWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Choose Color for {selectedPillarForColor?.name}
          </Typography>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(4, 1fr)', 
            gap: 1, 
            mb: 2 
          }}>
            {colorPalette.map((color) => {
              // Convert to rgba with same opacity as KPI background
              const colorWithOpacity = (() => {
                try {
                  const r = parseInt(color.substring(1,3), 16);
                  const g = parseInt(color.substring(3,5), 16);
                  const b = parseInt(color.substring(5,7), 16);
                  return `rgba(${r}, ${g}, ${b}, 0.1)`;
                } catch (error) {
                  return color;
                }
              })();

              return (
                <Box
                  key={color}
                  onClick={() => handleColorSelect(color)}
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: colorWithOpacity,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: pillarColors[selectedPillarForColor?.id] === color ? '3px solid' : '1px solid',
                    borderColor: pillarColors[selectedPillarForColor?.id] === color ? 'primary.main' : 'divider',
                    position: 'relative',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: 2
                    },
                    transition: 'all 0.2s ease',
                    // Add a small solid color indicator in the center
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: 8,
                      height: 8,
                      bgcolor: color,
                      borderRadius: '50%',
                      opacity: 0.8
                    }
                  }}
                />
              );
            })}
          </Box>
          
          <Button
            fullWidth
            size="small"
            onClick={resetPillarColor}
            startIcon={<RestoreIcon />}
            sx={{ fontSize: '0.75rem' }}
          >
            Reset to Default
          </Button>
        </Box>
      </Popover>
    </Stack>
  );
};

// Iframe Controls Panel
const IframeControlsPanel = ({ open, onClose, iframeRef }) => {
  const [zoom, setZoom] = useState(100);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30);

  const handleZoomChange = (event, newValue) => {
    setZoom(newValue);
    if (iframeRef.current) {
      iframeRef.current.style.transform = `scale(${newValue / 100})`;
      iframeRef.current.style.transformOrigin = 'top left';
      iframeRef.current.style.width = `${(100 / newValue) * 100}%`;
      iframeRef.current.style.height = `${(100 / newValue) * 100}%`;
    }
  };

  const handleRefresh = () => {
    if (iframeRef.current) {
      iframeRef.current.src = iframeRef.current.src;
    }
  };

  const resetZoom = () => {
    setZoom(100);
    if (iframeRef.current) {
      iframeRef.current.style.transform = 'none';
      iframeRef.current.style.width = '100%';
      iframeRef.current.style.height = '100%';
    }
  };

  return (
    <Menu
      open={open}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={{ top: 100, left: window.innerWidth - 350 }}
      PaperProps={{
        sx: { 
          width: 320, 
          maxHeight: 400,
          p: 2
        }
      }}
    >
      <Box>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TuneIcon />
          Dashboard Controls
        </Typography>
        
        <Divider sx={{ my: 2 }} />
        
        {/* Zoom Controls */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Zoom Level</Typography>
          <Box sx={{ px: 1 }}>
            <Slider
              value={zoom}
              onChange={handleZoomChange}
              min={50}
              max={200}
              step={10}
              marks={[
                { value: 50, label: '50%' },
                { value: 100, label: '100%' },
                { value: 200, label: '200%' }
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={value => `${value}%`}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button size="small" onClick={() => handleZoomChange(null, Math.max(50, zoom - 10))}>
              <ZoomOutIcon />
            </Button>
            <Button size="small" onClick={resetZoom}>Reset</Button>
            <Button size="small" onClick={() => handleZoomChange(null, Math.min(200, zoom + 10))}>
              <ZoomInIcon />
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Refresh Controls */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Refresh</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleRefresh}
              startIcon={<RefreshIcon />}
            >
              Refresh Now
            </Button>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                size="small"
              />
            }
            label="Auto Refresh"
          />
          {autoRefresh && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" gutterBottom display="block">
                Interval (seconds)
              </Typography>
              <Slider
                value={refreshInterval}
                onChange={(e, val) => setRefreshInterval(val)}
                min={10}
                max={300}
                step={10}
                valueLabelDisplay="auto"
                size="small"
              />
            </Box>
          )}
        </Box>


      </Box>
    </Menu>
  );
};

// Enhanced View Selector
const ViewSelector = ({ views, selectedView, onViewChange, orgSlug, labelText = 'View', currentViewCode }) => {
  const navigate = useNavigate();

  return (
    <FormControl 
      size="small" 
      className="organization-view-selector" 
      sx={{ 
        minWidth: 200,
        '& .MuiInputLabel-root': {
          color: 'white',
          '&.Mui-focused': {
            color: 'white'
          }
        },
        '& .MuiSelect-root': {
          color: 'white'
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(255, 255, 255, 0.23)'
        },
        '& .MuiSelect-icon': {
          color: 'white'
        }
      }}
    >
      <InputLabel sx={{ color: 'white' }}>
        <Box display="flex" alignItems="center" gap={0.5} sx={{ color: 'white' }}>
          <LanguageIcon fontSize="small" sx={{ color: 'white' }} />
          {labelText}
        </Box>
      </InputLabel>
      <Select
        value={selectedView?.code || currentViewCode || ''}
        onChange={(e) => {
          const newView = views.find(c => c.code === e.target.value);
          onViewChange(newView);
          navigate(`/org/${orgSlug}/${e.target.value}`);
        }}
        label="View"
        sx={{ 
          color: 'white',
          '& .MuiSelect-select': {
            color: 'white'
          }
        }}
      >
        {views.map((view) => (
          <MenuItem key={view.code} value={view.code}>
            <Box display="flex" alignItems="center" gap={1}>
              <span>{view.name}</span>
              <Chip 
                size="small" 
                label={view.code.toUpperCase()} 
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  '& .MuiChip-label': {
                    color: 'white'
                  }
                }}
              />
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

// Enhanced Share Dashboard Modal Component with real link management
const ShareDashboardModal = ({ open, onClose, orgSlug, view, organizationData }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [copied, setCopied] = useState('');
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [links, setLinks] = useState([]);
  const [createForm, setCreateForm] = useState({
    name: '',
    type: 'private', // 'private' | 'vanity'
    vanitySlug: '',
    allowedDomains: '' // comma-separated
  });
  
  // Basic share settings
  const [settings, setSettings] = useState({
    showHeader: false,
    showFooter: true,
    zoom: 100
  });

  // Advanced embed settings (from EmbedPage)
  const [resizeMode, setResizeMode] = useState('fixedHeight');
  const [aspectRatioX, setAspectRatioX] = useState(4);
  const [aspectRatioY, setAspectRatioY] = useState(3.3);
  const [scale, setScale] = useState(0.92);
  const [minHeight, setMinHeight] = useState(600);
  const [eventsCollapsed, setEventsCollapsed] = useState(false);
  const [eventsWidth, setEventsWidth] = useState(25);
  const [selectedTheme, setSelectedTheme] = useState('situation-room-default');

  const baseUrl = window.location.origin;

  // Load existing share links when modal opens
  useEffect(() => {
    const loadLinks = async () => {
      try {
        setLoadingLinks(true);
        const api = await import('../api/shareLinks');
        const { success, links: items } = await api.listShareLinks(orgSlug);
        if (success) setLinks(items || []);
      } catch (e) {
        console.error('Failed to load share links', e);
      } finally {
        setLoadingLinks(false);
      }
    };
    if (open && orgSlug) loadLinks();
  }, [open, orgSlug]);

  const handleCreateLink = async () => {
    try {
      const api = await import('../api/shareLinks');
      const payload = {
        viewCode: view?.toLowerCase?.() || view,
        name: createForm.name || `${(view || '').toUpperCase()} public link`,
        type: createForm.type,
        vanitySlug: createForm.type === 'vanity' && createForm.vanitySlug ? createForm.vanitySlug.trim() : undefined,
        allowedDomains: createForm.allowedDomains
          ? createForm.allowedDomains.split(',').map(s => s.trim()).filter(Boolean)
          : undefined
      };
      const res = await api.createShareLink(orgSlug, payload);
      if (res?.success) {
        // Refresh list
        const listRes = await api.listShareLinks(orgSlug);
        if (listRes.success) setLinks(listRes.links || []);
        setCreateForm({ name: '', type: 'private', vanitySlug: '', allowedDomains: '' });
      }
    } catch (e) {
      console.error('Failed to create share link', e);
    }
  };

  // NOTE: copyToClipboard declared once (used across tabs)
  
  // Get container and iframe configuration for advanced embeds
  const getContainerConfig = () => {
    if (resizeMode === 'aspectRatio') {
      return {
        width: 'auto',
        overflow: 'hidden',
        aspectRatio: `${aspectRatioX}/${aspectRatioY}`,
      };
    } else {
      return {
        width: 'auto',
        overflow: 'hidden',
        height: `${minHeight}px`,
      };
    }
  };

  const getIframeConfig = () => {
    if (resizeMode === 'aspectRatio') {
      return {
        width: `calc((100%)*${1 / scale})`,
        aspectRatio: `${aspectRatioX}/${aspectRatioY}`,
        transform: `scale(${scale})`,
        transformOrigin: '0 0',
      };
    } else {
      return {
        width: `calc((100%)*${1 / scale})`,
        height: `${minHeight * (1 / scale)}px`,
        transform: `scale(${scale})`,
        transformOrigin: '0 0',
      };
    }
  };

  // Generate embed URLs and codes
  const getEmbedUrl = (isAdvanced = false) => {
    const params = new URLSearchParams({
      token: publicToken, // Include token for public access
      showHeader: settings.showHeader.toString(),
      ...(isAdvanced && {
        eventsCollapsed: eventsCollapsed.toString(),
        eventsWidth: eventsWidth.toString(),
        theme: selectedTheme,
      })
    });

    // Include organization theme parameters
    const themeParams = organizationData?.branding ? 
      OrganizationThemeGenerator.generateIframeThemeParams(organizationData.branding) : '';
    
    return `${baseUrl}/${view}?${params.toString()}${themeParams}`;
  };

  const getSimpleIframeCode = () => {
    const embedUrl = getEmbedUrl(false);
    return `<iframe 
  src="${embedUrl}" 
  width="100%" 
  height="600" 
  frameborder="0" 
  style="border: none; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);"
  title="${organizationData?.name} - ${view} Dashboard">
</iframe>`;
  };

  const getAdvancedEmbedCode = () => {
    const embedUrl = getEmbedUrl(true);
    const containerConfig = getContainerConfig();
    const iframeConfig = getIframeConfig();
    
    const containerStyle = Object.entries(containerConfig)
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ');
    
    const iframeStyle = Object.entries(iframeConfig)
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ');

    return `<div style="${containerStyle}">
  <iframe 
    src="${embedUrl}" 
    frameborder="0" 
    style="${iframeStyle}" 
    allowfullscreen
    title="${organizationData?.name} - ${view} Dashboard">
  </iframe>
</div>`;
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(type);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ShareIcon />
          Share Dashboard
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
            <Tab icon={<LinkIcon />} label="Share Links" />
            <Tab icon={<CodeIcon />} label="Simple Embed" />
            <Tab icon={<SettingsIcon />} label="Advanced Embed" />
            <Tab icon={<TokenIcon />} label="API Access" />
          </Tabs>
        </Box>

        {/* Share Links Tab */}
        {activeTab === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>Create Link</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <TextField label="Name" size="small" fullWidth value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select label="Type" value={createForm.type} onChange={e => setCreateForm(f => ({ ...f, type: e.target.value }))}>
                    <MenuItem value="private">Private (unguessable)</MenuItem>
                    <MenuItem value="vanity">Vanity (friendly URL)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {createForm.type === 'vanity' && (
                <Grid item xs={12} md={5}>
                  <TextField label="Vanity slug" size="small" fullWidth placeholder="e.g. situation-overview" value={createForm.vanitySlug} onChange={e => setCreateForm(f => ({ ...f, vanitySlug: e.target.value }))} />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField label="Allowed domains (for iframe)" size="small" fullWidth placeholder="example.org, *.sub.example.org" value={createForm.allowedDomains} onChange={e => setCreateForm(f => ({ ...f, allowedDomains: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" onClick={handleCreateLink} disabled={loadingLinks}>Create Link</Button>
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom>Active Links</Typography>
            {loadingLinks ? (
              <Typography variant="body2">Loading...</Typography>
            ) : links.length === 0 ? (
              <Typography variant="body2" color="textSecondary">No links yet.</Typography>
            ) : (
              <Stack spacing={2}>
                {links.filter(l => l.viewCode === (view || '').toLowerCase()).map((l) => {
                  const publicUrl = l.urls?.vanityUrl || `${baseUrl}/public/${orgSlug}/${(view || '').toLowerCase()}?t=TOKEN_HIDDEN`;
                  const embedUrl = l.urls?.embedUrl || `${baseUrl}/embed/TOKEN_HIDDEN`;
                  return (
                    <Paper key={l.id} sx={{ p: 2 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Box>
                          <Typography variant="subtitle2">{l.name}</Typography>
                          <Typography variant="caption" color="textSecondary">Views: {l.viewsCount || 0} {l.lastAccessedAt ? ` • Last opened: ${new Date(l.lastAccessedAt).toLocaleString()}` : ''}</Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField value={publicUrl} size="small" InputProps={{ readOnly: true }} sx={{ minWidth: 360 }} />
                          <Button variant="outlined" onClick={() => copyToClipboard(publicUrl, `copy-${l.id}`)}>{copied === `copy-${l.id}` ? 'Copied!' : 'Copy'}</Button>
                          <Button variant="outlined" onClick={() => copyToClipboard(embedUrl, `embed-${l.id}`)}>Embed</Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Box>
        )}

        {/* Simple Embed Tab */}
        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>Simple Embed Code</Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Basic embed code with access token for public integration into your website. 
              No authentication required.
            </Typography>
            
            {/* Basic Settings */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Basic Settings</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.showHeader}
                    onChange={(e) => setSettings(prev => ({ ...prev, showHeader: e.target.checked }))}
                  />
                }
                label="Show Header"
              />
            </Box>

            <Paper sx={{ p: 2, bgcolor: 'grey.100', mb: 2, position: 'relative' }}>
              <pre style={{ 
                margin: 0, 
                whiteSpace: 'pre-wrap', 
                wordWrap: 'break-word',
                fontSize: '12px',
                fontFamily: 'monospace',
                lineHeight: 1.4
              }}>
                {getSimpleIframeCode()}
              </pre>
              <IconButton
                onClick={() => copyToClipboard(getSimpleIframeCode(), 'simple-embed')}
                sx={{ position: 'absolute', top: 8, right: 8 }}
              >
                {copied === 'simple-embed' ? <CheckIcon /> : <CopyIcon />}
              </IconButton>
            </Paper>
          </Box>
        )}

        {/* Advanced Embed Tab */}
        {activeTab === 2 && (
          <Box>
            <Grid container spacing={3}>
              {/* Configuration Column */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Configuration</Typography>
                
                <Stack spacing={3}>
                  {/* Resize Mode */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Resize Mode</Typography>
                    <ToggleButtonGroup
                      value={resizeMode}
                      exclusive
                      onChange={(e, value) => value && setResizeMode(value)}
                      size="small"
                      fullWidth
                    >
                      <ToggleButton value="fixedHeight">Fixed Height</ToggleButton>
                      <ToggleButton value="aspectRatio">Aspect Ratio</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  {/* Scale */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Zoom Scale: {Math.round(scale * 100)}%
                    </Typography>
                    <Slider
                      value={scale}
                      onChange={(e, value) => setScale(value)}
                      min={0.1}
                      max={1}
                      step={0.01}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                    />
                  </Box>

                  {/* Aspect Ratio Settings */}
                  {resizeMode === 'aspectRatio' && (
                    <Stack direction="row" spacing={2}>
                      <TextField
                        label="Width Ratio"
                        value={aspectRatioX}
                        type="number"
                        size="small"
                        onChange={(e) => setAspectRatioX(parseFloat(e.target.value) || 4)}
                      />
                      <TextField
                        label="Height Ratio"
                        value={aspectRatioY}
                        type="number"
                        size="small"
                        onChange={(e) => setAspectRatioY(parseFloat(e.target.value) || 3.3)}
                      />
                    </Stack>
                  )}

                  {/* Fixed Height Settings */}
                  {resizeMode === 'fixedHeight' && (
                    <TextField
                      label="Height (px)"
                      value={minHeight}
                      type="number"
                      size="small"
                      fullWidth
                      onChange={(e) => setMinHeight(parseInt(e.target.value) || 600)}
                    />
                  )}

                  {/* Events Panel Settings */}
                  <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Events Panel</Typography>
                    <Stack spacing={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={eventsCollapsed}
                            onChange={(e) => setEventsCollapsed(e.target.checked)}
                          />
                        }
                        label="Collapse events panel"
                      />
                      {!eventsCollapsed && (
                        <Box>
                          <Typography variant="caption" gutterBottom display="block">
                            Panel Width: {eventsWidth}%
                          </Typography>
                          <Slider
                            value={eventsWidth}
                            onChange={(e, value) => setEventsWidth(value)}
                            min={15}
                            max={40}
                            step={1}
                            valueLabelDisplay="auto"
                          />
                        </Box>
                      )}
                    </Stack>
                  </Box>

                  {/* Theme Selection */}
                  <FormControl fullWidth size="small">
                    <InputLabel>Theme</InputLabel>
                    <Select
                      value={selectedTheme}
                      label="Theme"
                      onChange={(e) => setSelectedTheme(e.target.value)}
                    >
                      {Object.keys(themes).map((themeKey) => (
                        <MenuItem key={themeKey} value={themeKey}>
                          {themeKey.split('-').map(word => 
                            word.charAt(0).toUpperCase() + word.slice(1)
                          ).join(' ')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Grid>

              {/* Code & Preview Column */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Advanced Embed Code</Typography>
                
                <Paper sx={{ p: 2, bgcolor: 'grey.100', mb: 2, position: 'relative', maxHeight: 300, overflow: 'auto' }}>
                  <pre style={{ 
                    margin: 0, 
                    whiteSpace: 'pre-wrap', 
                    wordWrap: 'break-word',
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    lineHeight: 1.3
                  }}>
                    {getAdvancedEmbedCode()}
                  </pre>
                  <IconButton
                    onClick={() => copyToClipboard(getAdvancedEmbedCode(), 'advanced-embed')}
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                  >
                    {copied === 'advanced-embed' ? <CheckIcon /> : <CopyIcon />}
                  </IconButton>
                </Paper>

                {/* Live Preview */}
                <Typography variant="subtitle2" gutterBottom>Live Preview</Typography>
                <Paper sx={{ p: 1, bgcolor: 'grey.50', height: 200, overflow: 'hidden' }}>
                  <div style={getContainerConfig()}>
                    <iframe
                      src={getEmbedUrl(true)}
                      style={{
                        ...getIframeConfig(),
                        border: 'none'
                      }}
                      title="Preview"
                    />
                  </div>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* API Access Tab */}
        {activeTab === 3 && (
          <Box>
            <Typography variant="h6" gutterBottom>API Access</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              API access tokens allow programmatic access to dashboard data. 
              Contact your administrator to generate API tokens.
            </Alert>
            <Button variant="outlined" disabled>
              Generate API Token (Coming Soon)
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Right Panel Component
const DashboardSettingsPanel = ({ 
  open, 
  onClose, 
  organizationData, 
  orgSlug, 
  onSave,
  availableViews,
  selectedView,
  availablePillars,
  visiblePillars,
  onPillarToggle,
  onPillarOrderChange,
  onExpandedPillarsChange,
  iframeRef,
  // NEW: Pending changes props
  hasPendingChanges,
  pendingChanges,
  savePendingChanges,
  discardPendingChanges,
  // Color management props
  setOrganizationData,
  sendSettingsUpdateToIframe,
  setVisiblePillars,
  handleResetToAdminDefault,
  // Super admin props
  isSuperAdmin
}) => {
  const { user, setUser } = useAuth(); // Access user for fallback data and updates
  const [activeSection, setActiveSection] = useState('account');
  
  const [zoom, setZoom] = useState(100);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [preserveFontsOnPreset, setPreserveFontsOnPreset] = useState(true); // Default: preserve fonts

  const [fontsLoaded, setFontsLoaded] = useState(false);
  
  // User Management State moved to DashboardSettingsPanel
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // User management modal state
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    role: 'member'
  });
  const [userFormErrors, setUserFormErrors] = useState({});
  const [savingUser, setSavingUser] = useState(false);
  
  // Password reset state
  const [passwordResetDialog, setPasswordResetDialog] = useState({
    open: false,
    user: null,
    loading: false,
    result: null
  });
  
  // Track if user has made modifications to prevent auto-sync overwrites
  const [userHasModifiedSettings, setUserHasModifiedSettings] = useState(false);

  // Logo upload state
  const [logoUploading, setLogoUploading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  
  // Track unsaved avatar for cleanup on reset
  const [unsavedAvatarUrl, setUnsavedAvatarUrl] = useState(null);

  // Logo upload function
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    try {
      setLogoUploading(true);
      
      const formData = new FormData();
      formData.append('logo', file);
      formData.append('organizationSlug', orgSlug);

      const response = await fetch('/api/organizations/upload-logo', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Upload endpoint not implemented yet. Please contact your administrator.');
        }
        throw new Error('Failed to upload logo');
      }

      const result = await response.json();
      
      // Update both headerLogoUrl and legacy logoUrl atomically to avoid race condition
      // Using setSettings directly instead of two handleSettingChange calls
      setSettings(prev => {
        const newSettings = {
          ...prev,
          headerLogoUrl: result.logoUrl,
          logoUrl: result.logoUrl
        };
        
        // Trigger iframe update with the new settings
        debouncedIframeUpdate(newSettings);
        
        // Send UPDATE_SETTINGS with branding for immediate logo application
        // Do NOT include font fields — logo upload should not change fonts
        if (iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage({
            type: 'UPDATE_SETTINGS',
            settings: {
              branding: {
                primaryColor: newSettings.primaryColor,
                secondaryColor: newSettings.secondaryColor,
                headerStyle: newSettings.headerStyle,
                headerTitle: newSettings.headerTitle,
                headerSubtitle: newSettings.headerSubtitle,
                headerLogoUrl: newSettings.headerLogoUrl,
                headerLogoStyle: newSettings.headerLogoStyle
              }
            }
          }, '*');
        }
        
        return newSettings;
      });
      
      // Mark that user has made modifications
      setUserHasModifiedSettings(true);
      
    } catch (error) {
      console.error('Failed to upload logo:', error);
      alert('Failed to upload logo. Please try again.');
    } finally {
      setLogoUploading(false);
      // Clear the file input
      event.target.value = '';
    }
  };

  // Helper function to determine if the dialog user is the current user
  const isCurrentUser = (dialogUser) => {
    if (!dialogUser) return false;
    
    // First try to match with main user object
    if (user?.email && dialogUser.email === user.email) {
      return true;
    }
    
    // Fallback: if no main user but we have users loaded, check if dialog user is in the first position
    // (assuming first user in the loaded list is the current user in test mode)
    if (!user?.email && users.length > 0 && dialogUser.email === users[0]?.email) {
      return true;
    }
    
    return false;
  };

  // Avatar upload function
  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 2MB for avatars)
    if (file.size > 2 * 1024 * 1024) {
      alert('File size must be less than 2MB');
      return;
    }

    // Check if we have user information
    if (!user?.id && users.length === 0) {
      alert('User information not available. Please try refreshing the page.');
      return;
    }

    // Determine current user ID - try to find current user in users list first
    let currentUserId = user?.id;
    let currentUser = user;
    
    // If no user from auth, try to find current user in loaded users list
    if (!currentUserId && users.length > 0) {
      // In test mode, look for a user that matches some criteria or just use first user
      currentUser = users[0]; // For now, use first user as fallback
      currentUserId = currentUser?.id;
    }
    
    if (!currentUserId) {
      alert('Unable to identify current user. Please try refreshing the page.');
      return;
    }

    try {
      setAvatarUploading(true);
      
      const formData = new FormData();
      formData.append('avatar', file);
      formData.append('userId', currentUserId);

      const headers = {
        'X-Test-Org': orgSlug,
      };
      
      // Add super admin header if user is super admin
      if (isSuperAdmin) {
        headers['x-test-user'] = 'admin';
      }

      const response = await fetch(`/api/organization-settings/users/${currentUserId}/upload-avatar`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Avatar upload endpoint not implemented yet. Please contact your administrator.');
        }
        throw new Error('Failed to upload avatar');
      }

      const result = await response.json();
      
      // Update the avatar URL in profile settings
      handleSettingChange('myProfile', { 
        ...(settings.myProfile || {}), 
        avatarUrl: result.avatarUrl 
      });
      
      // Track this as an unsaved avatar for potential cleanup on reset
      setUnsavedAvatarUrl(result.avatarUrl);
      
      // Update the global user object so AuthNavItem shows the new avatar
      if (user) {
        setUser({ 
          ...user, 
          avatar: result.avatarUrl,
          avatarUrl: result.avatarUrl 
        });
      }
      
      
      // Reload user list to show the new avatar
      await loadOrganizationUsers();
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      alert('Failed to upload avatar. Please try again.');
    } finally {
      setAvatarUploading(false);
      // Clear the file input
      event.target.value = '';
    }
  };

  const handleAvatarRemove = async () => {
    try {
      setAvatarUploading(true);

      // Determine if this is an unsaved avatar (newly uploaded) or saved avatar
      const isUnsavedAvatar = !!unsavedAvatarUrl;
      const originalAvatarUrl = originalSettings.myProfile?.avatarUrl;
      
      if (isUnsavedAvatar) {
        // For unsaved avatars, delete from backend immediately
        const currentUserId = user?.id || (users.length > 0 ? users[0]?.id : null);
        
        if (!currentUserId) {
          alert('Unable to identify current user. Please try refreshing the page.');
          return;
        }

        const headers = {
          'X-Test-Org': orgSlug,
        };
        
        if (isSuperAdmin) {
          headers['x-test-user'] = 'admin';
        }

        const response = await fetch(`/api/organization-settings/users/${currentUserId}/avatar`, {
          method: 'DELETE',
          credentials: 'include',
          headers
        });

        if (!response.ok) {
          throw new Error('Failed to remove avatar');
        }

        setUnsavedAvatarUrl(null);
        await loadOrganizationUsers();
        
        // For unsaved avatars, restore to original saved avatar (if any)
        handleSettingChange('myProfile', { 
          ...(settings.myProfile || {}), 
          avatarUrl: originalAvatarUrl 
        });
        
        // Update global user object to restore original avatar
        if (user) {
          setUser({ 
            ...user, 
            avatar: originalAvatarUrl,
            avatarUrl: originalAvatarUrl 
          });
        }
      } else {
        // For saved avatars, only update local settings (will be deleted from backend on save)
        
        // For saved avatars, set to null to mark for deletion
        handleSettingChange('myProfile', { 
          ...(settings.myProfile || {}), 
          avatarUrl: null 
        });
        
        // Update global user object to remove avatar
        if (user) {
          setUser({ 
            ...user, 
            avatar: null,
            avatarUrl: null 
          });
        }
      }
      
    } catch (error) {
      console.error('Failed to remove avatar:', error);
      alert('Failed to remove avatar. Please try again.');
    } finally {
      setAvatarUploading(false);
    }
  };

  const isDev = import.meta.env.DEV;
  const loadUsersInFlightRef = useRef(false);

  // Load organization users function with debouncing to prevent excessive calls
  const loadOrganizationUsers = useCallback(async () => {
    if (isDev) console.log('🔍 Loading organization users...', { orgSlug, user: user ? 'available' : 'missing' });
    
    if (!orgSlug) {
      if (isDev) console.log('❌ No orgSlug available');
      return;
    }
    if (loadUsersInFlightRef.current) {
      if (isDev) console.log('⏭️ Skipping user load - already in progress (ref guard)');
      return;
    }
    loadUsersInFlightRef.current = true;
    
    // Prevent duplicate calls by checking if already loading
    if (loadingUsers) {
      if (isDev) console.log('⏭️ Skipping user load - already in progress');
      loadUsersInFlightRef.current = false;
      return;
    }
    
    try {
      setLoadingUsers(true);
      const headers = {
        'X-Test-Org': orgSlug,
      };
      
      // Add super admin header if user is super admin
      if (isSuperAdmin) {
        headers['x-test-user'] = 'admin';
      }
      
      const response = await fetch(`/api/organization-settings/users`, {
        credentials: 'include',
        headers
      });
      
      if (response.ok) {
        const userData = await response.json();
        if (isDev) console.log('✅ Loaded organization users:', userData);
        setUsers(userData);

        // Sync global auth user avatar from org users or local profile settings
        try {
          const currentEmail = user?.email;
          if (currentEmail && Array.isArray(userData)) {
            const match = userData.find(u => u?.email === currentEmail || u?.id === user?.id);
            const candidateAvatar = match?.avatar || settings.myProfile?.avatarUrl || user?.avatarUrl || user?.avatar;
            if (candidateAvatar && (user?.avatar !== candidateAvatar || user?.avatarUrl !== candidateAvatar)) {
              setUser({ ...user, avatar: candidateAvatar, avatarUrl: candidateAvatar });
            }
          }
        } catch (e) {
          console.warn('Avatar sync skipped:', e);
        }
      } else {
        if (isDev) console.log('⚠️ User management API not available, using fallback data');
        // Fallback to include current user if API fails
        if (user) {
          if (isDev) console.log('✅ Setting fallback user data:', user);
          setUsers([{
            id: user.id || 'current',
            email: user.email,
            name: user.name || user.email,
            role: user.role || 'admin',
            status: 'active',
            last_active: 'now',
            avatar: user.avatar
          }]);
        } else {
          if (isDev) console.log('❌ No user data available for fallback');
          setUsers([]);
        }
      }
    } catch (error) {
      if (isDev) console.log('⚠️ User management service unavailable, using fallback data');
      // Fallback to include current user
      if (user) {
        if (isDev) console.log('✅ Setting fallback user data after error:', user);
        setUsers([{
          id: user.id || 'current',
          email: user.email,
          name: user.name || user.email,
          role: user.role || 'admin',
          status: 'active',
          last_active: 'now',
          avatar: user.avatar
        }]);
      } else {
        if (isDev) console.log('❌ No user data available for fallback after error');
        setUsers([]);
      }
    } finally {
      setLoadingUsers(false);
      loadUsersInFlightRef.current = false;
    }
  }, [orgSlug, user, isSuperAdmin]);
  
  // Load users when component mounts - REMOVED: Consolidated with settings panel useEffect to prevent duplicate calls
  
  // Track original settings for change detection
  const [originalSettings, setOriginalSettings] = useState({});
  
  // Local state for settings
  const [settings, setSettings] = useState({
    primaryColor: organizationData?.branding?.primaryColor || '#005F94',
    secondaryColor: organizationData?.branding?.secondaryColor || '#006D87',
    primaryFontFamily: organizationData?.branding?.primaryFontFamily || 'Open Sans',
    secondaryFontFamily: organizationData?.branding?.secondaryFontFamily || 'Open Sans',
    // Keep legacy fontFamily for backward compatibility
    fontFamily: organizationData?.branding?.fontFamily || 'Open Sans',
    logoUrl: organizationData?.branding?.logo || '',
    dashboardTitle: organizationData?.branding?.title || organizationData?.name || '',
    subtitle: organizationData?.branding?.subtitle || '',
    disclaimerText: organizationData?.branding?.disclaimerText || '',
    // Header Configuration
    headerStyle: organizationData?.branding?.headerStyle || 'full',
    headerTitle: organizationData?.branding?.headerTitle || selectedView?.name || organizationData?.name || 'Situation Room AI',
    headerSubtitle: organizationData?.branding?.headerSubtitle || 'Humanitarian Dashboard',
    headerLogoUrl: organizationData?.branding?.headerLogoUrl || '',
    headerLogoStyle: organizationData?.branding?.headerLogoStyle || 'normal',
    headerDisclaimerText: organizationData?.branding?.headerDisclaimerText || 'Test Environment',
    headerDisclaimerBody: organizationData?.branding?.headerDisclaimerBody || 'This application is currently in testing and is for demonstration purposes only.',
    headerDisclaimerIcon: organizationData?.branding?.headerDisclaimerIcon || 'WarningAmberIcon',
    statusIndicatorStyle: organizationData?.branding?.statusIndicatorStyle || 'rounded',
    headerColorMode: organizationData?.branding?.headerColorMode || 'primary',
    showStatusSchedule: organizationData?.branding?.showStatusSchedule !== false,
    showDisclaimer: organizationData?.branding?.showDisclaimer !== false,
    // Account Settings
    organizationName: organizationData?.name || '',
    organizationDescription: organizationData?.description || '',
    // My Profile Settings
    myProfile: {
      name: user?.name || '',
      email: user?.email || '',
      description: user?.description || ''
    }
  });

  // User Management Functions
  const handleAddUser = () => {
    setEditingUser(null);
    setUserForm({
      name: '',
      email: '',
      role: 'member',
      description: ''
    });
    setUserFormErrors({});
    setUserDialogOpen(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    
    // Map backend role values to dropdown values
    let mappedRole = user.role || 'member';
    if (user.role === 'org_admin' || user.role === 'super_admin') {
      mappedRole = 'admin';
    }
    
    setUserForm({
      name: user.name || '',
      email: user.email || '',
      role: mappedRole,
      description: user.description || ''
    });
    setUserFormErrors({});
    setUserDialogOpen(true);
  };

  const handleUserFormChange = (field, value) => {
    setUserForm(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (userFormErrors[field]) {
      setUserFormErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateUserForm = () => {
    const errors = {};
    
    if (!userForm.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!userForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userForm.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!userForm.role) {
      errors.role = 'Role is required';
    }
    
    // Check for duplicate email (excluding current user when editing)
    const duplicateUser = users.find(u => 
      u.email.toLowerCase() === userForm.email.toLowerCase() && 
      (!editingUser || u.id !== editingUser.id)
    );
    
    if (duplicateUser) {
      errors.email = 'A user with this email already exists';
    }
    
    setUserFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveUser = async () => {
    if (!validateUserForm()) {
      return;
    }

    setSavingUser(true);
    try {
      const headers = {
        'Content-Type': 'application/json',
        'X-Test-Org': orgSlug,
      };
      
      // Add super admin header if user is super admin
      if (isSuperAdmin) {
        headers['x-test-user'] = 'admin';
      }

      if (editingUser) {
        // Update existing user
        const response = await fetch(`/api/organization-settings/users/${editingUser.id}`, {
          method: 'PUT',
          credentials: 'include',
          headers,
          body: JSON.stringify({
            name: userForm.name,
            email: userForm.email,
            role: userForm.role,
            description: userForm.description,
            status: 'active'
          })
        });

        if (response.ok) {
          const updatedUser = await response.json();
          setUsers(prev => prev.map(u => u.id === editingUser.id ? updatedUser : u));
        } else {
          throw new Error('Failed to update user');
        }
      } else {
        // Invite new user
        const response = await fetch('/api/organization-settings/users/invite', {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify(userForm)
        });

        if (response.ok) {
          const newUser = await response.json();
          setUsers(prev => [...prev, newUser]);
        } else {
          const error = await response.json();
          throw new Error(error.error || 'Failed to invite user');
        }
      }

      setUserDialogOpen(false);
    } catch (error) {
      console.error('Error saving user:', error);
      setUserFormErrors({ general: error.message });
    } finally {
      setSavingUser(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user from the organization?')) {
      return;
    }

    try {
      const headers = {
        'X-Test-Org': orgSlug,
      };
      
      // Add super admin header if user is super admin
      if (isSuperAdmin) {
        headers['x-test-user'] = 'admin';
      }

      const response = await fetch(`/api/organization-settings/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers
      });

      if (response.ok) {
        setUsers(prev => prev.filter(u => u.id !== userId));
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to remove user');
      }
    } catch (error) {
      console.error('Error removing user:', error);
      alert('Failed to remove user: ' + error.message);
    }
  };

  const handleResetPassword = (userItem) => {
    setPasswordResetDialog({
      open: true,
      user: userItem,
      loading: false,
      result: null
    });
  };

  const confirmPasswordReset = async () => {
    const { user } = passwordResetDialog;
    
    setPasswordResetDialog(prev => ({ ...prev, loading: true, result: null }));

    try {
      const headers = {
        'X-Test-Org': orgSlug,
        'Content-Type': 'application/json'
      };
      
      // Add super admin header if user is super admin
      if (isSuperAdmin) {
        headers['x-test-user'] = 'admin';
      }

      const response = await fetch(`/api/organization-settings/users/${user.id}/reset-password`, {
        method: 'POST',
        credentials: 'include',
        headers
      });

      const data = await response.json();

      if (response.ok) {
        setPasswordResetDialog(prev => ({
          ...prev,
          loading: false,
          result: {
            success: true,
            message: data.message,
            tempPassword: data.tempPassword
          }
        }));
      } else {
        throw new Error(data.error || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setPasswordResetDialog(prev => ({
        ...prev,
        loading: false,
        result: {
          success: false,
          message: error.message
        }
      }));
    }
  };

  const closePasswordResetDialog = () => {
    setPasswordResetDialog({
      open: false,
      user: null,
      loading: false,
      result: null
    });
  };

  const handleZoomChange = (event, newValue) => {
    setZoom(newValue);
    if (iframeRef.current) {
      iframeRef.current.style.transform = `scale(${newValue / 100})`;
      iframeRef.current.style.transformOrigin = 'top left';
      iframeRef.current.style.width = `${(100 / newValue) * 100}%`;
      iframeRef.current.style.height = `${(100 / newValue) * 100}%`;
    }
  };

  const handleRefresh = () => {
    if (iframeRef.current) {
      iframeRef.current.src = iframeRef.current.src;
    }
  };

  const resetZoom = () => {
    setZoom(100);
    if (iframeRef.current) {
      iframeRef.current.style.transform = 'none';
      iframeRef.current.style.width = '100%';
      iframeRef.current.style.height = '100%';
    }
  };

  // Helper function to generate iframe URL with current settings
  const generateIframeUrl = (currentSettings = settings) => {
    const currentView = selectedView;
    if (!currentView) return null;

    // Create base URL (prefer ISO3 lower-case when present)
    const viewPath = (currentView.iso3 ? String(currentView.iso3).toLowerCase() : String(currentView.code).toLowerCase());
    const baseUrl = `${window.location.origin}/${viewPath}`;
    
    // Create URL with theme parameters
    const params = new URLSearchParams({
      showHeader: (currentSettings.headerStyle !== 'none').toString(),
      headerStyle: currentSettings.headerStyle,
      orgIframe: 'true',
      orgPrimaryColor: currentSettings.primaryColor,
      orgSecondaryColor: currentSettings.secondaryColor
    });
    // Only include font params if (a) DB has a font value, or (b) user modified font from default
    // This avoids sending fallback 'Open Sans' when user is only editing title/colors
    const dbPrimaryFont = organizationData?.branding?.primaryFontFamily;
    const dbSecondaryFont = organizationData?.branding?.secondaryFontFamily;
    const dbFont = organizationData?.branding?.fontFamily;
    const defaultFont = 'Open Sans';
    if (dbPrimaryFont || currentSettings.primaryFontFamily !== defaultFont) params.set('orgPrimaryFontFamily', currentSettings.primaryFontFamily);
    if (dbSecondaryFont || currentSettings.secondaryFontFamily !== defaultFont) params.set('orgSecondaryFontFamily', currentSettings.secondaryFontFamily);
    if (dbFont || currentSettings.fontFamily !== defaultFont) params.set('orgFontFamily', currentSettings.fontFamily);

    // Preserve orgSlug in the iframe URL so the embedded app can detect org context
    if (orgSlug) params.set('orgSlug', orgSlug);

    // Dev-only: propagate ?test-user=... so iframe requests can use header-based auth
    if (import.meta.env.DEV) {
      try {
        const parentParams = new URLSearchParams(window.location.search);
        const testUser = parentParams.get('test-user');
        if (testUser) params.set('test-user', testUser);
      } catch (_e) {}
    }
    
    // Add header parameters if header is visible
    if (currentSettings.headerStyle !== 'none') {
      // Always pass headerTitle and headerSubtitle, use fallbacks if empty
      const headerTitle = currentSettings.headerTitle || currentView?.name || organizationData?.name || 'Situation Room AI';
      const headerSubtitle = currentSettings.headerSubtitle || 'Humanitarian Dashboard';
      
      params.set('headerTitle', headerTitle);
      params.set('headerSubtitle', headerSubtitle);
      if (currentSettings.headerLogoUrl) params.set('headerLogo', currentSettings.headerLogoUrl);
      if (currentSettings.headerLogoStyle) params.set('headerLogoStyle', currentSettings.headerLogoStyle);
      params.set('headerDisclaimerText', currentSettings.headerDisclaimerText);
      params.set('headerDisclaimerBody', currentSettings.headerDisclaimerBody);
      params.set('headerDisclaimerIcon', currentSettings.headerDisclaimerIcon);
      params.set('statusIndicatorStyle', currentSettings.statusIndicatorStyle);
      params.set('headerColorMode', currentSettings.headerColorMode);
    }
    
    // Always set showStatusSchedule and showDisclaimer regardless of header style
    params.set('showStatusSchedule', currentSettings.showStatusSchedule.toString());
    params.set('showDisclaimer', currentSettings.showDisclaimer.toString());
    
    // Add legacy branding parameters for backward compatibility
    if (currentSettings.logoUrl) params.set('orgLogo', currentSettings.logoUrl);
    if (currentSettings.dashboardTitle) params.set('orgTitle', currentSettings.dashboardTitle);
    if (currentSettings.subtitle) params.set('orgSubtitle', currentSettings.subtitle);
    if (currentView?.name) params.set('orgViewTitle', currentView.name);
    
    // Add pillar settings
    const orgPillarSettings = organizationData?.pillarSettings?.[currentView.code] || [];
    if (orgPillarSettings.length > 0) {
      params.set('orgPillars', orgPillarSettings.join(','));
    }
    
    return `${baseUrl}?${params.toString()}`;
  };

  // Update iframe URL parameters without reload with throttling
  const updateIframeSettings = useCallback((newSettings) => {
    if (iframeRef.current) {
      try {
        const newUrl = generateIframeUrl(newSettings);
        if (newUrl) {
          // Parse the new URL to get just the search parameters
          const urlObj = new URL(newUrl);
          const newParams = urlObj.search;
          
          // Try to update URL parameters in iframe without reload
          if (iframeRef.current.contentWindow) {
            // Add a stable preview session id for correlation across logs
            const dbgPreviewId = (window.__orgPreviewId = (window.__orgPreviewId || Math.random().toString(36).slice(2)));
          // Send message to iframe to update its URL parameters
          iframeRef.current.contentWindow.postMessage({
            type: 'UPDATE_URL_PARAMS',
            params: newParams,
            previewId: dbgPreviewId
          }, '*');
          }
        }
      } catch (error) {
        console.warn('Failed to update iframe parameters:', error);
        // Fallback to full URL update
        const newUrl = generateIframeUrl(newSettings);
        if (newUrl && newUrl !== iframeRef.current.src) {
          iframeRef.current.src = newUrl;
        }
      }
    }
  }, [availableViews, organizationData, location.pathname]);

  // Throttled version for rapid UI changes
  const throttledUpdateIframeSettings = useCallback(
    debounce((newSettings) => {
      updateIframeSettings(newSettings);
    }, 25), // Further reduced since PanelGroup remounts cleanly
    [updateIframeSettings]
  );

  // Debounced iframe update for rapid changes (color pickers, sliders)
  const debouncedIframeUpdate = useCallback(
    debounce((newSettings) => {
      updateIframeSettings(newSettings);
    }, 300),
    [updateIframeSettings]
  );

  // Track previous header state to prevent unnecessary updates
  const prevHeaderStateRef = useRef(settings.headerStyle);
  
  const handleSettingChange = (key, value) => {
    let newSettings = {
      ...settings,
      [key]: value
    };
    
    // Ensure legacy fontFamily is updated when primary font changes
    if (key === 'primaryFontFamily') {
      newSettings.fontFamily = value;
    }
    
    setSettings(newSettings);
    
    // Mark that user has made modifications
    setUserHasModifiedSettings(true);
    
    // Update organization name immediately in header
    if (key === 'organizationName' && setOrganizationData) {
      setOrganizationData(prev => ({
        ...prev,
        name: value
      }));
    }
    
    // Load font when fontFamily changes AND send UPDATE_SETTINGS for immediate application
    if (key === 'fontFamily' || key === 'primaryFontFamily' || key === 'secondaryFontFamily') {
      loadOrganizationFont(value);
      
      // CRITICAL FIX: Also send UPDATE_SETTINGS with branding for immediate font application
      const brandingUpdate = {
        branding: {
          primaryColor: newSettings.primaryColor,
          secondaryColor: newSettings.secondaryColor,
          headerStyle: newSettings.headerStyle,
          headerTitle: newSettings.headerTitle,
          headerSubtitle: newSettings.headerSubtitle,
          fontFamily: newSettings.fontFamily,
          primaryFontFamily: newSettings.primaryFontFamily,
          secondaryFontFamily: newSettings.secondaryFontFamily
        }
      };
      
      // Send settings update immediately for font changes
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({
          type: 'UPDATE_SETTINGS',
          settings: brandingUpdate
        }, '*');
      }
    }
    
    // Update iframe in real-time without full reload using PostMessage
    if (key === 'primaryColor' || key === 'secondaryColor' || key === 'headerStyle' || key === 'headerTitle' || key === 'headerSubtitle' || key === 'headerLogoUrl' || key === 'headerLogoStyle') {
      // For theme colors, header style, and logo, send both URL params AND settings update with branding
      debouncedIframeUpdate(newSettings);
      
      // CRITICAL FIX: Also send UPDATE_SETTINGS with branding for immediate theme/logo application
      const isFontChange = key === 'fontFamily' || key === 'primaryFontFamily' || key === 'secondaryFontFamily';
      const brandingUpdate = {
        branding: {
          primaryColor: newSettings.primaryColor,
          secondaryColor: newSettings.secondaryColor,
          headerStyle: newSettings.headerStyle,
          headerTitle: newSettings.headerTitle,
          headerSubtitle: newSettings.headerSubtitle,
          headerLogoUrl: newSettings.headerLogoUrl,
          headerLogoStyle: newSettings.headerLogoStyle,
          // Only send font fields when user is actually changing a font setting
          // Otherwise the settings panel's fallback defaults override the iframe's actual font
          ...(isFontChange ? {
            fontFamily: newSettings.fontFamily,
            primaryFontFamily: newSettings.primaryFontFamily,
            secondaryFontFamily: newSettings.secondaryFontFamily
          } : {})
        }
      };
      
      // Send settings update immediately for theme/logo changes
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({
          type: 'UPDATE_SETTINGS',
          settings: brandingUpdate
        }, '*');
      }
    } else if (key === 'showStatusSchedule' || key === 'showDisclaimer') {
      // For other UI changes
      // Use standard throttled updates - layout is now responsive
      throttledUpdateIframeSettings(newSettings);
    } else {
      // Use throttled updates for other settings to prevent rapid-fire messages
      throttledUpdateIframeSettings(newSettings);
    }
  };

  // Initialize original settings when organizationData changes
  // Also handles view switches: detects view change and force-syncs both originalSettings AND settings
  const syncViewCodeRef = useRef(selectedView?.code);
  useEffect(() => {
    // Detect view change — this effect fires BEFORE the separate reset effect,
    // so userHasModifiedSettings may still be stale (true from previous view edits).
    // On a view change we MUST update both originalSettings and settings unconditionally.
    const viewChanged = syncViewCodeRef.current !== selectedView?.code;
    if (viewChanged) {
      syncViewCodeRef.current = selectedView?.code;
      // Reset modification flag here (before the sync) so the state is clean
      setUserHasModifiedSettings(false);
      // Clean up unsaved avatar from server before clearing tracking state
      if (unsavedAvatarUrl) {
        cleanupUnsavedAvatar(); // fire-and-forget — cleanup is best-effort
      }
      setUnsavedAvatarUrl(null);
    }

    // Preserve existing avatar if already set, otherwise use current user avatar
    const currentUserFromList = users.length > 0 && (users.find(u => u.email === user?.email || u.id === 'test-admin') || users[0]);
    const existingAvatarUrl = originalSettings.myProfile?.avatarUrl || currentUserFromList?.avatar || user?.avatar || null;
    
    // When the view just changed, organizationData.branding.headerTitle may still hold
    // the PREVIOUS view's custom title (async clearing hasn't applied yet).
    // Use selectedView.name as the safe fallback on view change.
    const headerTitleValue = viewChanged
      ? (selectedView?.name || organizationData?.name || 'Situation Room AI')
      : (organizationData?.branding?.headerTitle || selectedView?.name || organizationData?.name || 'Situation Room AI');

    const newOriginalSettings = {
      primaryColor: organizationData?.branding?.primaryColor || '#005F94',
      secondaryColor: organizationData?.branding?.secondaryColor || '#006D87',
      primaryFontFamily: organizationData?.branding?.primaryFontFamily || 'Open Sans',
      secondaryFontFamily: organizationData?.branding?.secondaryFontFamily || 'Open Sans',
      fontFamily: organizationData?.branding?.fontFamily || 'Open Sans',
      logoUrl: organizationData?.branding?.logo || '',
      dashboardTitle: organizationData?.branding?.title || organizationData?.name || '',
      subtitle: organizationData?.branding?.subtitle || '',
      disclaimerText: organizationData?.branding?.disclaimerText || '',
      headerStyle: organizationData?.branding?.headerStyle || 'full',
      headerTitle: headerTitleValue,
      headerSubtitle: organizationData?.branding?.headerSubtitle || 'Humanitarian Dashboard',
      headerLogoUrl: organizationData?.branding?.headerLogoUrl || '',
      headerLogoStyle: organizationData?.branding?.headerLogoStyle || 'normal',
      headerDisclaimerText: organizationData?.branding?.headerDisclaimerText || 'Test Environment',
      headerDisclaimerBody: organizationData?.branding?.headerDisclaimerBody || 'This application is currently in testing and is for demonstration purposes only.',
      headerDisclaimerIcon: organizationData?.branding?.headerDisclaimerIcon || 'WarningAmberIcon',
      statusIndicatorStyle: organizationData?.branding?.statusIndicatorStyle || 'rounded',
      headerColorMode: organizationData?.branding?.headerColorMode || 'primary',
      showStatusSchedule: organizationData?.branding?.showStatusSchedule !== false,
      showDisclaimer: organizationData?.branding?.showDisclaimer !== false,
      // Account Settings
      organizationName: organizationData?.name || '',
      organizationDescription: organizationData?.description || '',
      // Profile Settings - preserve existing avatar to prevent resets
      myProfile: {
        name: user?.name || '',
        email: user?.email || '',
        description: user?.description || '',
        avatarUrl: existingAvatarUrl
      }
    };
    
    // Compare all branding-derived fields (not just 4) to catch any change
    const brandingKeys = ['primaryColor', 'secondaryColor', 'primaryFontFamily', 'secondaryFontFamily',
      'fontFamily', 'logoUrl', 'dashboardTitle', 'subtitle', 'disclaimerText', 'headerStyle',
      'headerTitle', 'headerSubtitle', 'headerLogoUrl', 'headerLogoStyle', 'headerDisclaimerText',
      'headerDisclaimerBody', 'headerDisclaimerIcon', 'statusIndicatorStyle', 'headerColorMode',
      'showStatusSchedule', 'showDisclaimer', 'organizationName', 'organizationDescription'];
    const shouldUpdate = viewChanged || !originalSettings || !originalSettings.myProfile ||
      brandingKeys.some(k => originalSettings[k] !== newOriginalSettings[k]);
    
    if (shouldUpdate) {
      setOriginalSettings(newOriginalSettings);
      
      // On view change: ALWAYS sync both (previous edits don't apply to new view)
      // Otherwise: only sync if user hasn't modified settings
      if (viewChanged || !userHasModifiedSettings) {
        setSettings(newOriginalSettings);
      }
      
    }
  }, [organizationData, selectedView?.code]); // Re-run on view switch to update headerTitle fallback

  // Update original settings when users are loaded (only on true initial load)
  useEffect(() => {
    if (users.length > 0 && originalSettings.myProfile && !originalSettings.myProfile.avatarUrl && !unsavedAvatarUrl) {
      // Find current user's avatar from the backend
      const currentUserFromList = users.find(u => u.email === user?.email || u.id === 'test-admin') || users[0];
      const savedAvatarUrl = currentUserFromList?.avatar;
      
      if (savedAvatarUrl) {
        // Update original settings to include the saved avatar
        const updatedOriginalSettings = {
          ...originalSettings,
          myProfile: {
            ...originalSettings.myProfile,
            avatarUrl: savedAvatarUrl
          }
        };
        setOriginalSettings(updatedOriginalSettings);
        
        // Also update current settings if user hasn't modified them
        if (!userHasModifiedSettings) {
          setSettings(updatedOriginalSettings);
        }
        
      }
    }
  }, [users, unsavedAvatarUrl]); // Add unsavedAvatarUrl as dependency to prevent updates after upload

  // Load users when component mounts or organization changes
  const usersEffectKeyRef = useRef('');
  useEffect(() => {
    const shouldLoad = !!organizationData && !!open;
    const effectKey = shouldLoad ? `${orgSlug}:open` : `${orgSlug}:closed`;

    if (isDev) {
      console.log('🔍 useEffect for loading users triggered:', { 
        organizationData: organizationData ? 'available' : 'missing', 
        open, 
        shouldLoad
      });
    }

    if (organizationData && open) {
      if (usersEffectKeyRef.current === effectKey) return;
      usersEffectKeyRef.current = effectKey;
      loadOrganizationUsers();
    } else {
      usersEffectKeyRef.current = effectKey;
      if (isDev) {
        console.log('❌ Users not loaded because:', { 
          organizationData: !organizationData ? 'missing' : 'available', 
          open: !open ? 'panel closed' : 'panel open'
        });
      }
    }
  }, [organizationData, open, orgSlug, loadOrganizationUsers, isDev]);

  // Unified change detection for settings and pillars
  const hasChanges = useMemo(() => {
    // Check settings changes (use deep comparison for object values like myProfile)
    const settingsChanged = Object.keys(settings).some(key => {
      const currentValue = settings[key];
      const originalValue = originalSettings[key];
      if (currentValue === originalValue) return false;
      // Deep compare objects (e.g. myProfile) to avoid false positives from reference differences
      if (typeof currentValue === 'object' && typeof originalValue === 'object' &&
          currentValue !== null && originalValue !== null) {
        return JSON.stringify(currentValue) !== JSON.stringify(originalValue);
      }
      return true;
    });

    // Check pillar changes
    const pillarChanges = hasPendingChanges;

    return settingsChanged || pillarChanges;
  }, [settings, originalSettings, hasPendingChanges]);

  // Unified save function for both settings and pillars
  const applyAllSettings = async () => {
    
    // Check if a saved avatar was marked for deletion
    const originalAvatarUrl = originalSettings.myProfile?.avatarUrl;
    const currentAvatarUrl = settings.myProfile?.avatarUrl;
    const savedAvatarWasRemoved = originalAvatarUrl && !currentAvatarUrl && !unsavedAvatarUrl;
    
    if (savedAvatarWasRemoved) {
      try {
        // Delete the saved avatar from backend
        const currentUserId = user?.id || (users.length > 0 ? users[0]?.id : null);
        
        if (currentUserId) {
          const headers = {
            'X-Test-Org': orgSlug,
          };
          
          if (isSuperAdmin) {
            headers['x-test-user'] = 'admin';
          }

          const response = await fetch(`/api/organization-settings/users/${currentUserId}/avatar`, {
            method: 'DELETE',
            credentials: 'include',
            headers
          });

          if (response.ok) {
            // Don't reload users immediately - let natural state updates handle this
          }
        }
      } catch (error) {
        console.warn('Failed to delete saved avatar during save:', error);
        // Don't fail the entire save operation for this
      }
    }
    
    // Save settings (must await to ensure backend save completes before resetting local state)
    await onSave(settings);
    
    // Save pillar changes if any
    if (hasPendingChanges) {
      await savePendingChanges();
    }
    
    // Build final saved state: incorporate unsaved avatar if present
    const savedSettings = unsavedAvatarUrl
      ? { ...settings, myProfile: { ...settings.myProfile, avatarUrl: unsavedAvatarUrl } }
      : { ...settings };
    
    // Sync both originalSettings and settings to the same object so hasChanges = false
    setSettings(savedSettings);
    setOriginalSettings(savedSettings);
    
    // Reset modification flag after successful save
    setUserHasModifiedSettings(false);
    
    // Clear unsaved avatar tracking since settings are now saved
    setUnsavedAvatarUrl(null);
    
  };

  // Clean up unsaved avatar
  const cleanupUnsavedAvatar = async () => {
    if (!unsavedAvatarUrl) return;
    
    try {
      // Determine current user ID
      let currentUserId = user?.id;
      if (!currentUserId && users.length > 0) {
        currentUserId = users[0]?.id;
      }
      
      if (!currentUserId) return;

      const headers = {
        'X-Test-Org': orgSlug,
      };
      
      if (isSuperAdmin) {
        headers['x-test-user'] = 'admin';
      }

      const response = await fetch(`/api/organization-settings/users/${currentUserId}/avatar`, {
        method: 'DELETE',
        credentials: 'include',
        headers
      });

      if (response.ok) {
      }
    } catch (error) {
      console.warn('Failed to cleanup unsaved avatar:', error);
      // Don't throw - this is cleanup, not critical
    } finally {
      setUnsavedAvatarUrl(null);
    }
  };

  // Reset all changes to original state
  const resetAllChanges = async () => {
    // Clean up any unsaved avatar first
    await cleanupUnsavedAvatar();
    
    // Use CONSISTENT font fallbacks that match the initial settings 
    const primaryFontFamily = organizationData?.branding?.primaryFontFamily || 'Open Sans';
    const secondaryFontFamily = organizationData?.branding?.secondaryFontFamily || 'Open Sans';
    const baseFontFamily = organizationData?.branding?.fontFamily || 'Open Sans';
    
    // Reset settings to true original from database (with same fallbacks as initial settings)
    const trueOriginalSettings = {
      primaryColor: organizationData?.branding?.primaryColor || '#005F94',
      secondaryColor: organizationData?.branding?.secondaryColor || '#006D87',
      primaryFontFamily: primaryFontFamily,
      secondaryFontFamily: secondaryFontFamily,
      fontFamily: baseFontFamily,
      logoUrl: organizationData?.branding?.logo || '',
      dashboardTitle: organizationData?.branding?.title || organizationData?.name || '',
      subtitle: organizationData?.branding?.subtitle || '',
      disclaimerText: organizationData?.branding?.disclaimerText || '',
      headerStyle: organizationData?.branding?.headerStyle || 'full',
      headerTitle: organizationData?.branding?.headerTitle || selectedView?.name || organizationData?.name || 'Situation Room AI',
      headerSubtitle: organizationData?.branding?.headerSubtitle || 'Humanitarian Dashboard',
      headerLogoUrl: organizationData?.branding?.headerLogoUrl || '',
      headerLogoStyle: organizationData?.branding?.headerLogoStyle || 'normal',
      headerDisclaimerText: organizationData?.branding?.headerDisclaimerText || 'Test Environment',
      headerDisclaimerBody: organizationData?.branding?.headerDisclaimerBody || 'This application is currently in testing and is for demonstration purposes only.',
      headerDisclaimerIcon: organizationData?.branding?.headerDisclaimerIcon || 'WarningAmberIcon',
      statusIndicatorStyle: organizationData?.branding?.statusIndicatorStyle || 'rounded',
      headerColorMode: organizationData?.branding?.headerColorMode || 'primary',
      showStatusSchedule: organizationData?.branding?.showStatusSchedule !== false,
      showDisclaimer: organizationData?.branding?.showDisclaimer !== false,
      // Account Settings (must match sync effect shape to prevent false hasChanges)
      organizationName: organizationData?.name || '',
      organizationDescription: organizationData?.description || '',
      // Reset myProfile to original saved state
      myProfile: originalSettings.myProfile || {
        name: '',
        email: '',
        description: '',
        avatarUrl: null
      }
    };
    
    
    setSettings(trueOriginalSettings);
    
    // CRITICAL: Update original settings to match reset state (clears "unsaved changes")
    setOriginalSettings(trueOriginalSettings);
    
    // Reset modification flag since we're reverting to original
    setUserHasModifiedSettings(false);
    
    // Update iframe with reset settings
    throttledUpdateIframeSettings(trueOriginalSettings);
    
    // CRITICAL FIX: Also send UPDATE_SETTINGS with branding for immediate reset application
    // Only include font fields if DB actually has them — avoid sending fallback defaults
    const resetFontFields = {};
    if (organizationData?.branding?.fontFamily) resetFontFields.fontFamily = trueOriginalSettings.fontFamily;
    if (organizationData?.branding?.primaryFontFamily) resetFontFields.primaryFontFamily = trueOriginalSettings.primaryFontFamily;
    if (organizationData?.branding?.secondaryFontFamily) resetFontFields.secondaryFontFamily = trueOriginalSettings.secondaryFontFamily;
    const brandingUpdate = {
      branding: {
        primaryColor: trueOriginalSettings.primaryColor,
        secondaryColor: trueOriginalSettings.secondaryColor,
        headerStyle: trueOriginalSettings.headerStyle,
        headerTitle: trueOriginalSettings.headerTitle,
        headerSubtitle: trueOriginalSettings.headerSubtitle,
        ...resetFontFields
      }
    };
    
    // Send settings update immediately for reset
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({
        type: 'UPDATE_SETTINGS',
        settings: brandingUpdate
      }, '*');
    }
    
    // Reset pillar changes (now async)
    if (hasPendingChanges) {
      await discardPendingChanges();
    }
    
    // Reload user list to reflect avatar changes (removal of unsaved or restoration of saved)
    await loadOrganizationUsers();
    
  };

  const applySettings = () => {
    onSave(settings);
  };

  // Handle theme preset selection
  const handleThemePresetSelect = (presetKey) => {
    const preset = organizationThemePresets[presetKey];
    if (preset) {
      /* 
       * FONT PRESERVATION LOGIC:
       * 
       * Theme presets now preserve existing font settings to avoid unexpected
       * font changes when users only want to apply color schemes.
       * 
       * BEHAVIOR:
       * - Colors: Always applied from preset
       * - Fonts: Only applied if user has default/inconsistent fonts, otherwise preserved
       * 
       * This prevents the jarring experience of fonts changing when applying
       * color-focused presets like "UN Blue".
       */
      
      // Determine whether to apply font changes based on user preference
      const shouldPreserveFonts = preserveFontsOnPreset;
      
      const newSettings = {
        ...settings,
        primaryColor: preset.primaryColor,
        secondaryColor: preset.secondaryColor,
        backgroundColor: preset.backgroundColor || settings.backgroundColor,
        // USER-CONTROLLED FONT APPLICATION: Respect user's font preservation preference
        ...(shouldPreserveFonts ? {
          // Preserve current fonts when user prefers it
        } : {
          // Apply preset fonts when user wants full theme replacement
        primaryFontFamily: preset.fontFamily,
        secondaryFontFamily: preset.fontFamily,
        fontFamily: preset.fontFamily
        })
      };
      
      setSettings(newSettings);
      
      // Mark as modified so DB fetches don't silently overwrite preset changes
      setUserHasModifiedSettings(true);
      
      // Use throttled update for theme preset to prevent conflicts
      throttledUpdateIframeSettings(newSettings);
      
      // CRITICAL FIX: Also send UPDATE_SETTINGS with branding for immediate theme preset application
      // Only send font fields if preset is explicitly changing them (not when preserving fonts)
      const presetFontFields = shouldPreserveFonts ? {} : {
        fontFamily: newSettings.fontFamily,
        primaryFontFamily: newSettings.primaryFontFamily,
        secondaryFontFamily: newSettings.secondaryFontFamily
      };
      const brandingUpdate = {
        branding: {
          primaryColor: newSettings.primaryColor,
          secondaryColor: newSettings.secondaryColor,
          headerStyle: newSettings.headerStyle,
          headerTitle: newSettings.headerTitle,
          headerSubtitle: newSettings.headerSubtitle,
          ...presetFontFields
        }
      };
      
      // Send settings update immediately for theme preset changes
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({
          type: 'UPDATE_SETTINGS',
          settings: brandingUpdate
        }, '*');
      }
      
    }
  };

  // Check if current settings match a preset
  const getCurrentPreset = () => {
    return Object.entries(organizationThemePresets).find(([key, preset]) => 
      preset.primaryColor === settings.primaryColor && 
      preset.secondaryColor === settings.secondaryColor &&
      preset.fontFamily === settings.fontFamily
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      className="settings-panel dashboard-settings-panel"
      data-testid="dashboard-settings-panel"
      aria-label="Dashboard Settings Panel"
      sx={{
        width: 360,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 360,
          boxSizing: 'border-box',
          borderLeft: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
          height: 'calc(100vh - 64px)',
          position: 'fixed',
          right: 0,
          top: '64px',
          zIndex: 1200
        },
      }}
    >
      {/* Panel Header */}
      <Box className="settings-panel-header">
        <Box display="flex" alignItems="center" gap={1}>
          <PanelIcon sx={{ color: 'white', fontSize: '1.1rem' }} />
          <Typography variant="subtitle1" fontWeight="600" sx={{ color: 'white', fontSize: '0.95rem' }}>
            Settings
          </Typography>
        </Box>
        <IconButton 
          onClick={onClose} 
          sx={{ color: 'white', p: 0.5 }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Settings Content */}
      <Box className="settings-panel-content" sx={{ flex: 1, overflow: 'auto' }}>
        {/* Viewport Controls Section */}
        <Accordion 
          className="settings-accordion settings-panel-section" 
          expanded={activeSection === 'viewport'} 
          onChange={() => setActiveSection(activeSection === 'viewport' ? '' : 'viewport')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box className="settings-section-header">
              <TuneIcon />
              <Typography variant="subtitle1">Viewport Controls</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              {/* Zoom Controls */}
              <Box className="settings-form-section">
                <Typography className="settings-form-label" gutterBottom>Zoom Level</Typography>
                <Box sx={{ px: 1 }}>
                  <Slider
                    className="settings-slider"
                    value={zoom}
                    onChange={handleZoomChange}
                    min={50}
                    max={200}
                    step={10}
                    marks={[
                      { value: 50, label: '50%' },
                      { value: 100, label: '100%' },
                      { value: 200, label: '200%' }
                    ]}
                    valueLabelDisplay="auto"
                    valueLabelFormat={value => `${value}%`}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button size="small" onClick={() => handleZoomChange(null, Math.max(50, zoom - 10))}>
                    <ZoomOutIcon />
                  </Button>
                  <Button size="small" onClick={resetZoom}>Reset</Button>
                  <Button size="small" onClick={() => handleZoomChange(null, Math.min(200, zoom + 10))}>
                    <ZoomInIcon />
                  </Button>
                </Box>
              </Box>

              {/* Refresh Controls */}
              <Box className="settings-form-section">
                <Typography className="settings-form-label" gutterBottom>Refresh</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button 
                    className="settings-button settings-button-secondary"
                    variant="outlined" 
                    size="small" 
                    onClick={handleRefresh}
                    startIcon={<RefreshIcon />}
                    fullWidth
                  >
                    Refresh Dashboard
                  </Button>
                </Box>
                <FormControlLabel
                  className="settings-switch"
                  control={
                    <Switch
                      checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Auto Refresh"
                />
                {autoRefresh && (
                  <Box sx={{ mt: 1 }}>
                    <Typography className="settings-form-caption" gutterBottom display="block">
                      Interval: {refreshInterval}s
                    </Typography>
                    <Slider
                      className="settings-slider"
                      value={refreshInterval}
                      onChange={(e, val) => setRefreshInterval(val)}
                      min={10}
                      max={300}
                      step={10}
                      valueLabelDisplay="auto"
                      size="small"
                    />
                  </Box>
                )}
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Account Settings Section */}
        <Accordion 
          className="settings-accordion settings-panel-section" 
          expanded={activeSection === 'account'} 
          onChange={() => setActiveSection(activeSection === 'account' ? '' : 'account')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box className="settings-section-header">
              <AccountBoxIcon />
              <Typography variant="subtitle1">Account Settings</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              {/* Organization Name */}
              <Box className="settings-form-section">
                <Typography className="settings-form-label" gutterBottom>
                  Organization Name
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={settings.organizationName || ''}
                  onChange={(e) => handleSettingChange('organizationName', e.target.value)}
                  placeholder="Enter organization name"
                  helperText="This name will appear in the dashboard header and public pages"
                />
              </Box>

              {/* Organization Description */}
              <Box className="settings-form-section">
                <Typography className="settings-form-label" gutterBottom>
                  Description
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  value={settings.organizationDescription || ''}
                  onChange={(e) => handleSettingChange('organizationDescription', e.target.value)}
                  placeholder="Brief description of your organization"
                  helperText="Optional description for internal reference"
                />
              </Box>


            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Branding & Theme Section */}
        <Accordion 
          className="settings-accordion settings-panel-section" 
          expanded={activeSection === 'branding'} 
          onChange={() => setActiveSection(activeSection === 'branding' ? '' : 'branding')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box className="settings-section-header">
              <ColorLensIcon />
              <Typography variant="subtitle1">Branding & Theme</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              {/* Theme Presets */}
              <Box className="settings-form-section">
                <Typography className="settings-form-label" gutterBottom>Theme Presets</Typography>
                <Typography className="settings-form-caption" gutterBottom>
                  Choose from pre-designed color schemes that match your organization type.
                </Typography>
                
                {/* Font Preservation Option */}
                <FormControlLabel
                  sx={{ mb: 2, alignSelf: 'flex-start' }}
                  control={
                    <Switch
                      checked={preserveFontsOnPreset}
                      onChange={(e) => setPreserveFontsOnPreset(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Box>
                      <Typography className="settings-form-caption" component="span">
                        Preserve current fonts when applying presets
                      </Typography>
                      <Typography className="settings-form-caption" variant="caption" display="block" sx={{ opacity: 0.7, fontSize: '0.75rem' }}>
                        When enabled, only colors change. When disabled, fonts also change to match the preset.
                      </Typography>
                    </Box>
                  }
                />
                <Grid container spacing={1.5}>
                  {Object.entries(organizationThemePresets).map(([key, preset]) => {
                    const currentPreset = getCurrentPreset();
                    const isSelected = currentPreset && currentPreset[0] === key;
                    
                    return (
                      <Grid item xs={6} key={key}>
                        <Card 
                          className={`theme-preview-card ${isSelected ? 'selected' : ''}`}
                          sx={{ 
                            cursor: 'pointer',
                            border: '2px solid',
                            borderColor: isSelected ? preset.primaryColor : 'transparent',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              borderColor: preset.primaryColor,
                              transform: 'scale(1.02)',
                              boxShadow: `0 2px 8px ${preset.primaryColor}30`
                            }
                          }}
                          onClick={() => handleThemePresetSelect(key)}
                        >
                        <CardContent>
                          <Box className="color-dots">
                            <Box 
                              sx={{ 
                                width: 16, 
                                height: 16, 
                                borderRadius: '50%', 
                                bgcolor: preset.primaryColor,
                                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
                              }} 
                            />
                            <Box 
                              sx={{ 
                                width: 12, 
                                height: 12, 
                                borderRadius: '50%', 
                                bgcolor: preset.secondaryColor,
                                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
                              }} 
                            />
                          </Box>
                          <Box>
                            <Typography className="theme-name">
                              {preset.name}
                            </Typography>
                            <Typography className="theme-font">
                              {preset.fontFamily}
                            </Typography>
                          </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>

              {/* Colors */}
              <Box className="settings-form-section">
                <Typography className="settings-form-label" gutterBottom>Brand Colors</Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography className="settings-form-caption" gutterBottom display="block">Primary Color</Typography>
                    <Box className="color-picker-container">
                      <input
                        type="color"
                        value={settings.primaryColor}
                        onChange={(e) => handleSettingChange('primaryColor', e.target.value)}
                        className="color-picker-input"
                      />
                      <TextField
                        className="settings-text-field"
                        size="small"
                        value={settings.primaryColor}
                        onChange={(e) => handleSettingChange('primaryColor', e.target.value)}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Typography className="settings-form-caption" gutterBottom display="block">Secondary Color</Typography>
                    <Box className="color-picker-container">
                      <input
                        type="color"
                        value={settings.secondaryColor}
                        onChange={(e) => handleSettingChange('secondaryColor', e.target.value)}
                        className="color-picker-input"
                      />
                      <TextField
                        className="settings-text-field"
                        size="small"
                        value={settings.secondaryColor}
                        onChange={(e) => handleSettingChange('secondaryColor', e.target.value)}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>
                </Stack>
              </Box>

              {/* Typography */}
              <Box className="settings-form-section">
                <Typography className="settings-form-label" gutterBottom sx={{ mb: 3 }}>Typography</Typography>
                <Stack spacing={2}>
                  <FormControl className="settings-select" fullWidth size="small">
                    <InputLabel>Primary Font (Headings)</InputLabel>
                    <Select
                      value={settings.primaryFontFamily}
                      label="Primary Font (Headings)"
                      onChange={(e) => handleSettingChange('primaryFontFamily', e.target.value)}
                      onOpen={() => {
                        // Ensure all fonts are loaded when dropdown opens
                        const dropdownFonts = [
                          'Open Sans', 'Roboto', 'Lato', 'Montserrat', 'Source Sans Pro',
                          'Inter', 'Poppins', 'Nunito', 'Barlow', 'Work Sans', 
                          'Fira Sans', 'Libre Baskerville', 'Futura'
                        ];
                        dropdownFonts.forEach(font => loadOrganizationFont(font));
                      }}
                      renderValue={(value) => (
                        <Typography
                          sx={{
                            fontFamily: `"${value}", -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
                            fontWeight: 600,
                            // Force re-render when fonts are loaded
                            opacity: fontsLoaded ? 1 : 0.9
                          }}
                        >
                          {value}
                        </Typography>
                      )}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 400,
                            '& .MuiMenuItem-root': {
                              padding: '12px 16px'
                            }
                          }
                        }
                      }}
                    >
                      {[
                        'Open Sans',
                        'Roboto', 
                        'Lato',
                        'Montserrat',
                        'Source Sans Pro',
                        'Inter',
                        'Poppins',
                        'Nunito',
                        'Barlow',
                        'Work Sans',
                        'Fira Sans',
                        'Libre Baskerville',
                        'Futura'
                      ].map((fontName) => (
                        <MenuItem 
                          key={fontName} 
                          value={fontName}
                          onMouseEnter={() => loadOrganizationFont(fontName)}
                        >
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontFamily: `"${fontName}", -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
                              fontSize: '16px',
                              fontWeight: 600,
                              fontDisplay: 'swap',
                              // Force re-render when fonts are loaded
                              opacity: fontsLoaded ? 1 : 0.9
                            }}
                          >
                            {fontName}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl className="settings-select" fullWidth size="small">
                    <InputLabel>Secondary Font (Body Text)</InputLabel>
                    <Select
                      value={settings.secondaryFontFamily}
                      label="Secondary Font (Body Text)"
                      onChange={(e) => handleSettingChange('secondaryFontFamily', e.target.value)}
                      onOpen={() => {
                        // Ensure all fonts are loaded when dropdown opens
                        const dropdownFonts = [
                          'Open Sans', 'Roboto', 'Lato', 'Montserrat', 'Source Sans Pro',
                          'Inter', 'Poppins', 'Nunito', 'Barlow', 'Work Sans', 
                          'Fira Sans', 'Libre Baskerville', 'Futura', 'Futura LT Pro'
                        ];
                        dropdownFonts.forEach(font => loadOrganizationFont(font));
                      }}
                      renderValue={(value) => (
                        <Typography
                          sx={{
                            fontFamily: `"${value}", -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
                            fontWeight: 400,
                            // Force re-render when fonts are loaded
                            opacity: fontsLoaded ? 1 : 0.9
                          }}
                        >
                          {value}
                        </Typography>
                      )}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 400,
                            '& .MuiMenuItem-root': {
                              padding: '12px 16px'
                            }
                          }
                        }
                      }}
                    >
                      {[
                        'Open Sans',
                        'Roboto', 
                        'Lato',
                        'Montserrat',
                        'Source Sans Pro',
                        'Inter',
                        'Poppins',
                        'Nunito',
                        'Barlow',
                        'Work Sans',
                        'Fira Sans',
                        'Libre Baskerville',
                        'Futura'
                      ].map((fontName) => (
                        <MenuItem 
                          key={fontName} 
                          value={fontName}
                          onMouseEnter={() => loadOrganizationFont(fontName)}
                        >
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontFamily: `"${fontName}", -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
                              fontSize: '16px',
                              fontWeight: 400,
                              fontDisplay: 'swap',
                              // Force re-render when fonts are loaded
                              opacity: fontsLoaded ? 1 : 0.9
                            }}
                          >
                            {fontName}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Box>




            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Header Configuration Section */}
        <Accordion 
          className="settings-accordion settings-panel-section" 
          expanded={activeSection === 'header'} 
          onChange={() => setActiveSection(activeSection === 'header' ? '' : 'header')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box className="settings-section-header">
              <SettingsIcon />
              <Typography variant="subtitle1">Header Configuration</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              {/* Header Style Selection */}
              <Box className="settings-form-section">
                <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
                  Header Style
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[
                    { value: 'full', name: 'Full Header', description: 'Colored background' },
                    { value: 'minimal', name: 'Minimal Header', description: 'White background' },
                    { value: 'none', name: 'No Header', description: 'Hidden' }
                  ].map((option) => (
                    <Box
                      key={option.value}
                      onClick={() => handleSettingChange('headerStyle', option.value)}
                      sx={{
                        flex: '1',
                        minWidth: '100px',
                        p: 1.5,
                        border: '1px solid',
                        borderRadius: 1,
                        borderColor: settings.headerStyle === option.value ? 'var(--color-primary)' : 'divider',
                        bgcolor: settings.headerStyle === option.value ? 'var(--color-primary-08)' : 'background.paper',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: 'var(--color-primary)',
                          bgcolor: settings.headerStyle === option.value ? 'var(--color-primary-12)' : 'action.hover'
                        }
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: settings.headerStyle === option.value ? 600 : 500,
                          color: settings.headerStyle === option.value ? 'var(--color-primary)' : 'text.primary',
                          mb: 0.5
                        }}
                      >
                        {option.name}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: settings.headerStyle === option.value ? 'var(--color-primary)' : 'text.secondary',
                          opacity: 0.8
                        }}
                      >
                        {option.description}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Header Color Toggle - Only show for Full Header */}
                {settings.headerStyle === 'full' && (
                  <Box sx={{ mt: 2 }}>
                    <Typography 
                      className="settings-form-label" 
                      gutterBottom 
                      sx={{ mb: 1, fontSize: '0.9rem', fontWeight: 500 }}
                    >
                      Header Color
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {[
                        { value: 'primary', name: 'Primary Color', color: settings.primaryColor },
                        { value: 'secondary', name: 'Secondary Color', color: settings.secondaryColor }
                      ].map((option) => (
                        <Box
                          key={option.value}
                          onClick={() => handleSettingChange('headerColorMode', option.value)}
                          sx={{
                            flex: '1',
                            p: 1.5,
                            border: '1px solid',
                            borderRadius: 1,
                            borderColor: settings.headerColorMode === option.value ? option.color : 'divider',
                            bgcolor: settings.headerColorMode === option.value ? `${option.color}08` : 'background.paper',
                            cursor: 'pointer',
                            textAlign: 'center',
                            transition: 'all 0.2s ease',
                            position: 'relative',
                            '&:hover': {
                              borderColor: option.color,
                              bgcolor: settings.headerColorMode === option.value ? `${option.color}12` : 'action.hover'
                            }
                          }}
                        >
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              bgcolor: option.color,
                              mx: 'auto',
                              mb: 0.5,
                              border: '2px solid white',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                            }}
                          />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: settings.headerColorMode === option.value ? 600 : 500,
                              color: settings.headerColorMode === option.value ? option.color : 'text.primary',
                              fontSize: '0.8rem'
                            }}
                          >
                            {option.name}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            
              {settings.headerStyle !== 'none' && (
                <Box className="settings-form-section">
                  <Stack spacing={2}>
                    <TextField
                      className="settings-text-field header-title-field"
                      fullWidth
                      size="small"
                      label="Header Title"
                      data-testid="header-title-input"
                      value={settings.headerTitle}
                      onChange={(e) => handleSettingChange('headerTitle', e.target.value)}
                      placeholder="Enter dashboard title"
                    />
                    <TextField
                      className="settings-text-field header-subtitle-field"
                      fullWidth
                      size="small"
                      label="Header Subtitle"
                      data-testid="header-subtitle-input"
                      value={settings.headerSubtitle}
                      onChange={(e) => handleSettingChange('headerSubtitle', e.target.value)}
                      placeholder="Enter dashboard subtitle"
                    />
                    {/* Logo URL and Upload Section */}
                    <Box>
                      <Typography className="settings-form-label" gutterBottom>
                        Logo
                      </Typography>
                      
                      {/* Current Logo Preview */}
                      {settings.headerLogoUrl && (
                        <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
                          <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                            Current Logo:
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <img 
                              src={settings.headerLogoUrl} 
                              alt="Current logo" 
                              style={{ 
                                maxHeight: '40px', 
                                maxWidth: '120px', 
                                objectFit: 'contain',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                padding: '4px',
                                backgroundColor: 'white'
                              }} 
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <Typography variant="caption" color="textSecondary">
                              Logo uploaded successfully
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      
                      <Stack spacing={2}>
                        {/* Upload Button */}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={logoUploading ? <CircularProgress size={16} sx={{ color: '#005F94' }} /> : <CloudUploadIcon />}
                            disabled={logoUploading}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {logoUploading ? 'Uploading...' : 'Upload Logo'}
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={handleLogoUpload}
                            />
                          </Button>
                          <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                            Max 5MB • PNG, JPG, SVG
                          </Typography>
                        </Box>
                        
                        {/* URL Field */}
                    <TextField
                      className="settings-text-field"
                      fullWidth
                      size="small"
                          label="Or enter Logo URL"
                      value={settings.headerLogoUrl}
                      onChange={(e) => handleSettingChange('headerLogoUrl', e.target.value)}
                      placeholder="https://example.com/logo.png"
                    />
                      </Stack>
                    </Box>
                    <FormControl className="settings-select" fullWidth size="small">
                      <InputLabel>Logo Style</InputLabel>
                      <Select
                        value={settings.headerLogoStyle}
                        label="Logo Style"
                        onChange={(e) => handleSettingChange('headerLogoStyle', e.target.value)}
                      >
                        <MenuItem value="normal">Normal</MenuItem>
                        <MenuItem value="invert">Invert Colors</MenuItem>
                        <MenuItem value="white">Make White</MenuItem>
                        <MenuItem value="greyscale">Greyscale</MenuItem>
                        <MenuItem value="flexible-width">Flexible Width (Landscape)</MenuItem>
                      </Select>
                    </FormControl>
                    
                    {/* Show Disclaimer Toggle */}
                    <Box sx={{ mt: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.showDisclaimer}
                            onChange={(e) => handleSettingChange('showDisclaimer', e.target.checked)}
                            size="small"
                          />
                        }
                        label="Show Disclaimer"
                        sx={{ 
                          '& .MuiFormControlLabel-label': { 
                            fontSize: '0.9rem', 
                            fontWeight: 500 
                          } 
                        }}
                      />
                    </Box>

                    {/* Disclaimer Fields - Only show if disclaimer is enabled */}
                    {settings.showDisclaimer && (
                      <>
                    <TextField
                      className="settings-text-field"
                      fullWidth
                      size="small"
                      label="Disclaimer Text"
                      multiline
                      rows={2}
                      value={settings.headerDisclaimerText}
                      onChange={(e) => handleSettingChange('headerDisclaimerText', e.target.value)}
                      placeholder="Enter disclaimer text"
                    />
                    <TextField
                      className="settings-text-field"
                      fullWidth
                      size="small"
                      label="Disclaimer Hover Text"
                      multiline
                      rows={3}
                      value={settings.headerDisclaimerBody}
                      onChange={(e) => handleSettingChange('headerDisclaimerBody', e.target.value)}
                      placeholder="Enter detailed disclaimer text shown on hover"
                    />
                    <FormControl className="settings-select" fullWidth size="small">
                      <InputLabel>Disclaimer Icon</InputLabel>
                      <Select
                        value={settings.headerDisclaimerIcon || 'WarningAmberIcon'}
                        label="Disclaimer Icon"
                        onChange={(e) => handleSettingChange('headerDisclaimerIcon', e.target.value)}
                        renderValue={(value) => (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {(() => {
                              switch (value) {
                                case 'InfoIcon': return <InfoIcon sx={{ fontSize: '1rem' }} />;
                                case 'HelpIcon': return <HelpIcon sx={{ fontSize: '1rem' }} />;
                                case 'ErrorIcon': return <ErrorOutlineIcon sx={{ fontSize: '1rem' }} />;
                                case 'CheckCircleIcon': return <CheckCircleOutlineIcon sx={{ fontSize: '1rem' }} />;
                                case 'CampaignIcon': return <CampaignIcon sx={{ fontSize: '1rem' }} />;
                                case 'WarningAmberIcon':
                                case 'WarningIcon':
                                default: return <WarningAmberIcon sx={{ fontSize: '1rem' }} />;
                              }
                            })()}
                            <Typography variant="body2">
                              {(() => {
                                switch (value) {
                                  case 'InfoIcon': return 'Information';
                                  case 'HelpIcon': return 'Help';
                                  case 'ErrorIcon': return 'Error';
                                  case 'CheckCircleIcon': return 'Success';
                                  case 'CampaignIcon': return 'Announcement';
                                  case 'WarningAmberIcon':
                                  case 'WarningIcon':
                                  default: return 'Warning';
                                }
                              })()}
                            </Typography>
                          </Box>
                        )}
                      >
                        <MenuItem value="WarningAmberIcon">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WarningAmberIcon sx={{ fontSize: '1rem' }} />
                            <Typography>Warning</Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="InfoIcon">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InfoIcon sx={{ fontSize: '1rem' }} />
                            <Typography>Information</Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="HelpIcon">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <HelpIcon sx={{ fontSize: '1rem' }} />
                            <Typography>Help</Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="ErrorIcon">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ErrorOutlineIcon sx={{ fontSize: '1rem' }} />
                            <Typography>Error</Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="CheckCircleIcon">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: '1rem' }} />
                            <Typography>Success</Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="CampaignIcon">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CampaignIcon sx={{ fontSize: '1rem' }} />
                            <Typography>Announcement</Typography>
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                      </>
                    )}
                    
                    <FormControlLabel
                      className="settings-switch"
                      control={
                        <Switch
                          checked={settings.showStatusSchedule}
                          onChange={(e) => handleSettingChange('showStatusSchedule', e.target.checked)}
                        />
                      }
                      label="Show Status Schedule Indicator"
                    />
                    {settings.showStatusSchedule && (
                      <FormControl className="settings-select" fullWidth size="small">
                        <InputLabel>Status Indicator Style</InputLabel>
                        <Select
                          value={settings.statusIndicatorStyle || 'rounded'}
                          label="Status Indicator Style"
                          onChange={(e) => handleSettingChange('statusIndicatorStyle', e.target.value)}
                        >
                          <MenuItem value="rounded">Rounded (LED Style)</MenuItem>
                          <MenuItem value="flat">Flat (Modern)</MenuItem>
                          <MenuItem value="hidden">Hidden</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </Stack>
                </Box>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Content Visibility Section - Redesigned */}
        <Accordion 
          className="settings-accordion settings-panel-section" 
          expanded={activeSection === 'pillars'} 
          onChange={() => setActiveSection(activeSection === 'pillars' ? '' : 'pillars')}
          data-testid="content-visibility-panel"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box className="settings-section-header">
              <ViewColumnIcon />
              <Typography variant="subtitle1">Content Visibility</Typography>
              <Chip 
                size="small" 
                label={`${visiblePillars.length}/${availablePillars.length}`}
                sx={{ ml: 'auto', mr: 1, bgcolor: 'action.selected' }}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <ContentVisibilityManager
              availablePillars={availablePillars}
              visiblePillars={visiblePillars}
              onPillarToggle={onPillarToggle}
              selectedView={selectedView}
              organizationData={organizationData}
              onPillarOrderChange={onPillarOrderChange}
              onExpandedPillarsChange={onExpandedPillarsChange}
              setOrganizationData={setOrganizationData}
              sendSettingsUpdateToIframe={sendSettingsUpdateToIframe}
              setVisiblePillars={setVisiblePillars}
              iframeRef={iframeRef}
              handleResetToAdminDefault={handleResetToAdminDefault}
              orgSlug={orgSlug}
            />
          </AccordionDetails>
        </Accordion>

        {/* Sharing Section */}
        <Accordion 
          className="settings-accordion settings-panel-section" 
          expanded={activeSection === 'sharing'} 
          onChange={() => setActiveSection(activeSection === 'sharing' ? '' : 'sharing')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box className="settings-section-header">
              <ShareIcon />
              <Typography variant="subtitle1">Sharing & Embedding</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Typography className="settings-form-caption">
                Generate public links and embed codes for sharing this dashboard.
              </Typography>
              <Button className="settings-button settings-button-secondary" variant="outlined" startIcon={<LinkIcon />}>
                Generate Public Link
              </Button>
              <Button className="settings-button settings-button-secondary" variant="outlined" startIcon={<CodeIcon />}>
                Get Embed Code
              </Button>
              <Button className="settings-button settings-button-secondary" variant="outlined" startIcon={<TokenIcon />}>
                Manage API Tokens
              </Button>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* My Profile Section */}
        <Accordion 
          className="settings-accordion settings-panel-section" 
          expanded={activeSection === 'profile'} 
          onChange={() => setActiveSection(activeSection === 'profile' ? '' : 'profile')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box className="settings-section-header">
              <PersonIcon />
              <Typography variant="subtitle1">My Profile</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              <Typography className="settings-form-caption" sx={{ mb: 0.5 }}>
                Update your personal information and account settings.
              </Typography>
              
              <Stack spacing={1.5}>
                {/* Avatar Upload Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                  <Avatar 
                    src={
                      // Smart priority: respect local settings when avatar is marked for removal
                      (() => {
                        const backendAvatar = users.length > 0 && users.find(u => u.email === user?.email || u.id === 'test-admin')?.avatar;
                        const localAvatar = settings.myProfile?.avatarUrl;
                        const originalAvatar = originalSettings.myProfile?.avatarUrl;
                        
                        // If local settings explicitly removed a saved avatar, show nothing (marked for deletion)
                        if (originalAvatar && localAvatar === null && !unsavedAvatarUrl) {
                          return null;
                        }
                        
                        // Otherwise use normal priority: local > backend > fallback
                        return localAvatar || backendAvatar || user?.avatar || (users.length > 0 ? users[0]?.avatar : null);
                      })()
                    } 
                    sx={{ width: 56, height: 56 }}
                  >
                    {/* Show initials only if no avatar is being displayed (using same logic as src) */}
                    {(() => {
                      const backendAvatar = users.length > 0 && users.find(u => u.email === user?.email || u.id === 'test-admin')?.avatar;
                      const localAvatar = settings.myProfile?.avatarUrl;
                      const originalAvatar = originalSettings.myProfile?.avatarUrl;
                      
                      // If local settings explicitly removed a saved avatar, show initials
                      if (originalAvatar && localAvatar === null && !unsavedAvatarUrl) {
                        return (settings.myProfile?.name || user?.name || user?.email || (users.length > 0 ? users[0]?.name : null) || 'U').charAt(0).toUpperCase();
                      }
                      
                      // Show initials only if no avatar available anywhere
                      const hasAnyAvatar = localAvatar || backendAvatar || user?.avatar || (users.length > 0 && users[0]?.avatar);
                      return !hasAnyAvatar ? (settings.myProfile?.name || user?.name || user?.email || (users.length > 0 ? users[0]?.name : null) || 'U').charAt(0).toUpperCase() : null;
                    })()}
                  </Avatar>
                  
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={avatarUploading ? <CircularProgress size={16} sx={{ color: '#005F94' }} /> : <CloudUploadIcon />}
                        disabled={avatarUploading || (!user?.id && users.length === 0)}
                        size="small"
                      >
                        {avatarUploading ? 'Uploading...' : 'Upload Photo'}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleAvatarUpload}
                        />
                      </Button>
                      
                      {/* Show remove/undo button based on avatar state */}
                      {(() => {
                        const backendAvatar = users.length > 0 && users.find(u => u.email === user?.email || u.id === 'test-admin')?.avatar;
                        const localAvatar = settings.myProfile?.avatarUrl;
                        const originalAvatar = originalSettings.myProfile?.avatarUrl;
                        
                        // If saved avatar is marked for deletion, show undo button
                        if (originalAvatar && localAvatar === null && !unsavedAvatarUrl) {
                          return (
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              disabled={avatarUploading}
                              onClick={() => {
                                // Restore the saved avatar
                                handleSettingChange('myProfile', { 
                                  ...(settings.myProfile || {}), 
                                  avatarUrl: originalAvatar 
                                });
                              }}
                              startIcon={<UndoIcon />}
                            >
                              Undo Removal
                            </Button>
                          );
                        }
                        
                        // Show remove button if any avatar is available
                        const hasAnyAvatar = localAvatar || backendAvatar || user?.avatar || (users.length > 0 && users[0]?.avatar);
                        return hasAnyAvatar && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            disabled={avatarUploading}
                            onClick={handleAvatarRemove}
                            startIcon={<DeleteIcon />}
                          >
                            Remove
                          </Button>
                        );
                      })()}
                    </Stack>
                    <Typography variant="caption" color="textSecondary" display="block">
                      {(!user?.id && users.length === 0) ? 'Loading user info...' : 'Max 2MB • PNG, JPG, GIF'}
                    </Typography>
                  </Box>
                </Box>
                
                <TextField
                  label="Full Name"
                  value={settings.myProfile?.name || user?.name || (users.length > 0 ? users[0]?.name : '') || ''}
                  onChange={(e) => handleSettingChange('myProfile', { 
                    ...(settings.myProfile || {}), 
                    name: e.target.value 
                  })}
                  fullWidth
                  size="small"
                />
                
                <TextField
                  label="Email Address"
                  type="email"
                  value={settings.myProfile?.email || user?.email || (users.length > 0 ? users[0]?.email : '') || ''}
                  onChange={(e) => handleSettingChange('myProfile', { 
                    ...(settings.myProfile || {}), 
                    email: e.target.value 
                  })}
                  fullWidth
                  size="small"
                />
                
                <TextField
                  label="Notes / Description"
                  value={settings.myProfile?.description || ''}
                  onChange={(e) => handleSettingChange('myProfile', { 
                    ...(settings.myProfile || {}), 
                    description: e.target.value 
                  })}
                  placeholder="Job title, department, etc. (optional)"
                  multiline
                  rows={1}
                  fullWidth
                  size="small"
                />
                
                <Box sx={{ pt: 0.5 }}>
                  <Button
                    className="settings-button settings-button-secondary"
                    variant="outlined"
                    onClick={() => handleResetPassword(user || (users.length > 0 ? users[0] : null))}
                    startIcon={<VpnKeyIcon />}
                    size="small"
                    disabled={!user && users.length === 0}
                  >
                    Change My Password
                  </Button>
                </Box>
              </Stack>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Users Section */}
        <Accordion 
          className="settings-accordion settings-panel-section" 
          expanded={activeSection === 'users'} 
          onChange={() => setActiveSection(activeSection === 'users' ? '' : 'users')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box className="settings-section-header" sx={{ flex: 1, mr: 2 }}>
              <PeopleIcon />
              <Typography variant="subtitle1">User Management</Typography>
              <Badge badgeContent={users.length} color="primary" sx={{ ml: 1 }} />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography className="settings-form-caption">
                  Manage organization users and access permissions.
                </Typography>
                <Button
                  className="settings-button"
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={handleAddUser}
                  size="small"
                  sx={{ 
                    whiteSpace: 'nowrap',
                    minWidth: 'auto',
                    px: 2
                  }}
                >
                  Add
                </Button>
              </Box>
              
              {/* User List */}
              {loadingUsers ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={20} sx={{ color: '#005F94' }} />
                </Box>
              ) : users.length > 0 ? (
                <Stack spacing={0.5}>
                  {users.map((userItem) => (
                    <Paper 
                      key={userItem.id} 
                      variant="outlined" 
                      sx={{ 
                        p: 1.5,
                        '&:hover': { 
                          bgcolor: 'action.hover'
                        },
                        transition: 'background-color 0.2s ease'
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1.5} flex={1}>
                          <Avatar 
                            src={
                              (() => {
                                const isCurrentUser = userItem.email === user?.email || userItem.id === 'test-admin';
                                
                                if (isCurrentUser) {
                                  // For current user, apply smart logic like in profile section
                                  const localAvatar = settings.myProfile?.avatarUrl;
                                  const originalAvatar = originalSettings.myProfile?.avatarUrl;
                                  
                                  // If local settings explicitly removed a saved avatar, show nothing
                                  if (originalAvatar && localAvatar === null && !unsavedAvatarUrl) {
                                    return null;
                                  }
                                  
                                  // Otherwise use local settings or fallback to backend
                                  return localAvatar || userItem.avatar;
                                } else {
                                  // For other users, just use backend avatar
                                  return userItem.avatar;
                                }
                              })()
                            } 
                            sx={{ width: 28, height: 28 }}
                          >
                            {(() => {
                              const isCurrentUser = userItem.email === user?.email || userItem.id === 'test-admin';
                              
                              if (isCurrentUser) {
                                // For current user, apply smart logic
                                const localAvatar = settings.myProfile?.avatarUrl;
                                const originalAvatar = originalSettings.myProfile?.avatarUrl;
                                
                                // If saved avatar is marked for deletion, show initials
                                if (originalAvatar && localAvatar === null && !unsavedAvatarUrl) {
                                  return userItem.name ? userItem.name.charAt(0).toUpperCase() : <PersonIcon fontSize="small" />;
                                }
                                
                                // Show initials only if no avatar available
                                const hasAvatar = localAvatar || userItem.avatar;
                                return !hasAvatar ? (userItem.name ? userItem.name.charAt(0).toUpperCase() : <PersonIcon fontSize="small" />) : null;
                              } else {
                                // For other users, show initials if no backend avatar
                                return !userItem.avatar ? (userItem.name ? userItem.name.charAt(0).toUpperCase() : <PersonIcon fontSize="small" />) : null;
                              }
                            })()}
                          </Avatar>
                          <Box flex={1}>
                            <Box display="flex" alignItems="center" gap={1} mb={0.25}>
                              <Typography variant="body2" fontWeight="medium">
                                {userItem.name}
                              </Typography>
                              {userItem.email === user?.email && (
                                <Chip 
                                  label="You" 
                                  size="small" 
                                  color="primary" 
                                  sx={{ fontSize: '0.7rem', height: 20 }} 
                                />
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {userItem.email}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1.5} mt={0.25}>
                              <Chip 
                                label={
                                  userItem.role === 'super_admin' ? 'Super Admin' : 
                                  userItem.role === 'org_admin' || userItem.role === 'admin' ? 'Admin' :
                                  'User'
                                } 
                                size="small" 
                                color={
                                  userItem.role === 'super_admin' ? 'warning' :
                                  userItem.role === 'org_admin' || userItem.role === 'admin' ? 'error' : 
                                  'primary'
                                } 
                                variant={userItem.role === 'super_admin' ? 'filled' : 'outlined'}
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                              {userItem.status !== 'active' && (
                                <Box 
                                  sx={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                  }}
                                >
                                  <Box 
                                    sx={{ 
                                      width: 6, 
                                      height: 6, 
                                      borderRadius: '50%', 
                                      bgcolor: userItem.status === 'invited' ? 'warning.main' : 'default'
                                    }} 
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {userItem.status === 'invited' ? 'Pending' : userItem.status}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Box>
                        
                        {/* Actions */}
                        {userItem.role !== 'super_admin' && (
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="Edit User">
                              <IconButton
                                size="small"
                                onClick={() => handleEditUser(userItem)}
                                sx={{ color: 'primary.main' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reset Password">
                              <IconButton
                                size="small"
                                onClick={() => handleResetPassword(userItem)}
                                sx={{ color: 'warning.main' }}
                              >
                                <VpnKeyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove User">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteUser(userItem.id)}
                                sx={{ color: 'error.main' }}
                                disabled={userItem.email === user?.email}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Box textAlign="center" py={3}>
                  <PersonIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1.5 }} />
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    No users found
                  </Typography>
                  <Typography color="text.disabled" variant="caption">
                    Add users to get started with team collaboration
                  </Typography>
                </Box>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

            {/* User management section completed above */}


        {/* Save/Reset Controls - Only show when there are changes */}
        {hasChanges && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              p: 2,
              mt: 2,
              zIndex: 1,
              boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)'
            }}
          >
            {/* Warning indicator */}
            <Box 
              display="flex" 
              alignItems="center" 
              gap={1} 
              mb={2}
              sx={{ 
                color: 'warning.dark',
                backgroundColor: 'warning.light',
                borderRadius: 1,
                p: 1,
                border: '1px solid',
                borderColor: 'warning.main'
              }}
            >
              <WarningIcon fontSize="small" />
              <Typography variant="body2" fontWeight="medium">
                You have unsaved changes
              </Typography>
            </Box>
            
            {/* Action buttons - Professional equal width design */}
            <Box display="flex" gap={1.5} width="100%">
              <Button
                variant="outlined"
                onClick={resetAllChanges}
                startIcon={<UndoIcon />}
                fullWidth
                size="large"
                className="settings-reset-button"
                data-testid="reset-changes-button"
                sx={{
                  fontWeight: 500,
                  py: 1.5,
                  px: 2,
                  fontSize: '0.9375rem',
                  lineHeight: 1.5,
                  borderColor: 'grey.400',
                  color: 'text.primary',
                  backgroundColor: 'background.paper',
                  minHeight: '48px',
                  '&:hover': {
                    borderColor: 'grey.600',
                    backgroundColor: 'grey.100'
                  },
                  // Ensure consistent icon spacing
                  '& .MuiButton-startIcon': {
                    marginRight: '8px',
                    marginLeft: '0px'
                  }
                }}
              >
                Reset Changes
              </Button>
              
              <Button
                variant="contained"
                onClick={applyAllSettings}
                startIcon={<CheckIcon />}
                fullWidth
                size="large"
                color="primary"
                className="settings-save-button"
                data-testid="save-changes-button"
                sx={{
                  fontWeight: 500, // Matched to reset button
                  py: 1.5,
                  px: 2,
                  fontSize: '0.9375rem', // Explicitly set same font size
                  lineHeight: 1.5,
                  minHeight: '48px', // Explicit height matching
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)'
                  },
                  // Ensure consistent icon spacing
                  '& .MuiButton-startIcon': {
                    marginRight: '8px',
                    marginLeft: '0px'
                  }
                }}
              >
                Save All Changes
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* User Management Dialog */}
      <Dialog
        open={userDialogOpen}
        onClose={() => setUserDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PersonAddIcon color="primary" />
            <Typography variant="h6">
              {editingUser ? 'Edit User' : 'Add New User'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {userFormErrors.general && (
              <Alert severity="error">{userFormErrors.general}</Alert>
            )}
            
            <TextField
              label="Full Name"
              value={userForm.name}
              onChange={(e) => handleUserFormChange('name', e.target.value)}
              error={!!userFormErrors.name}
              helperText={userFormErrors.name}
              required
              fullWidth
              disabled={savingUser}
            />
            
            <TextField
              label="Email Address"
              type="email"
              value={userForm.email}
              onChange={(e) => handleUserFormChange('email', e.target.value)}
              error={!!userFormErrors.email}
              helperText={userFormErrors.email}
              required
              fullWidth
              disabled={savingUser}
            />
            
            <TextField
              label="Notes / Description"
              value={userForm.description}
              onChange={(e) => handleUserFormChange('description', e.target.value)}
              placeholder="Job title, department, contract details, or any other relevant information..."
              multiline
              rows={3}
              fullWidth
              disabled={savingUser}
              helperText="Optional field for storing additional information about this user"
            />
            
            <FormControl fullWidth error={!!userFormErrors.role} disabled={savingUser}>
              <InputLabel>Role</InputLabel>
              <Select
                value={userForm.role}
                label="Role"
                onChange={(e) => handleUserFormChange('role', e.target.value)}
              >
                <MenuItem value="member">
                  <Box>
                    <Typography variant="body2" fontWeight="medium">User</Typography>
                    <Typography variant="caption" color="textSecondary">
                      View-only access to dashboards and reports
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="admin">
                  <Box>
                    <Typography variant="body2" fontWeight="medium">Admin</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Full access: edit dashboards, manage users, and configure organization settings
                    </Typography>
                  </Box>
                </MenuItem>
              </Select>
              {userFormErrors.role && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {userFormErrors.role}
                </Typography>
              )}
            </FormControl>

            {!editingUser ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  An invitation email will be sent to the user with instructions to set up their account.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Update user information including name, email address, role, and description. Changes will be applied immediately.
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setUserDialogOpen(false)} 
            disabled={savingUser}
          >
            Cancel
          </Button>
          {editingUser && (
            <Button
              variant="outlined"
              color="warning"
              onClick={() => {
                setUserDialogOpen(false);
                handleResetPassword(editingUser);
              }}
              disabled={savingUser}
              startIcon={<VpnKeyIcon />}
              sx={{ mr: 1 }}
            >
              Reset Password
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleSaveUser}
            disabled={savingUser}
                                        startIcon={savingUser ? <CircularProgress size={16} sx={{ color: '#005F94' }} /> : <SaveIcon />}
          >
            {savingUser ? 'Saving...' : editingUser ? 'Update User' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog
        open={passwordResetDialog.open}
        onClose={closePasswordResetDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <VpnKeyIcon color="warning" />
            <Typography variant="h6">
              Reset User Password
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {passwordResetDialog.result ? (
              // Show result after password reset attempt
              <>
                <Alert severity={passwordResetDialog.result.success ? 'success' : 'error'}>
                  {passwordResetDialog.result.message}
                </Alert>
                
                {passwordResetDialog.result.success && passwordResetDialog.result.tempPassword && (
                  <Alert severity="info">
                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      {passwordResetDialog.result.isSelfReset ? 'Your New Temporary Password:' : 'Temporary Password (Development Mode):'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'background.paper', p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      {passwordResetDialog.result.tempPassword}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      {passwordResetDialog.result.isSelfReset 
                        ? 'You can continue using the dashboard with this password. Consider changing it to something more memorable in your profile settings.'
                        : 'In production, this would be sent via email to the user.'
                      }
                    </Typography>
                  </Alert>
                )}
              </>
            ) : (
              // Show confirmation before password reset
              <>
                <Alert severity="warning">
                  <Typography variant="body2">
                    {isCurrentUser(passwordResetDialog.user) ? (
                      <>This will reset <strong>your password</strong> and require you to log in with a new temporary password.</>
                    ) : (
                      <>This will reset the password for <strong>{passwordResetDialog.user?.name}</strong> ({passwordResetDialog.user?.email}) and require them to log in with a new temporary password.</>
                    )}
                  </Typography>
                </Alert>
                
                <Typography variant="body2" color="textSecondary">
                  {isCurrentUser(passwordResetDialog.user) ? (
                    <>A temporary password will be generated for you. You can continue using the dashboard immediately without needing to log out.</>
                  ) : (
                    <>A temporary password will be generated and sent to the user's email address. The user will be required to change their password on next login.</>
                  )}
                </Typography>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={closePasswordResetDialog}
            disabled={passwordResetDialog.loading}
          >
            {passwordResetDialog.result ? 'Close' : 'Cancel'}
          </Button>
          {!passwordResetDialog.result && (
            <Button
              variant="contained"
              color="warning"
              onClick={confirmPasswordReset}
              disabled={passwordResetDialog.loading}
                                          startIcon={passwordResetDialog.loading ? <CircularProgress size={16} sx={{ color: '#005F94' }} /> : <VpnKeyIcon />}
            >
              {passwordResetDialog.loading 
                ? 'Resetting...' 
                : isCurrentUser(passwordResetDialog.user)
                  ? 'Reset My Password' 
                  : 'Reset Password'
              }
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

const OrganizationDashboard = React.memo(() => {
  // One-time initial order handoff to iframe to prevent KPI misorder on first paint  
  const initialOrderSentRef = useRef(false);
  const isDev = import.meta.env.DEV;
  
  const params = useParams();
  let { orgSlug } = params;
  // Extract view from params (splat route)
  const splat = params['*'] || '';
  const viewFromParams = splat.split('/')[0];
  
  // Only log on mount or orgSlug change to reduce spam
  const orgSlugRef = useRef(orgSlug);
  if (orgSlugRef.current !== orgSlug) {
    orgSlugRef.current = orgSlug;
  }
  
  // Manual fallback: extract orgSlug from pathname if useParams fails
  if (!orgSlug && window.location.pathname.includes('/org/')) {
    const pathParts = window.location.pathname.split('/');
    const orgIndex = pathParts.indexOf('org');
    if (orgIndex !== -1 && orgIndex + 1 < pathParts.length) {
      orgSlug = pathParts[orgIndex + 1];
    }
  }
  
  // Normalize global workspace slug
  const isGlobalWorkspace = orgSlug === 'global';
  if (isGlobalWorkspace) {
    orgSlug = 'global';
  }
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setSkipAuthCheck, setUser } = useAuth();
  const { currentTheme, changeTheme } = useTheme();

  // State management
  const [organizationData, setOrganizationData] = useState(null);
  const [availableViews, setAvailableViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authenticationRequired, setAuthenticationRequired] = useState(false);

  // Debug lifecycle - track mount/unmount with stack trace
  useEffect(() => {
    const mountTime = Date.now();
    return () => {
      console.log('🛑 [OrganizationDashboard] UNMOUNTED after', Date.now() - mountTime, 'ms');
      console.trace('🔍 Unmount stack trace');
    };
  }, []);
  
  // Super admin state
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [orgSwitcherOpen, setOrgSwitcherOpen] = useState(false);

  // Password protection state
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // UI State - Updated for panel approach
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [settingsPanelOpen, setSettingsPanelOpen] = useState(true);
  
  // Pillar management state
  const [availablePillars, setAvailablePillars] = useState([]);
  const [visiblePillars, setVisiblePillars] = useState([]);
  const [loadingPillars, setLoadingPillars] = useState(false);
  const [expandedPillars, setExpandedPillars] = useState(new Set());
  // Cache last known good pillars per org+view to avoid transient empties
  const lastGoodPillarsRef = useRef(new Map()); // key: `${orgSlug}:${viewId}` -> pillars array
  
  // NEW: Pending changes state for manual save control
  const [pendingChanges, setPendingChanges] = useState({
    hasVisibilityChanges: false,
    hasOrderChanges: false,
    pendingVisiblePillars: null,
    pendingPillarOrder: null,
    originalVisiblePillars: null // Store original state for reset
  });
  
  // Update debug state for debug panel access (must be after pendingChanges declaration)
  useEffect(() => {
    window.debugPillarState = {
      availablePillars,
      visiblePillars,
      pendingChanges: pendingChanges?.hasVisibilityChanges || pendingChanges?.hasOrderChanges || false
    };
  }, [availablePillars, visiblePillars, pendingChanges]);
  
  // Consistent view resolution from URL path — used by all functions that need the current view
  // URL structure: /org/:orgSlug/:view, so view is at index 3
  const currentViewFromPath = useMemo(() => {
    const viewSegment = (location.pathname.split('/')[3] || '').toLowerCase();
    return viewSegment ? resolveViewFromParam(availableViews, viewSegment) : null;
  }, [location.pathname, availableViews]);

  // Refs
  const iframeRef = useRef(null);
  const containerRef = useRef(null);

  // Handshake cache: store the last full UPDATE_SETTINGS postMessage payload we sent.
  // The embedded dashboard may not have its message listener ready on first load.
  const lastSettingsUpdateRef = useRef(null);

  // Clear cached settings when organization or view changes to prevent stale data
  useEffect(() => {
    lastSettingsUpdateRef.current = null;
  }, [orgSlug, location.pathname]);

  // Clear stale headerTitle when view changes — prevents previous view's title from bleeding into the new view.
  // The correct title for the new view will be loaded from the DB via the org settings API fetch.
  const prevViewCodeRef = useRef(currentViewFromPath?.code);
  useEffect(() => {
    if (currentViewFromPath?.code && prevViewCodeRef.current !== currentViewFromPath.code) {
      prevViewCodeRef.current = currentViewFromPath.code;
      setOrganizationData(prev => {
        if (!prev?.branding?.headerTitle) return prev; // nothing to clear
        return {
          ...prev,
          branding: {
            ...prev.branding,
            headerTitle: null
          }
        };
      });
    }
  }, [currentViewFromPath?.code]);

  // Listen for IFRAME_READY messages from the embedded dashboard and resend the last settings payload.
  useEffect(() => {
    const handleIframeReady = (event) => {
      if (!event?.data || event.data.type !== 'IFRAME_READY') return;
      if (event.data.orgSlug && event.data.orgSlug !== orgSlug) return;
      if (!iframeRef.current?.contentWindow) return;
      
      // Get current view from path
      const pathView = (location.pathname.split('/').pop() || '').toLowerCase();
      
      // Check if cached settings are stale (from a different org or view)
      // orgSlug is stored in settings.organization.slug or settings.branding.orgSlug
      const cachedOrgSlug = lastSettingsUpdateRef.current?.settings?.organization?.slug || 
                            lastSettingsUpdateRef.current?.settings?.branding?.orgSlug;
      const cachedViewCode = (lastSettingsUpdateRef.current?.settings?.viewCode || '').toLowerCase();
      const isCacheStale = cachedOrgSlug && cachedOrgSlug !== orgSlug;
      const isViewMismatch = cachedViewCode && cachedViewCode !== pathView && cachedViewCode !== pathView.toUpperCase();
      
      if (!lastSettingsUpdateRef.current || isCacheStale || isViewMismatch) {
        if (isCacheStale) {
          console.warn('🤝 [IFRAME_READY] Cached settings are from different org, clearing cache and fetching fresh:', {
            cachedOrg: cachedOrgSlug,
            currentOrg: orgSlug
          });
          lastSettingsUpdateRef.current = null;
        } else if (isViewMismatch) {
          console.warn('🤝 [IFRAME_READY] Cached settings are from different view, clearing cache and fetching fresh:', {
            cachedView: cachedViewCode,
            currentView: pathView
          });
          lastSettingsUpdateRef.current = null;
        } else {
          console.warn('🤝 [IFRAME_READY] Received, but no cached settings payload to resend. Triggering immediate pillar fetch.');
        }
        // Trigger immediate pillar fetch for current view
        const currentView = currentViewFromPath;
        if (currentView) {
          fetchPillars(currentView.id);
        } else {
          console.warn('⚠️ [IFRAME_READY] No matching view found. Available views not loaded yet. Will rely on normal fetch triggers.');
        }
        return;
      }

      try {
        iframeRef.current.contentWindow.postMessage(lastSettingsUpdateRef.current, '*');
      } catch (e) {
        console.warn('🤝 [IFRAME_READY] Failed to resend cached settings:', e);
      }
    };

    window.addEventListener('message', handleIframeReady);
    return () => window.removeEventListener('message', handleIframeReady);
  }, [orgSlug, location.pathname, availableViews]); // fetchPillars is stable, no need in deps



  // MOVED: Reset to Admin Default function (moved here to access setPendingChanges state)
  const handleResetToAdminDefault = () => {
    // CRITICAL: Capture current state BEFORE making changes for proper reset functionality
    const currentStateBeforeAdminReset = [...visiblePillars];
    
    // Prevent auto-selection during reset by setting a longer suppression period
    const autoSelectionSuppressTime = Date.now() + 2000; // 2 second suppression
    window.pillarToggleAutoSelectionSuppress = autoSelectionSuppressTime;
    
    // CRITICAL FIX: Sort availablePillars by admin default order for consistency
    const sortedAdminPillars = [...availablePillars].sort((a, b) => {
      // Primary sort: custom_order (admin default order)
      const aCustom = typeof a.custom_order === 'number' ? a.custom_order : Infinity;
      const bCustom = typeof b.custom_order === 'number' ? b.custom_order : Infinity;
      if (aCustom !== bCustom) return aCustom - bCustom;
      
      // Fallback sort: order_index
      const aOrder = typeof a.order_index === 'number' ? a.order_index : Infinity;
      const bOrder = typeof b.order_index === 'number' ? b.order_index : Infinity;
      if (aOrder !== bOrder) return aOrder - bOrder;
      
      // Final fallback: name alphabetical
      return (a.name || '').localeCompare(b.name || '');
    });
    
    
    // Update local state first for immediate UI feedback
    setVisiblePillars(sortedAdminPillars);
    
    // Update organization data and send to iframe
    const updatedPillarSettings = {
      visiblePillars: sortedAdminPillars,
      pillarOrder: sortedAdminPillars // Preserve admin order
    };
    
    
    const currentView = currentViewFromPath;
    
    setOrganizationData(prev => ({
      ...prev,
      pillarSettings: {
        ...prev?.pillarSettings,
        [currentView?.code]: {
          ...prev.pillarSettings?.[currentView?.code],
          ...updatedPillarSettings
        }
      }
    }));
    
    // Send bulk update to iframe (mimicking the existing sendSettingsUpdateToIframe pattern)
    setTimeout(() => {
      console.log('🔄 RESET: Sending admin default state to iframe');
      
      const iframeData = {
        pillars: sortedAdminPillars,
        pillarCount: sortedAdminPillars.length,
        // IMPORTANT: No pillarToggle event to prevent auto-selection issues
        isResetOperation: true
      };
      
      sendSettingsUpdateToIframe({
        pillars: iframeData.pillars,
        orderedPillars: iframeData.pillars,
        resetOperation: true
      });
      
      // After reset, select the first pillar in the visual order
      setTimeout(() => {
        // Use the already-sorted admin pillars for first pillar selection
        const firstPillar = sortedAdminPillars[0];
          
        if (firstPillar) {
          console.log('🎯 RESET: Selecting first pillar after reset:', firstPillar.name);
          
          // Send pillar selection update to iframe without triggering auto-selection logic
          if (iframeRef && iframeRef.current) {
            iframeRef.current.contentWindow?.postMessage({
              type: 'SELECT_PILLAR',
              pillarId: firstPillar.id,
              pillarName: firstPillar.name,
              isResetSelection: true
            }, '*');
          }
        }
        
        // Clear the suppression flag
        delete window.pillarToggleAutoSelectionSuppress;
      }, 100);
      
    }, 50);
    
    // CRITICAL FIX: Mark admin default as pending changes (not permanent)
    // This allows users to Save (persist) or Reset (revert) the admin default
    setPendingChanges(prev => {
      const originalToUse = prev.originalVisiblePillars || currentStateBeforeAdminReset;
      console.log('🔄 ADMIN RESET: Setting pending changes:', {
        hadPendingChanges: !!(prev.hasVisibilityChanges || prev.hasOrderChanges),
        hadOriginalState: !!prev.originalVisiblePillars,
        usingOriginalFrom: prev.originalVisiblePillars ? 'previous-pending' : 'current-state',
        originalCount: originalToUse.length,
        originalFirstPillar: originalToUse[0]?.name || 'NONE',
        newPendingCount: sortedAdminPillars.length,
        newPendingFirstPillar: sortedAdminPillars[0]?.name || 'NONE'
      });
      
      return {
        hasVisibilityChanges: true,
        hasOrderChanges: true, // Admin default may have different order
        pendingVisiblePillars: sortedAdminPillars, // Use sorted admin pillars for consistency
        pendingPillarOrder: sortedAdminPillars,
        originalVisiblePillars: originalToUse // Preserve original saved state for reset
      };
    });
    
    console.log('✅ Reset to admin default completed - Save/Reset buttons should appear');
  };

  // Public access detection (supports /public and /embed routes)
  const urlParams = new URLSearchParams(location.search);
  const tokenParam = urlParams.get('t') || urlParams.get('token');
  const isPublicRoute = location.pathname.startsWith('/public/');
  const isEmbedRoute = location.pathname.startsWith('/embed/');
  const accessToken = tokenParam || (isEmbedRoute ? location.pathname.split('/').pop() : null);
  const isPublicAccess = isPublicRoute || isEmbedRoute || !!accessToken;

  // Skip authentication only for token-based access
  useEffect(() => {
    if (isPublicAccess) {
      setSkipAuthCheck(true);
    }
    return () => { setSkipAuthCheck(false); };
  }, [setSkipAuthCheck, isPublicAccess]);

  // Preload dropdown fonts for better UX (fonts loaded state managed in settings panel)
  useEffect(() => {
    const dropdownFonts = [
      'Open Sans', 'Roboto', 'Lato', 'Montserrat', 'Source Sans Pro',
      'Inter', 'Poppins', 'Nunito', 'Barlow', 'Work Sans', 
      'Fira Sans', 'Libre Baskerville'
    ];
    
    // Load fonts without setting state (state is managed in DashboardSettingsPanel)
    dropdownFonts.forEach(font => loadOrganizationFont(font));
  }, []);

  // Load saved pillar settings for all views using the new database endpoint
  const loadSavedPillarSettings = useCallback(async (orgData, views, orgSlug) => {
    try {
      const pillarSettings = {};
      
      // Fetch pillar settings for each view from the new unified endpoint
      for (const view of views) {
        try {
          // Loading organization pillar settings for view
          const response = await fetch(`/api/organizations/${orgSlug}/settings/${view.code}`, {
            credentials: 'include'
          });
          
          if (response.ok) {
            const data = await response.json();
            // Organization settings loaded successfully
            
            // Extract pillar keys from the organization settings
            pillarSettings[view.code] = data.pillars || [];
          } else {
            console.log(`⚠️ No organization settings found for ${view.code}, using empty array`);
            // If no settings found, use empty array (show all pillars by default)
            pillarSettings[view.code] = [];
          }
        } catch (error) {
          console.error(`❌ Error loading organization settings for ${view.code}:`, error);
          pillarSettings[view.code] = [];
        }
      }
      
      console.log('📋 Final organization pillar settings loaded:', {
        views: Object.keys(pillarSettings),
        totalSettings: Object.values(pillarSettings).reduce((sum, arr) => sum + arr.length, 0),
        settingsBreakdown: pillarSettings
      });
      
      // Update organization data with pillar settings
      setOrganizationData(prev => ({
        ...prev,
        pillarSettings
      }));
      
    } catch (error) {
      console.error('❌ Error loading saved pillar settings:', error);
    }
  }, []);

  // Standalone function to fetch organization data (can be called from multiple places)
  const fetchOrganizationData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build request headers
      const headers = {};
      if (password) {
        headers['X-Org-Password'] = password;
      }
      if (accessToken) {
        headers['X-Access-Token'] = accessToken;
      }

      const response = await fetch(`/api/test-multitenant/org/${orgSlug}/views`, {
        headers
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          if (isPublicAccess) {
            setError('Invalid or expired access token');
          } else {
            setAuthenticationRequired(true);
          }
          return;
        }
        if (response.status === 403) {
          if (isPublicAccess) {
            setError('Access denied with this token');
          } else {
            setPasswordRequired(true);
          }
          return;
        }
        throw new Error(`Failed to load organization data: ${response.statusText}`);
      }

      const data = await response.json();
      setOrganizationData(data.organization);
      setAvailableViews(data.views);
      setPasswordRequired(false);
      setPasswordError('');

      // Load saved pillar settings for all views
      await loadSavedPillarSettings(data.organization, data.views, orgSlug);
      
      // Check if user is super admin and fetch all organizations
      await checkSuperAdminAndFetchOrgs();
      
      // Apply organization theme if available
      if (data.organization?.theme) {
        // TODO: Apply custom organization theme
        console.log('Organization theme:', data.organization.theme);
      }
      
    } catch (err) {
      console.error('Error fetching organization data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orgSlug, password, accessToken, isPublicAccess]);

  // Resolve public link and fetch organization data
  useEffect(() => {
    console.log('🔄 [DATA_FETCH_EFFECT] triggered', { 
      orgSlug, 
      isPublicRoute, 
      isEmbedRoute,
      timestamp: Date.now(),
      loading
    });
    
    // RACE CONDITION FIX: Reset initial order sent flag when organization changes
    // This allows the iframe to receive correct org-specific pillars for the new organization
    initialOrderSentRef.current = false;
    const resolveAndFetch = async () => {
      try {
        if (isPublicRoute || isEmbedRoute) {
          const urlParts = location.pathname.split('/').filter(Boolean);
          // Check if we are on /public/:orgSlug/:view (3 parts)
          const isOrgViewPublicRoute = isPublicRoute && urlParts.length === 3 && urlParts[0] === 'public';
          
          const tokenOrSlug = accessToken || (isPublicRoute ? (location.pathname.split('/').slice(-1)[0] || '') : '');
          
          // If it's a direct org/view public route, or no token is provided, just fetch org data
          if (isOrgViewPublicRoute || !tokenOrSlug) {
            console.log('🔗 Direct public view route detected, fetching organization data directly');
            if (orgSlug === 'global') {
              // Global workspace: load all views without org scoping (same as non-public path)
              console.log('🌍 Loading global workspace (public route)...');
              try {
                setLoading(true);
                const res = await fetch('/api/views');
                if (!res.ok) throw new Error('Failed to load global views');
                const allViews = await res.json();
                setOrganizationData({ name: 'Global Workspace', slug: 'global' });
                const norm = allViews.map(c => ({ 
                  id: c.id || c.code || c.iso3, 
                  name: c.name, 
                  code: (c.code || c.iso3 || '').toLowerCase(),
                  iso3: (c.iso3 || c.code || '').toUpperCase()
                }));
                setAvailableViews(norm);
              } catch (err) {
                console.error('❌ Error loading global workspace (public):', err);
                setError(err.message);
              } finally {
                setLoading(false);
              }
            } else {
              await fetchOrganizationData();
            }
            return;
          }
          
          const api = await import('../api/shareLinks');
          const res = await api.resolveShareLink(tokenOrSlug);
          if (res?.success) {
            // Sanity: ensure orgSlug matches resolved org when present
            if (res.organization?.slug && orgSlug && res.organization.slug !== orgSlug) {
              console.warn('Organization slug mismatch between URL and resolved token');
            }
            await fetchOrganizationData();
          } else {
            setError('Invalid or inactive public link');
          }
        } else if (orgSlug) {
          if (orgSlug === 'global') {
            // Global workspace: load all views without org scoping
            console.log('🌍 Loading global workspace...');
            try {
              setLoading(true);
              console.log('📡 Fetching views from /api/views...');
              const res = await fetch('/api/views');
              if (!res.ok) throw new Error('Failed to load global views');
              const allViews = await res.json();
              console.log('✅ Views loaded:', allViews.length, 'views');
              setOrganizationData({ name: 'Global Workspace', slug: 'global' });
              // Normalize to expected structure { id, name, code }
              const norm = allViews.map(c => ({ 
                id: c.id || c.code || c.iso3, 
                name: c.name, 
                code: (c.code || c.iso3 || '').toLowerCase(),
                iso3: (c.iso3 || c.code || '').toUpperCase()
              }));
              console.log('🗺️ Available views:', norm.map(c => ({ name: c.name, code: c.code, id: c.id })));
              console.log('🔍 Looking for view code:', 'mmr', 'in available views');
              // Check if Myanmar exists with different codes
              const myanmarVariants = norm.filter(c => 
                c.name.toLowerCase().includes('myanmar') || 
                c.code === 'mmr' || 
                c.code === 'mm' || 
                c.code === 'mya'
              );
              console.log('🇲🇲 Myanmar variants found:', myanmarVariants);
              setAvailableViews(norm);
              console.log('🎯 Available views set:', norm.length);
              
              // Check if user is super admin and fetch all organizations
              console.log('👑 Checking super admin status...');
              await checkSuperAdminAndFetchOrgs();
              console.log('✅ Global workspace setup complete');
            } catch (err) {
              console.error('❌ Error loading global workspace:', err);
              setError(err.message);
            } finally {
              setLoading(false);
              console.log('🏁 Loading state set to false');
            }
          } else {
            await fetchOrganizationData();
          }
        }
      } catch (e) {
        console.error('Error resolving public link:', e);
        setError('Failed to resolve link');
      }
    };
    resolveAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgSlug, isPublicRoute, isEmbedRoute]);

  // Debug: Track loading state changes
  useEffect(() => {
    console.log('📊 [LOADING_STATE] changed to:', loading, 'at', Date.now());
  }, [loading]);

  // Check super admin status and fetch all organizations
  const checkSuperAdminAndFetchOrgs = useCallback(async () => {
    try {
      const isDev = import.meta.env.DEV;
      // Only allow super-admin detection when explicitly enabled
      const params = new URLSearchParams(window.location.search);
      const testAdminEnabled = params.get('test-user') === 'admin' || import.meta.env.VITE_ENABLE_SUPERADMIN_TEST === 'true';
      const isLoggedInSuperAdmin = !!(user?.is_admin || user?.organization_role === 'super_admin');

      if (!testAdminEnabled && !isLoggedInSuperAdmin) {
        setIsSuperAdmin(false);
        setAllOrganizations([]);
        return;
      }

      const response = await fetch('/api/admin/organizations', {
        credentials: 'include',
        headers: testAdminEnabled ? { 'x-test-user': 'admin' } : {}
      });
      
      if (response.ok) {
        const organizations = await response.json();
        setIsSuperAdmin(true);
        setAllOrganizations(organizations);
        if (isDev) console.log('Super admin access detected, organizations loaded:', organizations.length);
      } else {
        setIsSuperAdmin(false);
        setAllOrganizations([]);
      }
    } catch (error) {
      const isDev = import.meta.env.DEV;
      if (isDev) console.log('Not a super admin or admin API unavailable');
      setIsSuperAdmin(false);
      setAllOrganizations([]);
    }
  }, [user]);

  // Check super admin status when auth changes or on mount
  useEffect(() => {
    checkSuperAdminAndFetchOrgs();
  }, [checkSuperAdminAndFetchOrgs]);

  // Handle organization switching for super admin
  const handleOrganizationSwitch = async (newOrgSlug) => {
    if (!isSuperAdmin || newOrgSlug === orgSlug) {
      setOrgSwitcherOpen(false);
      return;
    }
    
    try {
      // Close the dialog first for immediate feedback
      setOrgSwitcherOpen(false);
      
      // Fetch the new organization's available views to determine valid navigation target
      const response = await fetch(`/api/organizations/${newOrgSlug}`, {
        credentials: 'include'
      });
      
      let newOrgViews = [];
      if (response.ok) {
        const newOrgData = await response.json();
        newOrgViews = newOrgData.views || [];
      }
      
      // Get current view code from URL
      const pathParts = location.pathname.split('/');
      const currentViewCode = pathParts[pathParts.length - 1];
      
      // Check if current view exists in the new organization
      const viewExistsInNewOrg = newOrgViews.some(v => 
        (v.code || '').toLowerCase() === currentViewCode.toLowerCase() ||
        (v.iso3 || '').toLowerCase() === currentViewCode.toLowerCase() ||
        (v.name || '').toLowerCase() === currentViewCode.toLowerCase()
      );
      
      let targetView = currentViewCode;
      if (!viewExistsInNewOrg && newOrgViews.length > 0) {
        // Current view doesn't exist in new org - use first available view
        const firstView = newOrgViews[0];
        targetView = firstView.code || firstView.iso3 || firstView.name || '';
        console.log(`View "${currentViewCode}" not available in ${newOrgSlug}, redirecting to "${targetView}"`);
      }
      
      const newPath = `/org/${newOrgSlug}${targetView ? `/${targetView.toLowerCase()}` : ''}`;
      
      console.log(`Super admin switching from ${orgSlug} to ${newOrgSlug}`);
      navigate(newPath);
      
    } catch (error) {
      console.error('Error switching organization:', error);
      // Fallback: navigate to org root and let it handle view selection
      navigate(`/org/${newOrgSlug}`);
      setOrgSwitcherOpen(false);
    }
  };

  // Handle password submission
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (!password.trim()) {
      setPasswordError('Please enter a password');
      return;
    }
    setPasswordError('');
    // The useEffect will trigger with the new password
  };

  // Handle theme changes
  const handleThemeChange = async (orgSettings) => {
    try {
      // Create and apply organization theme
      const theme = OrganizationThemeGenerator.createDynamicTheme(orgSettings);
      
      // Apply the theme via the existing theme context
      changeTheme(theme);
      
      // Apply organization-specific CSS variables and custom styles
      OrganizationThemeGenerator.applyOrganizationCssVariables(orgSettings);
      
      if (orgSettings.customCss) {
        OrganizationThemeGenerator.applyCustomCSS(orgSettings.customCss, organizationData?.id);
      }
      
      // Save organization branding settings to backend
      // Only send font values to backend if DB has them or user changed them from default
      const dbHasFont = organizationData?.branding?.fontFamily;
      const dbHasPrimaryFont = organizationData?.branding?.primaryFontFamily;
      const dbHasSecondaryFont = organizationData?.branding?.secondaryFontFamily;
      const fontDefault = 'Open Sans';
      const brandingSettings = {
        theme_primary_color: orgSettings.primaryColor,
        theme_secondary_color: orgSettings.secondaryColor,
        theme_font_family: (dbHasFont || orgSettings.fontFamily !== fontDefault) ? orgSettings.fontFamily : undefined,
        primary_font_family: (dbHasPrimaryFont || orgSettings.primaryFontFamily !== fontDefault) ? orgSettings.primaryFontFamily : undefined,
        secondary_font_family: (dbHasSecondaryFont || orgSettings.secondaryFontFamily !== fontDefault) ? orgSettings.secondaryFontFamily : undefined,
        custom_logo_url: orgSettings.logoUrl,
        dashboard_title_template: orgSettings.dashboardTitle,
        subtitle: orgSettings.subtitle,
        disclaimer_text: orgSettings.disclaimerText,
        // Header Configuration
        header_style: orgSettings.headerStyle,
        header_title: undefined,
        header_subtitle: orgSettings.headerSubtitle,
        header_logo_url: orgSettings.headerLogoUrl,
        header_logo_style: orgSettings.headerLogoStyle,
        header_disclaimer_text: orgSettings.headerDisclaimerText,
        header_disclaimer_body: orgSettings.headerDisclaimerBody,
        header_disclaimer_icon: orgSettings.headerDisclaimerIcon,
        status_indicator_style: orgSettings.statusIndicatorStyle,
        header_color_mode: orgSettings.headerColorMode,
        show_status_schedule: orgSettings.showStatusSchedule
      };
      
      try {
        if (!orgSlug) {
          console.warn('No orgSlug found; skipping branding save');
        } else {
          const response = await fetch('/api/organization-settings', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-Test-Org': orgSlug,
          },
          credentials: 'include',
          body: JSON.stringify(brandingSettings),
          });

          if (response.ok) {
          const updatedSettings = await response.json();
          // Reduced logging - only show success message
          console.log('✅ Organization branding settings saved successfully');
          
          // Update local organization data
          // Only write font fields if DB already has them or user explicitly changed them from default
          const saveFontFields = {};
          const defaultFont = 'Open Sans';
          if (organizationData?.branding?.fontFamily || orgSettings.fontFamily !== defaultFont) saveFontFields.fontFamily = orgSettings.fontFamily;
          if (organizationData?.branding?.primaryFontFamily || orgSettings.primaryFontFamily !== defaultFont) saveFontFields.primaryFontFamily = orgSettings.primaryFontFamily;
          if (organizationData?.branding?.secondaryFontFamily || orgSettings.secondaryFontFamily !== defaultFont) saveFontFields.secondaryFontFamily = orgSettings.secondaryFontFamily;
          setOrganizationData(prev => ({
            ...prev,
            branding: {
              ...prev?.branding,
              primaryColor: orgSettings.primaryColor,
              secondaryColor: orgSettings.secondaryColor,
              ...saveFontFields,
              logo: orgSettings.logoUrl,
              title: orgSettings.dashboardTitle,
              subtitle: orgSettings.subtitle,
              disclaimerText: orgSettings.disclaimerText,
              // Header Configuration
              headerStyle: orgSettings.headerStyle,
              headerTitle: orgSettings.headerTitle,
              headerSubtitle: orgSettings.headerSubtitle,
              headerLogoUrl: orgSettings.headerLogoUrl,
              headerLogoStyle: orgSettings.headerLogoStyle,
              headerDisclaimerText: orgSettings.headerDisclaimerText,
              headerDisclaimerBody: orgSettings.headerDisclaimerBody,
              headerDisclaimerIcon: orgSettings.headerDisclaimerIcon,
              statusIndicatorStyle: orgSettings.statusIndicatorStyle,
              headerColorMode: orgSettings.headerColorMode,
              showStatusSchedule: orgSettings.showStatusSchedule
            }
          }));

          // Save per-view custom_title (header title is per-view, not per-org)
          console.log('🏷️ [TITLE SAVE] Checking per-view title save:', {
            currentViewCode: currentViewFromPath?.code,
            headerTitle: orgSettings.headerTitle,
            headerTitleType: typeof orgSettings.headerTitle,
            willSave: !!(currentViewFromPath?.code && orgSettings.headerTitle !== undefined)
          });
          if (currentViewFromPath?.code && orgSettings.headerTitle !== undefined) {
            try {
              const titleResp = await fetch(`/api/organizations/${orgSlug}/settings/${currentViewFromPath.code}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ customTitle: orgSettings.headerTitle || null }),
              });
              console.log('🏷️ [TITLE SAVE] Per-view title save response:', titleResp.status, titleResp.ok);
            } catch (e) {
              console.error('Failed to save per-view custom title:', e);
            }
          }

          // NEW: Send theme update via postMessage (immediate, no reload needed)
          // Only include font fields if DB actually has them — avoid sending fallback defaults
          const fontFields = {};
          if (organizationData?.branding?.fontFamily) fontFields.fontFamily = orgSettings.fontFamily;
          if (organizationData?.branding?.primaryFontFamily) fontFields.primaryFontFamily = orgSettings.primaryFontFamily;
          if (organizationData?.branding?.secondaryFontFamily) fontFields.secondaryFontFamily = orgSettings.secondaryFontFamily;
          sendSettingsUpdateToIframe({
            branding: {
              ...organizationData?.branding,
              primaryColor: orgSettings.primaryColor,
              secondaryColor: orgSettings.secondaryColor,
              ...fontFields,
              headerStyle: orgSettings.headerStyle,
              headerTitle: orgSettings.headerTitle,
              headerSubtitle: orgSettings.headerSubtitle,
              headerLogoUrl: orgSettings.headerLogoUrl,
              headerLogoStyle: orgSettings.headerLogoStyle,
              showStatusSchedule: orgSettings.showStatusSchedule,
              showDisclaimer: orgSettings.showDisclaimer
            },
            themeUpdate: true
          });
          } else {
            console.error('Failed to save organization branding settings:', response.statusText);
          }
        }
      } catch (error) {
        console.error('Error saving organization branding settings:', error);
      }
      
      // Save pillar settings if they were updated
      // NOTE: This legacy endpoint can overwrite ordered pillar settings stored via /api/organizations/:orgSlug/settings/:viewCode.
      // We skip it when there are pending pillar changes to avoid clobbering display_order.
      if (orgSettings.pillarSettings && !hasPendingChanges) {
        const currentView = currentViewFromPath;
        
        if (currentView) {
          const pillarKeys = orgSettings.pillarSettings[currentView.code] || [];
          
          try {
            const response = await fetch(`/api/organization-settings/pillars/${currentView.code}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'X-Test-Org': orgSlug,
              },
              credentials: 'include',
              body: JSON.stringify({ pillarIds: pillarKeys }),
            });
            
            if (response.ok) {
              console.log('✅ Pillar settings saved successfully');
              // Update organization data with new pillar settings
              setOrganizationData(prev => ({
                ...prev,
                pillarSettings: {
                  ...prev?.pillarSettings,
                  [currentView.code]: pillarKeys
                }
              }));
            } else {
              console.error('Failed to save pillar settings:', response.statusText);
            }
          } catch (error) {
            console.error('Error saving pillar settings:', error);
          }
        }
      }
      
      
      // Reduced logging to prevent console spam
      if (process.env.NODE_ENV === 'development') {
        console.log('Organization theme applied:', { 
          primaryColor: orgSettings.primaryColor, 
          secondaryColor: orgSettings.secondaryColor 
        });
      }
      
    } catch (error) {
      console.error('Error applying organization theme:', error);
      throw error;
    }
  };

  // Helper function to sort pillars by hierarchy (same as PillarVisibilityManager)
  const sortPillarsByHierarchy = (pillars) => {
    if (!Array.isArray(pillars)) return [];
    
    // Create a deep copy to avoid mutations
    const validData = [...pillars].filter(p => p && p.id);
    
    // Separate parent pillars and sub-pillars
    const parentPillars = validData.filter(p => !p.parent_pillar_id);
    const subPillars = validData.filter(p => p.parent_pillar_id);
    
      // Sort parent pillars by order_index, with special handling for problematic pillars with order_index 0
  parentPillars.sort((a, b) => {
    const aIsProblematic = (a.name === 'Education' || a.name === 'Humanitarian Access') && (a.order_index || 0) === 0;
    const bIsProblematic = (b.name === 'Education' || b.name === 'Humanitarian Access') && (b.order_index || 0) === 0;
    
    // Force only problematic pillars (with order_index 0) to the end
    if (aIsProblematic && !bIsProblematic) return 1;
    if (!aIsProblematic && bIsProblematic) return -1;
    if (aIsProblematic && bIsProblematic) {
      // Between problematic pillars, sort alphabetically
      return (a.name || '').localeCompare(b.name || '');
    }
    
    // Normal sorting for all other pillars (including Humanitarian Access with order_index 18)
    if (a.order_index === b.order_index) {
      return (a.name || '').localeCompare(b.name || '');
    }
    return (a.order_index || 0) - (b.order_index || 0);
  });
    
    // Create the final sorted array
    const sortedData = [];
    
    // For each parent, add it and its children
    parentPillars.forEach(parent => {
      // Add the parent
      sortedData.push(parent);
      
      // Find and sort children
      const children = subPillars
        .filter(p => p.parent_pillar_id === parent.id)
        .sort((a, b) => {
          if (a.order_index === b.order_index) {
            return (a.name || '').localeCompare(b.name || '');
          }
          return (a.order_index || 0) - (b.order_index || 0);
        });
      
      // Add children
      sortedData.push(...children);
    });
    
    // Add any remaining sub-pillars that might have been orphaned
    const remainingSubPillars = subPillars.filter(
      p => !sortedData.some(sorted => sorted.id === p.id)
    );
    
    if (remainingSubPillars.length > 0) {
      sortedData.push(...remainingSubPillars.sort((a, b) => {
        if (a.order_index === b.order_index) {
          return (a.name || '').localeCompare(b.name || '');
        }
        return (a.order_index || 0) - (b.order_index || 0);
      }));
    }
    
    return sortedData;
  };

  // Fetch available pillars for the selected view using admin baseline
  // Fetch token to ignore stale responses across org/view switches
  const fetchContextRef = useRef({ org: null, viewId: null, token: 0 });
  // Mute pillar reloads briefly after local user actions (toggles/order)
  const localChangeUntilRef = useRef(0);
  const pillarFetchInFlightRef = useRef(new Set());
  // Deduplicate overlapping effects that can both trigger fetchPillars
  const lastAutoPillarFetchRef = useRef({ key: null, ts: 0 });

  const fetchPillars = async (viewId) => {
    if (!viewId) return;
    const inflightKey = `${orgSlug || 'unknown'}:${viewId}`;
    if (pillarFetchInFlightRef.current.has(inflightKey)) return;
    pillarFetchInFlightRef.current.add(inflightKey);
    if (loadingPillars) {
      pillarFetchInFlightRef.current.delete(inflightKey);
      return;
    }
    if (Date.now() < localChangeUntilRef.current) {
      if (isDev) console.log('DBG:PILLARS FETCH SKIP', { reason: 'local-change-window' });
      pillarFetchInFlightRef.current.delete(inflightKey);
      return;
    }
    
    setLoadingPillars(true);
    try {
      const currentView = availableViews.find(c => c.id === viewId);
      
      if (!currentView) {
        if (isDev) console.log('📋 No current view found');
        return;
      }

      // Capture current context and issue a new token
      const currentToken = (fetchContextRef.current.token || 0) + 1;
      fetchContextRef.current = { org: orgSlug, viewId: currentView.id, token: currentToken };

      // Step 1: Load MASTER admin-configured pillars for this view (PillarVisibilityManager data)
      if (isDev) console.log('🏛️ Loading admin-configured pillars for view:', currentView.name);
      
      try {
        const adminPillarsResponse = await fetch(`/api/views/${currentView.id}/pillars/visibility`, {
          credentials: 'include'
        });
        
        if (adminPillarsResponse.ok) {
          const adminPillarsData = await adminPillarsResponse.json();

          // Ignore stale response if org/view changed mid-flight
          const isStale = fetchContextRef.current.token !== currentToken || fetchContextRef.current.org !== orgSlug || fetchContextRef.current.viewId !== currentView.id;
          if (isStale) {
            console.warn('⏭️ Ignoring stale adminPillarsResponse due to context change');
            return;
          }
          if (isDev) console.log('✅ Loaded admin pillar configuration:', {
            totalRecords: adminPillarsData.length,
            viewId: currentView.id,
            viewName: currentView.name
          });
          
          if (adminPillarsData && adminPillarsData.length > 0) {
            // Transform admin data to match our pillar structure
            // Processing admin pillar data
            
            const adminPillars = adminPillarsData.map((item, index) => ({
              id: item.pillar_id,
              name: item.pillar_name || 'Unnamed Pillar',
              key: item.pillar_key || item.pillar_id.toString(),
              parent_pillar_id: item.parent_pillar_id,
              order_index: typeof item.order_index === 'number' ? item.order_index : 0,
              // CRITICAL FIX: Set custom_order to preserve admin default order for sorting
              custom_order: typeof item.custom_order === 'number' ? item.custom_order : 
                           (typeof item.order_index === 'number' ? item.order_index : index),
              // Note: is_visible controls analysis behavior, not organization dashboard visibility
              analysis_behavior_flag: item.is_visible !== false, 
              // CRITICAL FIX: Map shows_own_data for proper auto-selection behavior
              showsOwnData: item.shows_own_data !== false, // Default to true if not specified
              framework_id: item.framework_id,
              color: item.pillar_color,
              description: item.pillar_description || item.description || '', // Add description from database
              subPillars: [] // Will be populated by hierarchy sorting
            }));
            
            if (isDev) console.log('🔍 Admin pillar analysis:', {
              totalAdminPillars: adminPillars.length,
              allAdminPillarNames: adminPillars.slice(0, 10).map(p => p.name),
              parentPillarsInAdmin: adminPillars.filter(p => !p.parent_pillar_id).map(p => ({ 
                name: p.name, 
                analysis_behavior_flag: p.analysis_behavior_flag,
                showsOwnData: p.showsOwnData,
                order_index: p.order_index,
                description: p.description
              })),
              contextPillar: adminPillars.find(p => p.name === 'Context') ? {
                name: 'Context',
                showsOwnData: adminPillars.find(p => p.name === 'Context').showsOwnData,
                analysis_behavior_flag: adminPillars.find(p => p.name === 'Context').analysis_behavior_flag,
                rawData: adminPillarsData.find(item => item.pillar_name === 'Context')
              } : 'NOT_FOUND',
              descriptionsFound: adminPillars.filter(p => p.description && p.description.length > 0).length,
              sampleDescriptions: adminPillars.slice(0, 3).map(p => ({
                name: p.name,
                description: p.description
              }))
            });
            
            // Apply admin hierarchy sorting (same logic as PillarVisibilityManager)
            const sortedAdminPillars = sortPillarsByHierarchy(adminPillars);
            
            // Filter based on correct visibility rules:
            // 1. Visible parent pillars: Always include (with or without sub-pillars)  
            // 2. Invisible parent pillars: Only include if they have visible sub-pillars
            // 3. Sub-pillars: Only include if visible
            
            const visibleSubPillars = sortedAdminPillars.filter(p => 
              p.parent_pillar_id && p.analysis_behavior_flag === true
            );
            
            const parentsWithVisibleChildren = new Set(
              visibleSubPillars.map(p => p.parent_pillar_id)
            );
            
            const adminVisiblePillars = sortedAdminPillars.filter(p => {
              if (p.parent_pillar_id) {
                // Sub-pillar: include only if visible
                return p.analysis_behavior_flag === true;
              } else {
                // Parent pillar: include if visible OR if invisible but has visible children
                return p.analysis_behavior_flag === true || parentsWithVisibleChildren.has(p.id);
              }
            });
            
            // Admin pillar configuration processed
            
            // Set available pillars to admin-visible pillars in admin-defined order
            // Setting admin-visible pillars as available pillars
            
            // SAFETY CHECK: Validate adminVisiblePillars before setting
            const safeAdminVisiblePillars = adminVisiblePillars.filter(pillar => {
              if (!pillar || typeof pillar !== 'object') return false;
              if (pillar.$$typeof || pillar._owner || pillar.type || pillar.props || pillar.ref) {
                console.error('❌ React component detected in adminVisiblePillars:', pillar.name || 'Unknown');
                return false;
              }
              return true;
            });
            
            if (safeAdminVisiblePillars.length !== adminVisiblePillars.length) {
              console.warn('⚠️ Filtered out React components from adminVisiblePillars');
            }
            
            // CRITICAL FIX: Sort pillars by custom_order since backend returns them in wrong order
            const sortedPillars = [...safeAdminVisiblePillars].sort((a, b) => {
              const aOrder = typeof a.custom_order === 'number' ? a.custom_order : Infinity;
              const bOrder = typeof b.custom_order === 'number' ? b.custom_order : Infinity;
              if (aOrder !== bOrder) return aOrder - bOrder;
              
              const aOrderIndex = typeof a.order_index === 'number' ? a.order_index : Infinity;
              const bOrderIndex = typeof b.order_index === 'number' ? b.order_index : Infinity;
              if (aOrderIndex !== bOrderIndex) return aOrderIndex - bOrderIndex;
              
              return (a.name || '').localeCompare(b.name || '');
            });
            
            setAvailablePillars(sortedPillars);
            // Focused baseline log
            try {
              if (isDev) console.log('DBG:FLOW PARENT BASELINE', {
                viewCode: currentView.code,
                adminBaselineCount: sortedPillars.length,
                firstFive: sortedPillars.slice(0,5).map(p => `${p.id}_${p.framework_id}`),
                firstFiveDetails: sortedPillars.slice(0,5).map(p => ({ 
                  id: p.id, 
                  name: p.name, 
                  custom_order: p.custom_order,
                  order_index: p.order_index 
                }))
              });
            } catch {}
            // Available pillars configured
            
            // Step 2: Apply organization overrides using SAME database source as iframe
            try {
              // Fetching organization settings from database
              
              const orgSettingsResponse = await fetch(`/api/organizations/${orgSlug}/settings/${currentView.code}?t=${Date.now()}`, {
                credentials: 'include'
              });
              
              if (orgSettingsResponse.ok) {
                const orgSettingsFromDB = await orgSettingsResponse.json();
                
                // DEBUG: Log the RAW pillars array from the API to verify order
                if (isDev && orgSettingsFromDB.pillars) {
                  console.log('🔍 RAW API PILLARS ORDER:', orgSettingsFromDB.pillars);
                  if (orgSettingsFromDB.pillars.length > 0) {
                    const firstPillar = orgSettingsFromDB.pillars[0];
                    console.log('🔍 FIRST PILLAR ID:', firstPillar);
                  }
                }
                
                // Always update branding headerTitle from per-view fetch (even when falsy)
                // This clears stale headerTitle from a previous view that had a custom_title
                const dbHeaderTitle = orgSettingsFromDB.branding?.headerTitle || null;
                setOrganizationData(prev => ({
                  ...prev,
                  branding: {
                    ...prev?.branding,
                    headerTitle: dbHeaderTitle
                  }
                }));
                
                // Send DB custom title to iframe explicitly (sendSettingsUpdateToIframe
                // no longer includes headerTitle to avoid stale overwrites on pillar updates)
                if (dbHeaderTitle && iframeRef.current?.contentWindow) {
                  iframeRef.current.contentWindow.postMessage({
                    type: 'UPDATE_SETTINGS',
                    settings: {
                      branding: { headerTitle: dbHeaderTitle }
                    }
                  }, '*');
                }

                // CRITICAL: Check if organization has saved settings (even if empty array)
                const hasOrgSettings = orgSettingsFromDB && orgSettingsFromDB.pillars !== undefined;
                const orgPillarKeys = orgSettingsFromDB.pillars || [];
                
                console.log('📋 Organization settings from database:', {
                  viewCode: currentView.code,
                  hasOrgSettings,
                  pillarCount: orgPillarKeys.length,
                  pillars: orgPillarKeys
                });
                
                if (hasOrgSettings) {
                  // Organization has SAVED settings (even if empty) - respect them
                  const isStaleInner = fetchContextRef.current.token !== currentToken || fetchContextRef.current.org !== orgSlug || fetchContextRef.current.viewId !== currentView.id;
                  if (isStaleInner) {
                    console.warn('⏭️ Ignoring stale orgSettingsResponse due to context change');
                    return;
                  }
                  
                  const pillarLookup = new Map();
                  sortedPillars.forEach(p => {
                    if (!p) return;
                    if (p.id !== undefined && p.id !== null) {
                      pillarLookup.set(p.id, p);
                      pillarLookup.set(String(p.id), p);
                    }
                    if (p.key) pillarLookup.set(p.key, p);
                    if (p.id !== undefined && p.id !== null && p.framework_id !== undefined && p.framework_id !== null) {
                      pillarLookup.set(`${p.id}_${p.framework_id}`, p);
                    }
                  });

                  const orgVisiblePillars = (orgPillarKeys || [])
                    .map(k => pillarLookup.get(k) || pillarLookup.get(String(k)))
                    .filter(Boolean);

                  // CRITICAL FIX: Inject display_order into visible pillars so iframe/consumers respect the array order
                  // even if they sort by object properties.
                  const orderedOrgVisiblePillars = orgVisiblePillars.map((p, index) => ({
                    ...p,
                    custom_order: index,
                    display_order: index,
                    order_index: index
                  }));

                  // CRITICAL FIX: Re-sort availablePillars to match the saved organization order.
                  // This ensures the Settings Panel (PillarManagementPanel) shows the correct custom order on load.
                  const orgOrderedAvailablePillars = [...sortedPillars].sort((a, b) => {
                    const indexA = orgPillarKeys.indexOf(a.id) !== -1 ? orgPillarKeys.indexOf(a.id) : orgPillarKeys.indexOf(String(a.id));
                    const indexB = orgPillarKeys.indexOf(b.id) !== -1 ? orgPillarKeys.indexOf(b.id) : orgPillarKeys.indexOf(String(b.id));
                    
                    // DEBUG: Log sort comparison for critical pillars
                    if ((a.id === 317 || a.id === 380) && (b.id === 317 || b.id === 380)) {
                      console.log('🔍 SORT DEBUG:', {
                        a: a.name, aId: a.id, indexA,
                        b: b.name, bId: b.id, indexB,
                        result: (indexA !== -1 && indexB !== -1) ? indexA - indexB : 'fallback'
                      });
                    }

                    // If both present, sort by index
                    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                    // If one present, prioritize it
                    if (indexA !== -1) return -1;
                    if (indexB !== -1) return 1;
                    // Fallback to original admin order
                    return 0;
                  }).map((p, index) => {
                    // CRITICAL: Inject the new display_order so PillarManagementPanel respects it.
                    // If we don't do this, PillarManagementPanel will re-sort based on the original admin display_order.
                    const keyIndex = orgPillarKeys.indexOf(p.id) !== -1 ? orgPillarKeys.indexOf(p.id) : orgPillarKeys.indexOf(String(p.id));
                    
                    // If the pillar is in the saved list, use its position there.
                    // If not, we still need to give it a valid order index higher than the saved ones to keep it at the end.
                    // But since we just sorted them, the array index 'index' is already the correct order.
                    return { 
                      ...p, 
                      display_order: index, 
                      custom_order: index,
                      order_index: index 
                    };
                  });
                  
                  setAvailablePillars(orgOrderedAvailablePillars);
                  
                  console.log('✅ Using organization overrides:', {
                    savedCount: orgPillarKeys.length,
                    matchedCount: orgVisiblePillars.length,
                    pillarNames: orgVisiblePillars.map(p => p.name),
                    orgPillarKeys: orgPillarKeys,
                    matchedIds: orgVisiblePillars.map(p => p.id)
                  });
                  
                  if (pendingChanges.hasVisibilityChanges) {
                    console.log('DBG:PILLARS FETCH SKIP', { reason: 'pending-visibility-changes' });
                  } else {
                    setVisiblePillars(orderedOrgVisiblePillars);
                  }
                } else {
                  // No saved settings - fallback to admin defaults (instead of hiding everything)
                  const isStaleInner = fetchContextRef.current.token !== currentToken || fetchContextRef.current.org !== orgSlug || fetchContextRef.current.viewId !== currentView.id;
                  if (isStaleInner) {
                    console.warn('⏭️ Ignoring stale baseline set due to context change');
                    return;
                  }
                  
                  console.log('📋 No organization settings - falling back to admin defaults');
                  if (pendingChanges.hasVisibilityChanges) {
                    console.log('DBG:PILLARS FETCH SKIP', { reason: 'pending-visibility-changes' });
                  } else {
                    // Use admin defaults if no overrides exist
                    setVisiblePillars(adminVisiblePillars);
                  }
                }
              } else {
                console.log('⚠️ Failed to fetch organization settings from database, using local data fallback');
                // Fallback to existing logic
                const localOrgPillarKeys = organizationData?.pillarSettings?.[currentView.code];
                if (localOrgPillarKeys && localOrgPillarKeys.length > 0) {
                  const isStaleInner = fetchContextRef.current.token !== currentToken || fetchContextRef.current.org !== orgSlug || fetchContextRef.current.viewId !== currentView.id;
                  if (isStaleInner) {
                    console.warn('⏭️ Ignoring stale local fallback due to context change');
                    return;
                  }
                  // Support both old format (p.key, p.id) and new format (id_framework_id)
                    const orgVisiblePillars = adminVisiblePillars.filter(p => 
                    localOrgPillarKeys.includes(p.key) || 
                    localOrgPillarKeys.includes(p.id.toString()) ||
                    localOrgPillarKeys.includes(`${p.id}_${p.framework_id}`)
                    );
                    console.log('🏢 Applied organization overrides from local data (fallback):', {
                      adminVisible: adminVisiblePillars.length,
                    orgOverrideKeys: localOrgPillarKeys,
                      finalVisible: orgVisiblePillars.length,
                      finalPillars: orgVisiblePillars.map(p => p.name)
                    });
                    setVisiblePillars(orgVisiblePillars);
                  } else {
                  const isStaleInner = fetchContextRef.current.token !== currentToken || fetchContextRef.current.org !== orgSlug || fetchContextRef.current.viewId !== currentView.id;
                  if (isStaleInner) {
                    console.warn('⏭️ Ignoring stale baseline set due to context change');
                    return;
                  }
                  console.log('📋 No organization settings found in local data - starting with 0 visible pillars');
                  setVisiblePillars([]); // Start with empty array, not all pillars
                  try {
                    console.log('DBG:FLOW PARENT SUMMARY', {
                      viewCode: currentView.code,
                      source: 'local-fallback-baseline',
                      visibleCount: adminVisiblePillars.length
                    });
                  } catch {}
                }
              }
            } catch (error) {
              console.error('❌ Error fetching organization settings from database:', error);
              // Fallback to existing logic
              if (organizationData?.pillarSettings && organizationData.pillarSettings[currentView.code]) {
                const orgPillarKeys = organizationData.pillarSettings[currentView.code];
                if (orgPillarKeys.length > 0) {
                  const isStaleInner = fetchContextRef.current.token !== currentToken || fetchContextRef.current.org !== orgSlug || fetchContextRef.current.viewId !== currentView.id;
                  if (isStaleInner) {
                    console.warn('⏭️ Ignoring stale baseline set due to context change');
                    return;
                  }
                  // Support both old format (p.key, p.id) and new format (id_framework_id)
                  const orgVisiblePillars = adminVisiblePillars.filter(p => 
                    orgPillarKeys.includes(p.key) || 
                    orgPillarKeys.includes(p.id.toString()) ||
                    orgPillarKeys.includes(`${p.id}_${p.framework_id}`)
                  );
                  setVisiblePillars(orgVisiblePillars);
                } else {
                  const isStaleInner = fetchContextRef.current.token !== currentToken || fetchContextRef.current.org !== orgSlug || fetchContextRef.current.viewId !== currentView.id;
                  if (isStaleInner) {
                    console.warn('⏭️ Ignoring stale baseline set due to context change');
                    return;
                  }
                  setVisiblePillars([]); // Start with empty array, not all pillars
                }
              } else {
                const isStaleInner = fetchContextRef.current.token !== currentToken || fetchContextRef.current.org !== orgSlug || fetchContextRef.current.viewId !== currentView.id;
                if (isStaleInner) {
                  console.warn('⏭️ Ignoring stale baseline set due to context change');
                  return;
                }
                setVisiblePillars([]); // Start with empty array, not all pillars
              }
            }
            
          } else {
            console.log('⚠️ No admin pillar configuration found, using fallback');
            await handleFallbackPillarLoading();
          }
        } else {
          console.log('⚠️ Admin pillar configuration API failed, using fallback');
          await handleFallbackPillarLoading();
        }
      } catch (error) {
        console.log('❌ Error loading admin pillar configuration:', error);
        await handleFallbackPillarLoading();
      }
      
    } catch (error) {
      console.error('Error in fetchPillars:', error);
    } finally {
      setLoadingPillars(false);
      pillarFetchInFlightRef.current.delete(inflightKey);
    }
  };

  // Fallback pillar loading when admin configuration is not available
  const handleFallbackPillarLoading = async () => {
    try {
      const response = await fetch(`/api/pillars/hierarchical`);
      if (response.ok) {
        let pillars = await response.json();
        
        // Sort by display_order if available, otherwise by id
        pillars.sort((a, b) => {
          const orderA = a.display_order || a.id || 0;
          const orderB = b.display_order || b.id || 0;
          return orderA - orderB;
        });
        
        // SAFETY CHECK: Validate pillars before setting
        const safePillars = pillars.filter(pillar => {
          if (!pillar || typeof pillar !== 'object') return false;
          if (pillar.$$typeof || pillar._owner || pillar.type || pillar.props || pillar.ref) {
            console.error('❌ React component detected in fallback pillars:', pillar.name || 'Unknown');
            return false;
          }
          return true;
        });
        
        if (safePillars.length !== pillars.length) {
          console.warn('⚠️ Filtered out React components from fallback pillars');
        }
        
        setAvailablePillars(safePillars);
        
        // Apply organization settings if available
        const currentView = currentViewFromPath;
        
        if (currentView && organizationData?.pillarSettings) {
          const orgPillarKeys = organizationData.pillarSettings[currentView.code] || [];
          if (orgPillarKeys.length > 0) {
            // Accept both legacy pillar.key and id_framework_id tokens
            const tokens = orgPillarKeys.map(String);
            const idfwSet = new Set(tokens.filter(t => /^\d+_/.test(t)));
            const keySet = new Set(tokens.filter(t => !/^\d+_/.test(t)));
            const visiblePillarObjects = safePillars.filter(p => {
              const idfw = `${p.id}_${p.framework_id}`;
              return keySet.has(p.key) || idfwSet.has(idfw);
            });
            console.log('DBG:VISIBLE APPLY', {
              orgKeyCount: orgPillarKeys.length,
              matchByKeyCount: keySet.size,
              matchByIdfwCount: idfwSet.size,
              resultCount: visiblePillarObjects.length
            });
            setVisiblePillars(visiblePillarObjects);
          } else {
            // CRITICAL FIX: Use safePillars instead of pillars to prevent React component contamination
            setVisiblePillars(safePillars);
          }
        } else {
          // CRITICAL FIX: Use safePillars instead of pillars to prevent React component contamination
          setVisiblePillars(safePillars);
        }
      }
    } catch (error) {
      console.error('Error in fallback pillar loading:', error);
    }
  };

  // Handle pillar toggle with real-time updates (PostMessage)
  const handlePillarToggle = (pillar) => {
    // In-flight guard to prevent double clicks/races
    window.__toggleInflight = window.__toggleInflight || new Set();
    const idfw = `${pillar.id}_${pillar.framework_id}`;
    const nowTs = Date.now();
    if (window.__lastToggleTs && (nowTs - window.__lastToggleTs < 250) && window.__toggleInflight.has(idfw)) {
      console.log('DBG:TOGGLE SKIP', { idfw, sinceMs: nowTs - window.__lastToggleTs });
      return;
    }
    window.__toggleInflight.add(idfw);
    window.__lastToggleTs = nowTs;
    console.log('DBG:TOGGLE START', { idfw, name: pillar.name, clickTs: nowTs });
    
    // Store the current state before making changes (for reset functionality)
    const currentVisiblePillars = visiblePillars
      .map((p, index) => sanitizePillarData(p, index))
      .filter(Boolean);
    
    setVisiblePillars(prev => {
      const isVisible = prev.some(p => p.id === pillar.id && p.framework_id === pillar.framework_id);
      let newPillars;
      
      if (isVisible) {
        // Remove only the specific pillar instance that was clicked
        const filteredPillars = prev.filter(p => !(p.id === pillar.id && p.framework_id === pillar.framework_id));
        
        // CRITICAL FIX: Preserve admin default order after filtering
        newPillars = filteredPillars.sort((a, b) => {
          // Primary sort: custom_order (admin default order)
          const aCustom = typeof a.custom_order === 'number' ? a.custom_order : Infinity;
          const bCustom = typeof b.custom_order === 'number' ? b.custom_order : Infinity;
          if (aCustom !== bCustom) return aCustom - bCustom;
          
          // Fallback sort: order_index
          const aOrder = typeof a.order_index === 'number' ? a.order_index : Infinity;
          const bOrder = typeof b.order_index === 'number' ? b.order_index : Infinity;
          if (aOrder !== bOrder) return aOrder - bOrder;
          
          // Final fallback: name alphabetical
          return (a.name || '').localeCompare(b.name || '');
        });
        
        console.log('DBG:TOGGLE APPLY', { idfw, action: 'hide', newCount: newPillars.length });
        
        // DEBUG: Track Context pillar position in visiblePillars state
        const contextInVisible = newPillars.find(p => p.name === 'Context');
        const contextPositionInVisible = contextInVisible ? newPillars.findIndex(p => p.name === 'Context') : -1;
        console.log('🎯 VISIBLE PILLARS CONTEXT TRACKING:', {
          contextFound: !!contextInVisible,
          contextPosition: contextPositionInVisible,
          contextIsFirst: contextPositionInVisible === 0,
          firstPillarName: newPillars[0]?.name,
          totalVisible: newPillars.length,
          toggledOff: pillar.name
        });
        
        // Check if this was the last pillar with this key/name
        const remainingWithSameKey = newPillars.filter(p => 
          p.key === pillar.key || p.name === pillar.name
        );
        console.log('TOGGLE: Remaining pillars with same key/name:', {
          pillarKey: pillar.key,
          pillarName: pillar.name,
          pillarId: pillar.id,
          frameworkId: pillar.framework_id,
          remainingCount: remainingWithSameKey.length,
          remainingNames: remainingWithSameKey.map(p => ({id: p.id, name: p.name, framework_id: p.framework_id}))
        });
      } else {
        // Add pillar in correct position based on availablePillars order
        const availableOrder = availablePillars.map(p => p.id);
        const pillarIndex = availableOrder.indexOf(pillar.id);
        
        // Find the correct insertion point to maintain order
        let insertIndex = 0;
        for (let i = 0; i < prev.length; i++) {
          const currentPillarIndex = availableOrder.indexOf(prev[i].id);
          if (currentPillarIndex > pillarIndex) {
            break;
          }
          insertIndex = i + 1;
        }
        
        // Use global sanitization function to prevent React component contamination
        const sanitizedPillar = sanitizePillarData(pillar, insertIndex);
          
        if (!sanitizedPillar) {
          console.error('TOGGLE: Failed to sanitize pillar, skipping add:', pillar.name);
          newPillars = prev;
        } else {
          newPillars = [...prev.slice(0, insertIndex), sanitizedPillar, ...prev.slice(insertIndex)];
          console.log('DBG:TOGGLE APPLY', { idfw, action: 'show', newCount: newPillars.length });
        }
      }
      
      // Send immediate update to iframe for active data handling
      setTimeout(() => {
          const pathView = location.pathname.split('/').pop()?.toLowerCase(); // e.g., "ukr"
          console.log('🔍 [TOGGLE DEBUG] setTimeout executing:', {
            availableViewsCount: availableViews.length,
            pathname: location.pathname,
            searchingFor: pathView
          });
          
          const currentView = currentViewFromPath;
          
          if (currentView) {
          // CRITICAL FIX: Use unique ID_FRAMEWORK_ID identifiers for precise pillar control
          let iframePillarKeys;
          if (isVisible) {
            // Remove only the specific pillar instance that was clicked
            iframePillarKeys = newPillars.map(p => `${p.id}_${p.framework_id}`); // Use ID_FRAMEWORK_ID
            console.log('🔧 [IFRAME UPDATE] Hiding specific pillar instance:', {
              hiddenPillarKey: pillar.key,
              hiddenPillarName: pillar.name,
              hiddenPillarId: pillar.id,
              hiddenFrameworkId: pillar.framework_id,
              hiddenIdentifier: `${pillar.id}_${pillar.framework_id}`,
              iframeIdentifiers: iframePillarKeys,
              removedIdentifierPresent: iframePillarKeys.includes(`${pillar.id}_${pillar.framework_id}`)
            });
          } else {
            // Adding a pillar - use unique identifier
            iframePillarKeys = newPillars.map(p => `${p.id}_${p.framework_id}`); // Use ID_FRAMEWORK_ID
            console.log('🔧 [IFRAME UPDATE] Adding specific pillar instance:', {
              addedPillarKey: pillar.key,
              addedPillarName: pillar.name,
              addedPillarId: pillar.id,
              addedFrameworkId: pillar.framework_id,
              addedIdentifier: `${pillar.id}_${pillar.framework_id}`,
              iframeIdentifiers: iframePillarKeys
            });
          }
          
          // Enhanced: Include active pillar context to help iframe handle selection logic
          // Bump a monotonically increasing sequence for the iframe to ignore out-of-order older messages
          window.__iframeSeq = (window.__iframeSeq || 0) + 1;
          const seq = window.__iframeSeq;
          // Open a short window to ignore fetch-driven resets
          localChangeUntilRef.current = Date.now() + 600;
          sendSettingsUpdateToIframe({
            pillarKeys: iframePillarKeys,
            orderedPillars: newPillars, // ← FIX: Pass the updated pillars directly to avoid stale state
            seq,
            pillarToggle: {
              pillarName: pillar.name,
              pillarId: pillar.id,
              pillarKey: pillar.key,
              nowVisible: !isVisible,
              totalVisible: newPillars.length,
              triggeredByUserAction: true,
              timestamp: Date.now()
            }
          });
          console.log('DBG:TOGGLE SENT', { idfw, seq, totalVisible: newPillars.length });
          // Release in-flight guard shortly after posting the message
          setTimeout(() => { try { window.__toggleInflight.delete(idfw); } catch (_) {} }, 200);
        } else {
          console.warn('🚫 [TOGGLE DEBUG] No currentView found - iframe update skipped');
        }
      }, 50);
      
      // Mark changes as pending for save/reset functionality
      const currentView2 = currentViewFromPath;
      
      if (currentView2) {
        setPendingChanges(prev => {
          const newOriginal = prev.originalVisiblePillars || currentVisiblePillars;
          console.log('🔄 TOGGLE: Setting pending changes:', {
            hadOriginal: !!prev.originalVisiblePillars,
            settingOriginal: !prev.originalVisiblePillars,
            originalCount: newOriginal?.length || 0,
            originalFirstPillar: newOriginal?.[0]?.name || 'NONE',
            currentCount: currentVisiblePillars.length,
            currentFirstPillar: currentVisiblePillars[0]?.name || 'NONE'
          });
          
          return {
          ...prev,
          hasVisibilityChanges: true,
          pendingVisiblePillars: newPillars,
            originalVisiblePillars: newOriginal
          };
        });
        
        // Update local organizationData for immediate UI feedback
        // CRITICAL FIX: Use unique ID_FRAMEWORK_ID identifiers for precise pillar control
        let updatedPillarKeys;
        if (isVisible) {
          // Remove only the specific pillar instance that was clicked
          updatedPillarKeys = newPillars.map(p => `${p.id}_${p.framework_id}`); // Use ID_FRAMEWORK_ID
          console.log('🔧 [PILLAR TOGGLE] Hiding specific pillar instance:', {
            hiddenPillarKey: pillar.key,
            hiddenPillarName: pillar.name,
            hiddenPillarId: pillar.id,
            hiddenFrameworkId: pillar.framework_id,
            hiddenIdentifier: `${pillar.id}_${pillar.framework_id}`,
            finalIdentifiers: updatedPillarKeys,
            removedIdentifierPresent: updatedPillarKeys.includes(`${pillar.id}_${pillar.framework_id}`)
          });
        } else {
          // Adding a pillar - use unique identifier
          updatedPillarKeys = newPillars.map(p => `${p.id}_${p.framework_id}`); // Use ID_FRAMEWORK_ID
          console.log('🔧 [PILLAR TOGGLE] Adding specific pillar instance:', {
            addedPillarKey: pillar.key,
            addedPillarName: pillar.name,
            addedPillarId: pillar.id,
            addedFrameworkId: pillar.framework_id,
            addedIdentifier: `${pillar.id}_${pillar.framework_id}`,
            finalIdentifiers: updatedPillarKeys
          });
        }
        
        setOrganizationData(prevData => ({
          ...prevData,
          pillarSettings: {
            ...prevData?.pillarSettings,
            [currentView2.code]: updatedPillarKeys
          }
        }));
      }
      
      return newPillars;
    });
  };

  // Save pillar visibility changes to organization-specific settings (NOT admin settings)
  const savePillarVisibilityToView = async (currentView, visiblePillars) => {
    try {
      // Create organization-specific pillar settings using unique ID_FRAMEWORK_ID identifiers
      const pillarKeys = visiblePillars.map(p => `${p.id}_${p.framework_id}`);
      
      console.log('💾 Saving organization pillar overrides to database:', {
        view: currentView.code,
        orgSlug: orgSlug,
        pillarKeys: pillarKeys,
        pillarNames: visiblePillars.map(p => p.name)
      });

      // Save to the NEW organization settings endpoint (consistent with App.jsx)
      const response = await fetch(`/api/organization-settings/pillars/${currentView.code}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Test-Org': orgSlug, // Organization-specific header
        },
        credentials: 'include',
        body: JSON.stringify({ pillarIds: pillarKeys }),
      });

      if (response.ok) {
        console.log('✅ Organization pillar overrides saved to database (admin settings unchanged)');
        
        // Update local organizationData to reflect the change
        setOrganizationData(prev => ({
          ...prev,
          pillarSettings: {
            ...prev?.pillarSettings,
            [currentView.code]: pillarKeys
          }
        }));
      } else {
        console.warn('⚠️ Failed to save organization pillar overrides to database');
        console.error('Save response:', response.status, response.statusText);
              }
            } catch (error) {
      console.error('❌ Error saving organization pillar overrides to database:', error);
    }
  };

  // Handle pillar order change with persistence
  const handlePillarOrderChange = async (newPillarOrder) => {
    console.log('🚀 [DRAG ORDER] handlePillarOrderChange called with:', {
      newPillarOrderLength: newPillarOrder?.length || 0,
      newPillarOrderType: typeof newPillarOrder,
      isArray: Array.isArray(newPillarOrder),
      firstFewNames: newPillarOrder?.slice(0, 5).map(p => p?.name) || 'N/A',
      timestamp: new Date().toISOString()
    });
    
    try {
      // SAFETY CHECK: Validate and sanitize newPillarOrder before using it
      if (!Array.isArray(newPillarOrder)) {
        console.error('❌ handlePillarOrderChange: newPillarOrder is not an array:', typeof newPillarOrder);
        return;
      }
      
      const safePillarOrder = newPillarOrder.filter(pillar => {
        if (!pillar || typeof pillar !== 'object') {
          console.warn('❌ handlePillarOrderChange: Filtering out non-object pillar:', typeof pillar);
          return false;
        }
        if (pillar.$$typeof || pillar._owner || pillar.type || pillar.props || pillar.ref) {
          console.error('❌ handlePillarOrderChange: React component detected in newPillarOrder:', {
            name: pillar.name || 'Unknown',
            hasReactProps: true
          });
          return false;
        }
        return true;
      });
      
      if (safePillarOrder.length !== newPillarOrder.length) {
        console.warn('⚠️ handlePillarOrderChange: Filtered out', newPillarOrder.length - safePillarOrder.length, 'contaminated pillars');
      }
      
      const currentView = currentViewFromPath;
      
      if (!currentView) {
        console.warn('No current view found for pillar order update', { availableViewsCount: availableViews.length });
        return;
      }

      // CRITICAL FIX: Update custom_order values to match new positions
      // This ensures the Content Visibility panel sorts correctly after drag-and-drop
      const reorderedPillars = safePillarOrder.map((pillar, index) => ({
        ...pillar,
        custom_order: index // Update custom_order to match new position
      }));
      
      // Update the available pillars state with safe new order
      setAvailablePillars(reorderedPillars);
      
      // CRITICAL FIX: Also update visiblePillars to reflect new order in the right panel
      // Create completely new array with new object references to force React re-render
      const visiblePillarIds = new Set(visiblePillars.map(p => p.id));
      const newOrderedVisiblePillars = reorderedPillars
        .filter(p => visiblePillarIds.has(p.id))
        .map(p => ({ ...p })); // Create new object references to force re-render
      
      console.log('🔄 [DRAG ORDER] Updating visiblePillars state:', {
        oldOrder: visiblePillars.slice(0, 5).map(p => p.name),
        newOrder: newOrderedVisiblePillars.slice(0, 5).map(p => p.name),
        orderChanged: JSON.stringify(visiblePillars.map(p => p.id)) !== JSON.stringify(newOrderedVisiblePillars.map(p => p.id))
      });
      
      setVisiblePillars(newOrderedVisiblePillars);

      // First, get current pillar visibility data to preserve visibility settings
      const currentVisibilityResponse = await fetch(`/api/views/${currentView.id}/pillars/visibility`, {
        credentials: 'include'
      });
      
      let currentVisibilityData = [];
      if (currentVisibilityResponse.ok) {
        currentVisibilityData = await currentVisibilityResponse.json();
      }

      // Create a map of current visibility settings
      const visibilityMap = new Map();
      currentVisibilityData.forEach(item => {
        if (item.pillar_id) {
          visibilityMap.set(item.pillar_id, item.is_visible !== false);
        }
      });

      // Prepare updated pillar visibility data with new order
      const updatedVisibilityData = newPillarOrder.map((pillar, index) => ({
        pillar_id: pillar.id,
        order_index: index,
        is_visible: visibilityMap.get(pillar.id) ?? true // Default to visible if not found
      }));

      // Mark order changes as pending but still update the preview immediately
      setPendingChanges(prev => {
        const newOriginal = prev.originalVisiblePillars || visiblePillars;
        console.log('🔄 DRAG: Setting pending changes:', {
          hadOriginal: !!prev.originalVisiblePillars,
          settingOriginal: !prev.originalVisiblePillars,
          originalCount: newOriginal?.length || 0,
          originalFirstPillar: newOriginal?.[0]?.name || 'NONE',
          currentCount: visiblePillars.length,
          currentFirstPillar: visiblePillars[0]?.name || 'NONE'
        });
        
        return {
        ...prev,
        hasOrderChanges: true,
        pendingPillarOrder: safePillarOrder,
          originalVisiblePillars: newOriginal // Save original order for reset
        };
      });
      
      console.log('📝 Pillar order changes marked as pending - click Save to persist');
      
      // CRITICAL FIX: Use the NEW order (safePillarOrder) for iframe, not the old order (visiblePillars)
      // Filter safePillarOrder to only include visible pillars, maintaining the new order
      // Note: visiblePillarIds and newOrderedVisiblePillars already declared above for setVisiblePillars
      const newPillarKeys = newOrderedVisiblePillars.map(p => `${p.id}_${p.framework_id}`);
      
      console.log('📨 [DRAG ORDER] Sending order update to iframe:', {
        newPillarKeys: newPillarKeys,
        newOrderIds: safePillarOrder.map(p => p.id),
        newOrderNames: safePillarOrder.map(p => p.name),
        totalPillars: newPillarKeys.length,
        orderChanged: JSON.stringify(availablePillars.map(p => p.name)) !== JSON.stringify(safePillarOrder.map(p => p.name))
      });
      
      // 🎯 FOCUSED DEBUG: Show exact order sequences for comparison
      const beforeSequence = availablePillars.map(p => p.name).slice(0, 10);
      const afterSequence = safePillarOrder.map(p => p.name).slice(0, 10);
      console.log('🎯 [DRAG ORDER] BEFORE sequence (first 10):', beforeSequence.join(' → '));
      console.log('🎯 [DRAG ORDER] AFTER sequence (first 10):', afterSequence.join(' → '));
      console.log('🎯 [DRAG ORDER] ORDER CHANGED:', beforeSequence.join('|') !== afterSequence.join('|'));
      
      // CRITICAL FIX: Pass the newly ordered pillars directly to override the stale visiblePillars
      sendSettingsUpdateToIframe({
        pillarKeys: newPillarKeys,
        orderedPillars: newOrderedVisiblePillars, // ← NEW: Pass the correctly ordered pillars
        orderUpdate: {
          newOrder: safePillarOrder.map(p => p.id),
          totalPillars: newPillarKeys.length
        }
      });
      
      console.log('✅ [DRAG ORDER] Order update sent to iframe successfully');
    } catch (error) {
      console.error('Error saving pillar order:', error);
    }
  };

  // NEW: Save pending changes to database
  const savePendingChanges = async () => {
    const currentView = currentViewFromPath;
    
    if (!currentView) {
      console.error('No current view found for saving pending changes', { availableViewsCount: availableViews.length });
      return;
    }
    
    try {
      console.log('💾 Saving pending changes to database...', {
        viewCode: currentView.code,
        hasVisibilityChanges: pendingChanges.hasVisibilityChanges,
        hasOrderChanges: pendingChanges.hasOrderChanges,
        visiblePillarsCount: visiblePillars.length
      });
      
      const visiblePillarIdsSet = new Set(visiblePillars.map(p => p.id));

      let pillarIds = visiblePillars.map(p => p.id);
      if (
        pendingChanges.hasOrderChanges &&
        Array.isArray(pendingChanges.pendingPillarOrder) &&
        pendingChanges.pendingPillarOrder.length > 0
      ) {
        const orderedVisibleIds = pendingChanges.pendingPillarOrder
          .filter(p => p && visiblePillarIdsSet.has(p.id))
          .map(p => p.id);

        if (orderedVisibleIds.length > 0) {
          pillarIds = orderedVisibleIds;
        }
      }
      
      console.log('💾 Saving pillar visibility to organization settings:', {
        orgSlug,
        viewCode: currentView.code,
        pillarCount: pillarIds.length,
        pillarNames: visiblePillars.map(p => p.name),
        pillarIds
      });

      // CRITICAL FIX: Save to organization-specific endpoint, not admin view endpoint
      const response = await fetch(`/api/organizations/${orgSlug}/settings/${currentView.code}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pillars: pillarIds }),
      });
      
      if (response.ok) {
        console.log('✅ Pillar visibility saved to database');
      } else {
        throw new Error(`Failed to save pillar visibility: ${response.status}`);
      }
      
      // Clear pending changes
      setPendingChanges({
        hasVisibilityChanges: false,
        hasOrderChanges: false,
        pendingVisiblePillars: null,
        pendingPillarOrder: null,
        originalVisiblePillars: null
      });
      
      console.log('✅ All pending changes saved successfully');
      
    } catch (error) {
      console.error('❌ Error saving pending changes:', error);
    }
  };

  // Lightweight function to refresh only organization pillar settings (not full org data)
  const refreshOrganizationPillarSettings = useCallback(async (orgSlug, viewCode) => {
    try {
      console.log(`🔄 Refreshing organization pillar settings for ${viewCode}...`);
      
      const response = await fetch(`/api/organizations/${orgSlug}/settings/${viewCode}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log(`✅ Refreshed pillar settings for ${viewCode}:`, { 
          pillarCount: data.pillars?.length || 0 
        });
        
        // Update just the pillar settings for this view in organizationData
        setOrganizationData(prev => ({
          ...prev,
          pillarSettings: {
            ...prev?.pillarSettings,
            [viewCode]: data.pillars || []
          }
        }));
        
        return data.pillars || [];
      } else {
        console.log(`⚠️ No organization settings found for ${viewCode}, using empty array`);
        return [];
      }
    } catch (error) {
      console.error(`❌ Error refreshing organization settings for ${viewCode}:`, error);
      return [];
    }
  }, [orgSlug]);









  // Track if we've already fetched pillars for current settings panel session and org data state
  const [hasFetchedForCurrentSession, setHasFetchedForCurrentSession] = useState(false);
  const [lastOrgSettingsState, setLastOrgSettingsState] = useState(null);

  // Reset session flag when settings panel opens/closes or when organization settings change
  useEffect(() => {
    if (settingsPanelOpen) {
      setHasFetchedForCurrentSession(false);
    }
  }, [settingsPanelOpen]);

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'editor': return 'warning'; 
      case 'member': return 'primary';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'invited': return 'warning';
      case 'inactive': return 'default';
      default: return 'default';
    }
  };

  // Load users when organization data is available


  // Reset to admin defaults (clear organization overrides)
  const resetToAdminDefaults = async () => {
    console.log('🔄 Resetting to admin defaults (clearing organization overrides)...');
    
    const currentView = currentViewFromPath;
    
    if (currentView) {
      try {
        // DELETE the organization overrides to reset to admin defaults
        const response = await fetch(`/api/organizations/${orgSlug}/settings/${currentView.code.toUpperCase()}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        
        if (response.ok) {
          console.log('✅ Organization overrides deleted - reset to admin defaults');
          
          // Update local state to reflect no organization overrides (null, not empty array)
          setOrganizationData(prev => ({
            ...prev,
            pillarSettings: {
              ...prev?.pillarSettings,
              [currentView.code.toUpperCase()]: null
            }
          }));
          
          // Reload pillar data to get fresh admin defaults
          fetchPillars(currentView.id);
          
        } else {
          console.log('⚠️ No organization settings to delete - already at admin defaults');
          // Just reload pillar data 
          fetchPillars(currentView.id);
        }
        
      } catch (error) {
        console.error('❌ Error resetting to admin defaults:', error);
        // Fallback: just reload pillar data
        fetchPillars(currentView.id);
      }
    }
  };

  // Discard pending changes and restore original state 
  const discardPendingChanges = async () => {
    console.log('🔄 RESET: Discarding pending changes');
    
    // Store original state before clearing pending changes
    const originalVisiblePillars = pendingChanges.originalVisiblePillars;
    const hasOrderChanges = pendingChanges.hasOrderChanges;
    const hasVisibilityChanges = pendingChanges.hasVisibilityChanges;
    
    console.log('🔄 RESET: State to restore:', {
      hasOrderChanges,
      hasVisibilityChanges,
      originalVisiblePillarsCount: originalVisiblePillars?.length || 0,
      availablePillarsCount: availablePillars.length,
      currentVisiblePillarsCount: visiblePillars.length,
      originalExists: !!originalVisiblePillars,
      originalFirstPillar: originalVisiblePillars?.[0]?.name || 'NONE',
      currentFirstPillar: visiblePillars[0]?.name || 'NONE'
    });
    
    // Determine what to restore based on what changes occurred
    let pillarsToRestore;
    
    if (hasVisibilityChanges || hasOrderChanges) {
      if (originalVisiblePillars) {
        console.log('🔄 RESET: Restoring to original state before changes:', {
          originalCount: originalVisiblePillars.length,
          currentCount: visiblePillars.length,
          hasVisibilityChanges,
          hasOrderChanges
        });
        pillarsToRestore = originalVisiblePillars;
        
        // Update the current visible pillars state to the original
        setVisiblePillars(originalVisiblePillars);
        
        console.log('🔄 RESET: Original order restored:', {
          beforeRestoreSequence: visiblePillars.map(p => p.name).slice(0, 10).join(' → '),
          afterRestoreSequence: originalVisiblePillars.map(p => p.name).slice(0, 10).join(' → '),
          orderChanged: JSON.stringify(visiblePillars.map(p => p.name)) !== JSON.stringify(originalVisiblePillars.map(p => p.name))
        });
      } else {
        console.log('🔄 RESET: No original state found, falling back to admin default (all available pillars):', {
          availableCount: availablePillars.length,
          currentCount: visiblePillars.length,
          reason: 'Changes were previously saved, so restoring to admin default'
        });
        
        // Fall back to showing all available pillars (admin default)
        pillarsToRestore = availablePillars;
        
        // Update the current visible pillars state to show all available
        setVisiblePillars(availablePillars);
        
        console.log('🔄 RESET: Restored to admin default:', {
          beforeRestoreSequence: visiblePillars.map(p => p.name).slice(0, 10).join(' → '),
          afterRestoreSequence: availablePillars.map(p => p.name).slice(0, 10).join(' → '),
          restoredCount: availablePillars.length
        });
      }
    } else {
      console.log('🔄 RESET: No changes to restore');
      pillarsToRestore = visiblePillars;
    }
    
    // Clear pending changes FIRST for immediate UI feedback
    setPendingChanges({
      hasVisibilityChanges: false,
      hasOrderChanges: false,
      pendingVisiblePillars: null,
      pendingPillarOrder: null,
      originalVisiblePillars: null
    });

    // Update iframe with restored pillar list and order (reduced delay)
    setTimeout(() => {
      const currentView = currentViewFromPath;
      
      if (currentView && pillarsToRestore) {
        // Use the correct id_framework_id format for pillar keys
        const restoredPillarKeys = pillarsToRestore.map(p => `${p.id}_${p.framework_id}`);
        
        console.log('🔄 RESET: Sending restored state to iframe');
        
        sendSettingsUpdateToIframe({
          pillarKeys: restoredPillarKeys,
          orderedPillars: pillarsToRestore, // Send the correctly ordered pillars
          resetAction: {
            type: hasOrderChanges ? 'PILLAR_ORDER_RESET' : 'PILLAR_VISIBILITY_RESET',
            restoredCount: pillarsToRestore.length,
            orderRestored: hasOrderChanges,
            visibilityRestored: hasVisibilityChanges,
            timestamp: Date.now()
          }
        });
        
        console.log('✅ RESET: Iframe updated with restored pillars and order');
      }
    }, 50); // Reduced from 100ms to 50ms for faster feedback
    
    // Also restore organization data to remove local UI feedback changes
    const currentView = currentViewFromPath;
    
    if (currentView && pillarsToRestore) {
      console.log('🔄 RESET: Updating organization data with restored pillars');
      setOrganizationData(prevData => ({
        ...prevData,
        pillarSettings: {
          ...prevData?.pillarSettings,
          [currentView.code]: pillarsToRestore.map(p => `${p.id}_${p.framework_id}`)
        }
      }));
    }
    
    console.log('✅ RESET: Pending changes discarded and original state restored');
  };

  // Helper to check if there are any pending changes
  const hasPendingChanges = pendingChanges.hasVisibilityChanges || pendingChanges.hasOrderChanges;

  // GLOBAL: Deep sanitization function to ensure no React components leak through
    const sanitizePillarData = (pillar, index) => {
      try {
        // Multiple layers of validation
        if (!pillar || typeof pillar !== 'object') return null;
        if (pillar.$$typeof || pillar._owner || pillar.type || pillar.props) return null; // React component/element
        if (typeof pillar.id !== 'number' && !pillar.id) return null;
        if (typeof pillar.name !== 'string' && !pillar.name) return null;
        
        // Create completely new object with only the needed properties
      const sanitizedPillar = {
          key: String(pillar.key || pillar.id || `pillar_${index}`),
          id: Number(pillar.id) || index,
          name: String(pillar.name || `Pillar ${index}`),
        order: Number(index),
        parent_pillar_id: pillar.parent_pillar_id,
        order_index: pillar.order_index,
        analysis_behavior_flag: pillar.analysis_behavior_flag,
        framework_id: pillar.framework_id,
        color: pillar.color
      };

      // CRITICAL: Recursively sanitize subPillars if they exist
      if (Array.isArray(pillar.subPillars)) {
        sanitizedPillar.subPillars = pillar.subPillars
          .map((subPillar, subIndex) => sanitizePillarData(subPillar, `${index}-${subIndex}`))
          .filter(Boolean); // Remove any nulls from sub-pillar sanitization
      } else {
        sanitizedPillar.subPillars = [];
      }

      return sanitizedPillar;
      } catch (error) {
      console.error('SANITIZE: Error sanitizing pillar data:', error, pillar);
        return {
          key: `fallback_${index}`,
          id: index,
          name: `Fallback Pillar ${index}`,
        order: index,
        subPillars: []
        };
      }
    };

  // Helper function to send settings updates via postMessage (no URL generation needed)
  const sendSettingsUpdateToIframe = (updates) => {
    const iframeAvailable = iframeRef.current?.contentWindow;
    if (!iframeAvailable) {
      console.warn('IFRAME: Cannot send settings update - iframe not available. Caching for IFRAME_READY.');
    }
    
    // CRITICAL FIX: Prevent sending stale settings when org is switching
    // Check if organizationData.slug matches current orgSlug - if not, data is stale
    const orgDataSlug = organizationData?.slug || organizationData?.organization?.slug;
    if (orgDataSlug && orgDataSlug !== orgSlug) {
      console.warn('🚫 [IFRAME] Skipping settings update - organizationData is stale:', {
        orgDataSlug,
        currentOrgSlug: orgSlug
      });
      return;
    }
    
    // CRITICAL FIX: If this is a theme-only update, don't send pillar data
    // This prevents branding saves from overriding pillar settings
    if (updates.themeUpdate && updates.branding) {
      console.log('🎨 [THEME ONLY] Sending theme update without pillar data');
      const themeMessage = {
        type: 'UPDATE_SETTINGS',
        branding: updates.branding,
        themeUpdate: true
      };
      if (iframeAvailable) {
        iframeRef.current.contentWindow.postMessage(themeMessage, '*');
      }
      return;
    }
    
    // Guard against sending updates when pillars are not yet loaded
    if (!updates.expandedPillars && !updates.orderedPillars && (!visiblePillars || visiblePillars.length === 0) && !updates.pillarKeys) {
      console.log('🚫 IFRAME: Skipping update - no pillar data available yet');
      return;
    }
    
    // CRITICAL FIX: If only expandedPillars is being updated, don't send pillar data
    // This prevents sending empty pillars when just expanding/collapsing
    if (updates.expandedPillars && Object.keys(updates).length === 1) {
      console.log('DBG:EXPAND ONLY - Not sending pillar update, only expansion state');
      return;
    }
    
    // Get current view to preserve pillar settings when not explicitly updating them
    const currentView = currentViewFromPath;
    
    // ULTRA-SAFE: Filter out any React components from visiblePillars BEFORE processing
    const safeVisiblePillars = visiblePillars.filter(pillar => {
      if (!pillar || typeof pillar !== 'object') return false;
      if (pillar.$$typeof || pillar._owner || pillar.type || pillar.props) {
        console.warn('❌ Filtering out React component from visiblePillars:', pillar);
        return false;
      }
      return true;
    });
    
    // Preserve current pillar settings if not explicitly updating them
    let currentPillarData = [];
    
    // CRITICAL FIX: Use orderedPillars from drag operations if provided
    if (updates.orderedPillars) {
      console.log('🔧 [IFRAME] Using orderedPillars from drag operation:', updates.orderedPillars.length);
      currentPillarData = updates.orderedPillars
        .map((pillar, index) => sanitizePillarData(pillar, index))
        .filter(p => p !== null);
    } else if (!updates.pillarKeys) {
      // Include BOTH keys AND order information for the iframe
      // ENHANCED: Use deep sanitization to ensure no React components
      currentPillarData = safeVisiblePillars
        .map(sanitizePillarData)
        .filter(p => p !== null);
    } else {
      // If pillarKeys are provided, convert them to ordered data
      // CRITICAL FIX: Only include pillars that are explicitly in updates.pillarKeys (now ID_FRAMEWORK_ID)
      // This ensures we don't include stale pillars that were toggled off
      currentPillarData = updates.pillarKeys.map((identifier, index) => { // 'identifier' is now 'ID_FRAMEWORK_ID'
        const pillar = safeVisiblePillars.find(p => {
          if (!p || typeof p !== 'object' || p.$$typeof || p._owner || p.type) return false;
          // Match by the unique identifier (ID_FRAMEWORK_ID)
          return `${p.id}_${p.framework_id}` === identifier;
        });
        
        // Only return pillar data if the pillar was found AND its identifier is in updates.pillarKeys
        if (pillar) {
          return sanitizePillarData(pillar, index);
        } else {
          // If pillar not found in current state, don't include it
          const [id, framework_id] = identifier.split('_');
          // Enhanced debug for skipped pillar identifiers
          const specificPillar = availablePillars.find(p => p.id.toString() === id && p.framework_id.toString() === framework_id);
          console.warn(`🔧 [IFRAME] Skipping pillar identifier not found in current state: ${identifier}`, {
              availablePillarsCount: availablePillars.length,
              targetId: id,
              targetFrameworkId: framework_id,
              specificPillarFound: !!specificPillar,
              specificPillarData: specificPillar || 'NOT_FOUND',
              allAvailablePillarIds: availablePillars.map(p => `${p.id}_${p.framework_id}`),
              is_4_1_specifically: identifier === '4_1',
              infoComm4_1_in_available: !!availablePillars.find(p => p.id === 4 && p.framework_id === 1)
          });
          
          // Special handling for pillar 4_1 debugging
          if (identifier === '4_1') {
              console.error('🚨 [CRITICAL] Pillar 4_1 (Information and Communication) being skipped!', {
                  availablePillarsWithId4: availablePillars.filter(p => p.id === 4),
                  availablePillarsWithFramework1: availablePillars.filter(p => p.framework_id === 1),
                  exactMatch: availablePillars.find(p => p.id === 4 && p.framework_id === 1)
              });
          }
          return null;
        }
      }).filter(p => p !== null);
      
      console.log('🔧 [IFRAME] Using explicit pillarKeys (ID_FRAMEWORK_ID):', {
        providedIdentifiers: updates.pillarKeys,
        foundPillars: currentPillarData.length,
        pillarNames: currentPillarData.map(p => p.name),
        excludedHumanitarianAccess: !updates.pillarKeys.some(id => id.includes('humanitarian_access')) // Check if any HA identifier is excluded
      });
    }
    
    // Use the global sanitization function for consistent data cleaning
    const filteredCount = visiblePillars.length - safeVisiblePillars.length;
    if (filteredCount > 0) {
      console.log('IFRAME: Filtered out', filteredCount, 'React components from pillar data');
    }
    
    // Apply deep sanitization to current pillar data
    const cleanPillarData = currentPillarData
      .map((p, index) => sanitizePillarData(p, index))
      .filter(Boolean);

    // Expand parents with their sub-pillars to match dashboard expectation
    // This preserves the visible order while ensuring children are present
    const byId = new Map(cleanPillarData.map(p => [p.id, p]));
    const visibleIds = new Set(cleanPillarData.map(p => p.id));
    
    // Children expansion: If parent pillar is visible, include all its children
    // CRITICAL FIX: Only expand children when NOT using explicit orderedPillars from toggle operations
    const shouldExpandChildren = !updates.orderedPillars;
    
    if (updates.pillarToggle) {
      console.log('🔧 Pillar toggle - child expansion decision:', {
        useOrderedPillars: !!updates.orderedPillars,
        toggledPillar: updates.pillarToggle.pillarName,
        shouldExpandChildren,
        reason: shouldExpandChildren ? 'No explicit pillar list provided' : 'Using explicit pillar list from toggle'
      });
    }
    
    let expandedWithChildren = [];
    
    if (shouldExpandChildren) {
      // Traditional expansion - add children for visible parents
      const availableByParent = new Map();
      (availablePillars || []).forEach(p => {
        if (p.parent_pillar_id) {
          const list = availableByParent.get(p.parent_pillar_id) || [];
          list.push(p);
          availableByParent.set(p.parent_pillar_id, list);
        }
      });
      
      cleanPillarData.forEach(p => {
        expandedWithChildren.push(p);
        if (!p.parent_pillar_id) {
          const kids = availableByParent.get(p.id) || [];
          if (kids.length > 0) {
            const sanitizedKids = kids
              .map((k, idx) => sanitizePillarData(k, idx))
              .filter(Boolean)
              .filter(k => !visibleIds.has(k.id)); // avoid duplicates with parents
            sanitizedKids.sort((a, b) => {
              const aCustom = typeof a.custom_order === 'number' ? a.custom_order : Infinity;
              const bCustom = typeof b.custom_order === 'number' ? b.custom_order : Infinity;
              if (aCustom !== bCustom) return aCustom - bCustom;
              const aOrder = typeof a.order_index === 'number' ? a.order_index : Infinity;
              const bOrder = typeof b.order_index === 'number' ? b.order_index : Infinity;
              if (aOrder !== bOrder) return aOrder - bOrder;
              return (a.name || '').localeCompare(b.name || '');
            });
            expandedWithChildren.push(...sanitizedKids);
          }
        }
      });
    } else {
      // Use exact pillar list from toggle operation - no expansion
      expandedWithChildren = [...cleanPillarData];
      console.log('🔧 Using exact pillar list from toggle (no child expansion):', {
        originalCount: cleanPillarData.length,
        finalCount: expandedWithChildren.length
      });
    }

    // Final validation - ensure key/name present
    const preValidatedData = expandedWithChildren.filter(p => p && p.key && p.name);
    
    // CRITICAL FIX: Preserve drag operation order, otherwise use admin default order
    // Don't sort if this is a drag operation with explicit orderedPillars
    const finalValidData = updates.orderedPillars ? 
      preValidatedData : // Preserve the drag order
      preValidatedData.sort((a, b) => {
        // Primary sort: custom_order (admin default order)
        const aCustom = typeof a.custom_order === 'number' ? a.custom_order : Infinity;
        const bCustom = typeof b.custom_order === 'number' ? b.custom_order : Infinity;
        if (aCustom !== bCustom) return aCustom - bCustom;
        
        // Fallback sort: order_index
        const aOrder = typeof a.order_index === 'number' ? a.order_index : Infinity;
        const bOrder = typeof b.order_index === 'number' ? b.order_index : Infinity;
        if (aOrder !== bOrder) return aOrder - bOrder;
        
        // Final fallback: name alphabetical
        return (a.name || '').localeCompare(b.name || '');
      });
    
    // 🚨 CRITICAL DEBUG: Inspect finalValidData before mapping to pillars array
    const humanitarianAccess5_1 = finalValidData.find(p => p.id === 5 && p.framework_id === 1);
    const infoComm4_1 = finalValidData.find(p => p.id === 4 && p.framework_id === 1);
    console.log('🚨 [CRITICAL DEBUG] finalValidData before mapping to pillars array:', {
      totalCount: finalValidData.length,
      humanitarianAccess5_1_present: !!humanitarianAccess5_1,
      humanitarianAccess5_1_full: humanitarianAccess5_1 || 'NOT_FOUND',
      infoComm4_1_present: !!infoComm4_1,
      infoComm4_1_full: infoComm4_1 || 'NOT_FOUND',
      allPillarDetails: finalValidData.map(p => ({
        id: p.id,
        framework_id: p.framework_id,
        name: p.name,
        key: p.key,
        custom_order: p.custom_order,
        order_index: p.order_index,
        id_framework_combo: `${p.id}_${p.framework_id}`
      })),
      pillarIds_5_and_4: finalValidData.filter(p => p.id === 5 || p.id === 4),
      frameworkIds_1: finalValidData.filter(p => p.framework_id === 1),
      exact_5_1_check: finalValidData.filter(p => p.id === 5 && p.framework_id === 1),
      exact_4_1_check: finalValidData.filter(p => p.id === 4 && p.framework_id === 1),
      orderPreservation: {
        firstPillar: finalValidData[0] ? {
          name: finalValidData[0].name,
          id: finalValidData[0].id,
          custom_order: finalValidData[0].custom_order,
          order_index: finalValidData[0].order_index
        } : 'NONE',
        contextPillar: finalValidData.find(p => p.name === 'Context') ? {
          name: 'Context',
          position: finalValidData.findIndex(p => p.name === 'Context'),
          custom_order: finalValidData.find(p => p.name === 'Context').custom_order,
          order_index: finalValidData.find(p => p.name === 'Context').order_index
        } : 'NOT_FOUND'
      }
    });
    
    if (finalValidData.length !== cleanPillarData.length) {
      console.log('IFRAME: Removed', cleanPillarData.length - finalValidData.length, 'invalid pillars after sanitization');
    }
    
    // RESET FIX: For reset operations, send pillar objects to iframe, not just identifiers
    const isResetOperation = updates.resetOperation || updates.isResetOperation;
    
    // Destructure title fields out of branding — title is managed separately via URL param
    // and direct postMessage (handleSettingChange, resetAllChanges, theme updates).
    // Including it here would override the iframe's current title on every pillar update.
    const { headerTitle: _ht, headerSubtitle: _hs, ...brandingWithoutTitle } = organizationData?.branding || {};
    
    const settingsUpdate = {
      type: 'UPDATE_SETTINGS',
      settings: {
        branding: {
          // Include branding WITHOUT title fields to avoid overriding iframe's current title
          ...brandingWithoutTitle,
          // THEME FIX: Preserve all branding properties with proper fallbacks
          primaryColor: organizationData?.branding?.primaryColor || '#005F94',
          secondaryColor: organizationData?.branding?.secondaryColor || '#006D87',
          ...(organizationData?.branding?.fontFamily ? { fontFamily: organizationData.branding.fontFamily } : {}),
          headerStyle: organizationData?.branding?.headerStyle || 'full',
          showStatusSchedule: organizationData?.branding?.showStatusSchedule !== false,
          showDisclaimer: organizationData?.branding?.showDisclaimer !== false,
          orgSlug: orgSlug // CRITICAL FIX: Include orgSlug for organization change detection
        },
        organization: {
          slug: orgSlug, // CRITICAL FIX: Include organization slug for change detection
          name: organizationData?.name
        },
        // For reset operations, send full pillar objects; otherwise send identifiers
        pillars: isResetOperation ? finalValidData : finalValidData.map(p => `${p.id}_${p.framework_id}`),
        orderedPillars: finalValidData, // Clean pillar data with order information
        visiblePillars: finalValidData, // Add visible pillars data
        availablePillars: availablePillars, // Add available pillars data  
        expandedPillars: expandedPillars, // Add expansion state
        pillarColors: organizationData?.pillarColors || {}, // Add pillar colors
        ...updates
      }
    };
    
    const dbgPreviewId = (window.__orgPreviewId || 'unknown');
    
    // SPECIFIC DEBUG: Track Context pillar position
    const contextPillar = finalValidData.find(p => p.name === 'Context');
    const contextPosition = contextPillar ? finalValidData.findIndex(p => p.name === 'Context') : -1;
    console.log('🎯 CONTEXT PILLAR TRACKING:', {
      contextFound: !!contextPillar,
      contextPosition: contextPosition,
      contextIsFirst: contextPosition === 0,
      firstPillarName: finalValidData[0]?.name,
      firstPillarId: finalValidData[0]?.id,
      totalPillars: finalValidData.length,
      isToggleOperation: !!updates.pillarToggle,
      toggledPillar: updates.pillarToggle?.pillarName
    });
    
    console.log('DBG:PERSIST→IFRAME', {
      previewId: dbgPreviewId,
      orgSlug,
      viewCode: (currentView?.code || '').toUpperCase(),
      pillarsCount: finalValidData.length,
      pillars: finalValidData.map(p => `${p.id}_${p.framework_id}`),
      humanitarianAccessIncluded: finalValidData.some(p => p.key === 'humanitarian_access'),
      pillarToggle: updates.pillarToggle ? {
        name: updates.pillarToggle.pillarName,
        id: updates.pillarToggle.pillarId,
        nowVisible: updates.pillarToggle.nowVisible
      } : null,
      orderUpdateCount: updates?.orderUpdate?.totalPillars || null,
      resetAction: updates?.resetAction?.type || null
    });
    
    try {
      const dbgPreviewId = (window.__orgPreviewId || Math.random().toString(36).slice(2));
      window.__orgPreviewId = dbgPreviewId;
      // Attach sequence for ordering in iframe
      window.__iframeSeq = (window.__iframeSeq || 0) + 1;
      settingsUpdate.settings.seq = window.__iframeSeq;
      settingsUpdate.previewId = dbgPreviewId;

      // Cache full payload for IFRAME_READY resend (even if iframe not ready yet)
      lastSettingsUpdateRef.current = settingsUpdate;
      
      console.log('📨 Sending', settingsUpdate.type, 'to iframe:', settingsUpdate.settings.pillars?.length || 0, 'pillars');
      
      if (iframeAvailable) {
        iframeRef.current.contentWindow.postMessage(settingsUpdate, '*');
        console.log('✅ Settings update sent successfully');
      } else {
        console.log('⏳ Settings cached for IFRAME_READY resend');
      }
    } catch (error) {
      console.error('❌ Failed to send settings update:', error);
    }
  };

  // One-time initial order handoff to iframe to prevent KPI misorder on first paint
  useEffect(() => {
    if (initialOrderSentRef.current) return;
    if (!Array.isArray(availablePillars) || availablePillars.length === 0) return;
    if (!Array.isArray(visiblePillars) || visiblePillars.length === 0) return;
    
    // RACE CONDITION FIX: Wait for organization-specific settings to be loaded
    // This prevents sending admin baseline (55) before org settings (53) are applied
    if (orgSlug !== 'global' && !organizationData?.pillarSettings) {
      console.log('🚫 [ORG RACE FIX] Waiting for organization settings before sending initial pillars:', {
        orgSlug,
        hasOrgData: !!organizationData,
        hasPillarSettings: !!organizationData?.pillarSettings,
        availablePillarCount: availablePillars.length,
        visiblePillarCount: visiblePillars.length
      });
      return;
    }
    
    // ENHANCED RACE CONDITION FIX: Ensure pillar counts match expected org-specific values
    // Send to iframe regardless of count - strict counting causes blocking when pillars change
    // The iframe handles its own filtering based on the IDs we send
    try {
      const visibleSet = new Set(visiblePillars.map(p => `${p.id}_${p.framework_id || '0'}`));
      const ordered = availablePillars
        .filter(p => visibleSet.has(`${p.id}_${p.framework_id || '0'}`))
        .map((p, idx) => ({
          id: p.id,
          framework_id: p.framework_id,
          key: p.key,
          name: p.name,
          order_index: typeof p.order_index === 'number' ? p.order_index : idx,
          custom_order: typeof p.custom_order === 'number' ? p.custom_order : idx
        }));
      if (ordered.length > 0) {
        console.log('DBG:INITIAL ORDER→IFRAME', { 
          count: ordered.length, 
          firstFive: ordered.slice(0,5).map(p => `${p.id}_${p.framework_id}`),
          firstFiveDetails: ordered.slice(0,5).map(p => ({ id: p.id, name: p.name, custom_order: p.custom_order }))
        });
        // Send both keys and objects for strict application in iframe
        sendSettingsUpdateToIframe({
          pillars: ordered.map(p => `${p.id}_${p.framework_id || '0'}`),
          orderedPillars: ordered,
          seq: 0,
          initialOrder: true
        });
        initialOrderSentRef.current = true;
      }
    } catch (e) {
      console.warn('DBG:INITIAL ORDER→IFRAME failed', e);
    }
  }, [availablePillars, visiblePillars, sendSettingsUpdateToIframe, orgSlug, organizationData?.pillarSettings]);

  // Handle expanded pillars change and update iframe
  const handleExpandedPillarsChange = useCallback((newExpandedPillars) => {
    console.log('🔄 Expanded pillars changed:', Array.from(newExpandedPillars));
    setExpandedPillars(newExpandedPillars);
    
    // Send update to iframe with a small delay to ensure state is updated
    setTimeout(() => {
      sendSettingsUpdateToIframe({
        expandedPillars: newExpandedPillars
      });
    }, 50); // Reduced from 100ms to 50ms for faster feedback
  }, []);

  // Enhanced settings save handler - integrated with account settings
  const handleSettingsSave = async (settings) => {
    try {
      // Apply theme changes
      await handleThemeChange(settings);
      
      // Save account settings if they exist - integrated into main save flow
      if (settings.organizationName || settings.organizationDescription) {
        console.log('💾 Saving account settings:', {
          name: settings.organizationName,
          description: settings.organizationDescription
        });
        
        // API call to save account settings
        const response = await fetch(`/api/organizations/${orgSlug}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: settings.organizationName,
            description: settings.organizationDescription
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to save account settings');
        }

        const updatedOrganization = await response.json();
        
        // Update local organization data
        setOrganizationData(prev => ({
          ...prev,
          ...updatedOrganization
        }));
        
        console.log('✅ Account settings saved successfully');
      }
      
      
      console.log('✅ All organization settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      throw error; // Re-throw to show error in UI
    }
  };

  // Reset session flag when settings panel opens/closes or when organization settings change
  useEffect(() => {
    if (settingsPanelOpen) {
      setHasFetchedForCurrentSession(false);
    }
  }, [settingsPanelOpen]);

  // Reset session flag when organization settings are loaded for the first time
  useEffect(() => {
    const hasOrgSettings = !!organizationData?.pillarSettings;
    const orgSettingsChanged = hasOrgSettings !== lastOrgSettingsState;
    
    if (orgSettingsChanged) {
      if (isDev) console.log('🔄 Organization settings state changed, resetting fetch flag:', { 
        hadSettings: lastOrgSettingsState, 
        nowHasSettings: hasOrgSettings 
      });
      setLastOrgSettingsState(hasOrgSettings);
    }
  }, [organizationData?.pillarSettings, lastOrgSettingsState, isDev]);

  // Fetch pillars when settings panel opens and we haven't fetched yet
  useEffect(() => {
    const shouldFetchPillars = settingsPanelOpen && !loadingPillars && !hasFetchedForCurrentSession;
    if (shouldFetchPillars) {
      const currentView = currentViewFromPath;
      if (currentView) {
        const fetchKey = `${orgSlug || 'unknown'}:${currentView.id}`;
        const now = Date.now();
        const isDuplicate = lastAutoPillarFetchRef.current.key === fetchKey && (now - lastAutoPillarFetchRef.current.ts) < 1500;
        if (!isDuplicate) {
          lastAutoPillarFetchRef.current = { key: fetchKey, ts: now };
          if (isDev) console.log('🔄 Fetching pillars due to settings panel opening or organization data available');
          fetchPillars(currentView.id);
        } else if (isDev) {
          console.log('DBG:PILLARS FETCH SKIP', { reason: 'duplicate-settings-panel-trigger', fetchKey });
        }
        setHasFetchedForCurrentSession(true);
      }
    }
  }, [settingsPanelOpen, loadingPillars, location.pathname, availableViews, hasFetchedForCurrentSession, isDev]);

  // Always ensure pillars are fetched when org or view changes (independent of panel state)
  useEffect(() => {
    const currentView = currentViewFromPath;
    if (!currentView) {
      if (isDev) console.warn('⚠️ [ALWAYS FETCH] No matching view found. Available:', availableViews.map(v => v.code));
      return;
    }
    if (isDev) console.log('🚀 [ALWAYS FETCH] Triggering pillar fetch for view:', currentView.name);
    const fetchKey = `${orgSlug || 'unknown'}:${currentView.id}`;
    const now = Date.now();
    const isDuplicate = lastAutoPillarFetchRef.current.key === fetchKey && (now - lastAutoPillarFetchRef.current.ts) < 1500;
    if (isDuplicate) {
      if (isDev) console.log('DBG:PILLARS FETCH SKIP', { reason: 'duplicate-always-fetch-trigger', fetchKey });
      return;
    }
    lastAutoPillarFetchRef.current = { key: fetchKey, ts: now };
    // If we already have pillars for this org+view, don't clear them; just trigger a refresh
    fetchPillars(currentView.id);
  }, [orgSlug, location.pathname, availableViews, isDev]);

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} sx={{ color: '#005F94' }} />
      </Box>
    );
  }

  // Password required
  if (passwordRequired) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Card sx={{ maxWidth: 400, p: 3 }}>
          <CardContent>
            <Box textAlign="center" mb={3}>
              <BusinessIcon sx={{ fontSize: 60, color: 'var(--color-primary)', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Protected Organization
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {organizationData?.name || orgSlug}
              </Typography>
            </Box>
            
            <form onSubmit={handlePasswordSubmit}>
              <TextField
                fullWidth
                type="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!passwordError}
                helperText={passwordError}
                sx={{ mb: 2 }}
                autoFocus
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} sx={{ color: '#005F94' }} /> : 'Access Dashboard'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Authentication required (for non-public access)
  if (authenticationRequired && !isPublicAccess) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Alert severity="warning" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>Authentication Required</Typography>
          <Typography variant="body2">
            This organization dashboard requires authentication. Please contact your administrator.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>Error Loading Dashboard</Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box 
      ref={containerRef} 
      className="organization-dashboard"
      sx={{ 
        height: '100vh', 
        display: 'flex', 
        flexDirection: 'column'
      }}
    >
      {/* Enhanced Organization Header - Hidden for public access */}
      {!isPublicAccess && (
        <AppBar position="static" className="organization-header" elevation={0} sx={{ zIndex: 1300 }}>
          <Toolbar>
            <Box display="flex" alignItems="center" gap={2} sx={{ flex: 1 }}>
              {/* Organization Branding - Admin Header (NOT iframe logo) */}
              <Box 
                display="flex" 
                alignItems="center" 
                gap={2} 
                sx={{ 
                  ml: -1,
                  cursor: isSuperAdmin ? 'pointer' : 'default',
                  '&:hover': isSuperAdmin ? {
                    opacity: 0.8,
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 1,
                    transition: 'all 0.2s ease-in-out'
                  } : {}
                }}
                onClick={() => {
                  if (isSuperAdmin) {
                    const adminUrl = import.meta.env.PROD ? '/admin/' : 'http://localhost:3000/admin/';
                    window.location.href = adminUrl;
                  }
                }}
              >
                <img 
                  src="/favico.svg" 
                  alt="Situation Room AI Logo"
                  style={{
                    width: '28px',
                    height: '28px',
                    flexShrink: 0,
                    filter: 'brightness(0) invert(1)'
                  }}
                />
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" component="div" sx={{ color: 'white' }}>
                      {isSuperAdmin && orgSlug === 'global' ? 'Global Workspace' : (organizationData?.name || 'Situation Room')}
                    </Typography>
                    {(isSuperAdmin || user?.organization_role === 'org_admin') && (
                      <Chip
                        label={"Organisation Admin"}
                        size="small"
                        color="warning"
                        variant="filled"
                        sx={{ 
                          fontSize: '0.7rem',
                          height: 20,
                          bgcolor: 'rgba(255, 193, 7, 0.8)',
                          color: 'rgba(0, 0, 0, 0.87)'
                        }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ opacity: 0.8, color: 'white', mt: -0.5 }}>
                    {isSuperAdmin && orgSlug === 'global' ? 'Platform-wide configuration & preview' : 'Organization Dashboard'}
                  </Typography>
                </Box>
              </Box>

              {/* Status Indicator - Moved outside of clickable branding area */}
              {(organizationData?.branding?.showStatusSchedule !== false) && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  ml: 2,
                  mr: 1,
                  height: '32px',
                  minWidth: '80px'
                }}>
                  <SchedulerStatusIndicator style={organizationData?.branding?.statusIndicatorStyle || 'rounded'} />
                </Box>
              )}

              {/* View Selector */}
              <Box sx={{ ml: 'auto', mr: 2 }}>
                {(() => {
                  const pathParts = location.pathname.split('/');
                  const currentViewCode = (pathParts[3] || '').toLowerCase();
                  const selected = resolveViewFromParam(availableViews, currentViewCode);
                  return (
                    <ViewSelector
                      views={availableViews}
                      selectedView={selected}
                      currentViewCode={currentViewCode}
                      onViewChange={() => {
                        // Discard unsaved pillar changes when switching views
                        if (hasPendingChanges) {
                          discardPendingChanges();
                        }
                      }}
                      orgSlug={orgSlug}
                      labelText={orgSlug === 'global' ? 'All Views' : 'View'}
                    />
                  );
                })()}
              </Box>
            </Box>

            {/* Super Admin Controls - Grouped Together */}
            {isSuperAdmin && !isPublicAccess && (
              <Box 
                display="flex" 
                gap={1} 
                sx={{ 
                  mr: 3, 
                  p: 0.5,
                  bgcolor: 'rgba(96, 125, 139, 0.08)',
                  borderRadius: 1,
                  border: '1px solid rgba(96, 125, 139, 0.2)'
                }}
                className="super-admin-controls"
              >
                <Tooltip title="Go to Super Admin Dashboard">
                  <Button
                    color="inherit"
                    startIcon={<SuperAdminIcon />}
                    onClick={() => {
                      const adminUrl = import.meta.env.PROD ? '/admin/' : 'http://localhost:3000/admin/';
                      window.location.href = adminUrl;
                    }}
                    size="small"
                    sx={{
                      textTransform: 'none',
                      bgcolor: 'rgba(96, 125, 139, 0.15)',
                      border: '1px solid rgba(96, 125, 139, 0.3)',
                      '&:hover': {
                        bgcolor: 'rgba(96, 125, 139, 0.25)'
                      },
                      minWidth: 'auto'
                    }}
                  >
                    Super Admin
                  </Button>
                </Tooltip>
                
                {allOrganizations.length > 0 && (
                  <Tooltip title="Switch Organization">
                    <Button
                      color="inherit"
                      startIcon={<SwitchIcon />}
                      endIcon={<ArrowDownIcon />}
                      onClick={() => setOrgSwitcherOpen(true)}
                      className="super-admin-switcher"
                      sx={{
                        textTransform: 'none',
                        bgcolor: 'rgba(96, 125, 139, 0.15)',
                        border: '1px solid rgba(96, 125, 139, 0.3)',
                        '&:hover': {
                          bgcolor: 'rgba(96, 125, 139, 0.25)'
                        },
                        minWidth: 'auto'
                      }}
                    >
                      Switch
                    </Button>
                  </Tooltip>
                )}
              </Box>
            )}

            {/* Regular Control Buttons */}
            <Box display="flex" gap={1} className="organization-controls">
              <Tooltip title="Dashboard Settings Panel">
                <IconButton 
                  color="inherit" 
                  onClick={() => setSettingsPanelOpen(!settingsPanelOpen)}
                  size="small"
                  className="settings-panel-button"
                  data-testid="dashboard-settings-button"
                  aria-label="Dashboard Settings Panel"
                  sx={{ 
                    bgcolor: settingsPanelOpen ? 'rgba(255,255,255,0.2)' : 'transparent'
                  }}
                >
                  <PanelIcon />
                </IconButton>
              </Tooltip>

              {/* Admin/Login Controls */}
              <Box sx={{ ml: 1 }}>
                <AuthNavItem />
              </Box>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      {/* Dashboard Routes */}
      <Box 
        className="dashboard-content-transition"
        sx={{ 
          flex: 1, 
          position: 'relative', 
          overflow: 'hidden',
          height: isPublicAccess ? '100vh' : 'auto', // Full height for public access
          pr: settingsPanelOpen && !isPublicAccess ? {
            xs: 0,
            sm: 0, 
            md: '320px',
            lg: '360px'
          } : 0,
          transition: 'padding-right 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <Routes>
          <Route path="/" element={
            viewFromParams ? (
              <OrganizationViewDashboard 
                orgSlug={orgSlug} 
                organizationData={organizationData}
                availableViews={availableViews}
                iframeRef={iframeRef}
                isPublicAccess={isPublicAccess}
                isSuperAdmin={isSuperAdmin}
              />
            ) : availableViews.length > 0 ? (
              <Navigate to={availableViews[0].code?.toLowerCase() || ''} replace />
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography variant="h6" color="textSecondary">No views assigned to this organization.</Typography>
              </Box>
            )
          } />
          <Route path="/:view/*" element={
            <OrganizationViewDashboard 
              orgSlug={orgSlug} 
              organizationData={organizationData}
              availableViews={availableViews}
              iframeRef={iframeRef}
              isPublicAccess={isPublicAccess}
              isSuperAdmin={isSuperAdmin}
            />
          } />
        </Routes>
      </Box>

      {/* Settings Panel and Modals - Only for authenticated users */}
      {!isPublicAccess && (
        <>
          <DashboardSettingsPanel
            open={settingsPanelOpen}
            onClose={() => setSettingsPanelOpen(false)}
            organizationData={organizationData}
            orgSlug={orgSlug}
            onSave={handleSettingsSave}
            availableViews={availableViews}
            selectedView={resolveViewFromParam(availableViews, (location.pathname.split('/')[3] || '').toLowerCase())}
            availablePillars={availablePillars}
            visiblePillars={visiblePillars}
            onPillarToggle={handlePillarToggle}
            onPillarOrderChange={handlePillarOrderChange}
            onExpandedPillarsChange={handleExpandedPillarsChange}
            iframeRef={iframeRef}
            hasPendingChanges={hasPendingChanges}
            pendingChanges={pendingChanges}
            savePendingChanges={savePendingChanges}
            discardPendingChanges={discardPendingChanges}
            setOrganizationData={setOrganizationData}
            sendSettingsUpdateToIframe={sendSettingsUpdateToIframe}
            setVisiblePillars={setVisiblePillars}
            handleResetToAdminDefault={handleResetToAdminDefault}
            isSuperAdmin={isSuperAdmin}
          />

          <ShareDashboardModal
            open={shareModalOpen}
            onClose={() => setShareModalOpen(false)}
            orgSlug={orgSlug}
            view={currentViewFromPath?.code || location.pathname.split('/')[3]}
            organizationData={organizationData}
          />

          {/* Super Admin Organization Switcher Dialog */}
          <Dialog
            open={orgSwitcherOpen}
            onClose={() => setOrgSwitcherOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={1}>
                <SuperAdminIcon color="primary" />
                <Typography variant="h6">Switch Organization</Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Switch to view what other organizations see. You'll maintain the current view view.
              </Typography>
              
              <Stack spacing={1}>
                {allOrganizations.map((org) => (
                  <Card 
                    key={org.id}
                    sx={{ 
                      cursor: 'pointer',
                      border: org.slug === orgSlug ? '2px solid' : '1px solid',
                      borderColor: org.slug === orgSlug ? 'var(--color-primary)' : 'divider',
                      bgcolor: org.slug === orgSlug ? 'action.selected' : 'background.paper',
                      '&:hover': {
                        bgcolor: org.slug === orgSlug ? 'action.selected' : 'action.hover'
                      }
                    }}
                    onClick={() => handleOrganizationSwitch(org.slug)}
                  >
                    <CardContent sx={{ py: 1.5 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar sx={{ width: 40, height: 40 }}>
                            <BusinessIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {org.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {org.slug} • {org.view_count} views • {org.user_count} users
                            </Typography>
                          </Box>
                        </Box>
                        {org.slug === orgSlug && (
                          <Chip 
                            label="Current" 
                            color="primary" 
                            size="small"
                            variant="filled"
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOrgSwitcherOpen(false)}>
                Cancel
              </Button>
            </DialogActions>
          </Dialog>

          {/* User Management Dialogs - MOVED TO DashboardSettingsPanel
          <Dialog
            open={false}
            onClose={() => {}}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={1}>
                <PersonAddIcon color="primary" />
                <Typography variant="h6">Invite User</Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={3} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  label="Name"
                  value=""
                  onChange={() => {}}
                  placeholder="John Doe"
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value=""
                  onChange={() => {}}
                  placeholder="john@organization.com"
                />
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value="member"
                    label="Role"
                    onChange={() => {}}
                  >
                    <MenuItem value="member">User</MenuItem>
                    <MenuItem value="editor">Editor</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => {}}>Cancel</Button>
              <Button 
                onClick={() => {}}
                variant="contained"
                disabled={true}
              >
                Send Invite
              </Button>
            </DialogActions>
          </Dialog>

          {/* User Management Dialog removed - functionality in DashboardSettingsPanel */}
        </>
      )}
    </Box>
  );
});

/**
 * Organization View Dashboard Component
 * Embeds the existing dashboard with organization branding
 */
// Resolve a view from a URL param using flexible matching and common aliases
const resolveViewFromParam = (views, viewParam) => {
  const param = (viewParam || '').toLowerCase();
  if (!param) return undefined;
  // Common aliases for frequent mismatches between codes and slugs
  const aliasMap = {
    mmr: 'myanmar',
    mya: 'myanmar',
    mm: 'myanmar',
    sy: 'syria',
    syr: 'syria',
    cd: 'congo',
    cod: 'congo',
  };
  const aliasName = aliasMap[param] || param;
  // 1) Exact code match
  let found = views.find(c => (c.code || '').toLowerCase() === param);
  if (found) return found;

  // 2) Exact ISO3 match
  found = views.find(c => (c.iso3 || '').toLowerCase() === param);
  if (found) return found;

  // 3) Name/slug match
  found = views.find(c => (c.name || '').toLowerCase() === param || (c.slug || '').toLowerCase() === param);
  if (found) return found;

  // 4) Alias match fallback
  if (aliasName && aliasName !== param) {
    found = views.find(c => (c.code || '').toLowerCase() === aliasName);
    if (found) return found;
    found = views.find(c => (c.iso3 || '').toLowerCase() === aliasName);
    if (found) return found;
    found = views.find(c => (c.name || '').toLowerCase() === aliasName || (c.slug || '').toLowerCase() === aliasName);
    if (found) return found;
  }

  return undefined;
};

const OrganizationViewDashboard = React.memo(({ orgSlug, organizationData, availableViews, iframeRef, isPublicAccess, isSuperAdmin }) => {
  const { view = '' } = useParams();
  const location = useLocation();

  const [iframeLoading, setIframeLoading] = useState(true);
  const [fallbackView, setFallbackView] = useState(null);

  const isDev = import.meta.env.DEV;

  // Handshake cache: store the last settings payload we attempted to send to the iframe.
  // The iframe may not have its message listener installed yet on first load, so we resend on IFRAME_READY.
  const lastIframeSettingsRef = useRef(null);
  const lastLoggedSelectedViewRef = useRef(null);
  const lastLoggedDashboardUrlRef = useRef(null);
  
  // Find the selected view data assigned to this organization
  const selectedView = useMemo(() => resolveViewFromParam(availableViews, view), [availableViews, view]);

  useEffect(() => {
    if (!isDev) {
      return;
    }

    const nextLogKey = JSON.stringify({
      searchFor: view || '',
      id: selectedView?.id || null,
      code: selectedView?.code || null,
      iso3: selectedView?.iso3 || null,
      name: selectedView?.name || null
    });

    if (lastLoggedSelectedViewRef.current === nextLogKey) {
      return;
    }

    lastLoggedSelectedViewRef.current = nextLogKey;
    console.log('🎯 View search result:', { searchFor: view, selectedView });
  }, [isDev, selectedView, view]);

  // For super admins, if the view is not assigned to this org, try a global fallback
  useEffect(() => {
    let cancelled = false;
    const fetchGlobalView = async () => {
      try {
        if (!selectedView && isSuperAdmin) {
          const res = await fetch('/api/views');
          if (res.ok) {
            const all = await res.json();
            const normalized = all.map(c => ({
              id: c.id || c.code || c.iso3,
              name: c.name,
              code: (c.code || c.iso3 || '').toLowerCase()
            }));
            const found = resolveViewFromParam(normalized, view);
            if (!cancelled) setFallbackView(found || null);
          } else if (!cancelled) {
            setFallbackView(null);
          }
        } else if (!cancelled) {
          setFallbackView(null);
        }
      } catch (_e) {
        if (!cancelled) setFallbackView(null);
      }
    };
    fetchGlobalView();
    return () => { cancelled = true; };
  }, [selectedView, isSuperAdmin, view]);

  const effectiveView = selectedView || fallbackView;

  // Prefer resolved code from selectedView when URL param is a name (e.g., 'myanmar')
  const candidateCode = (selectedView?.code || '').toLowerCase();
  const candidateIso3 = (selectedView?.iso3 || '').toLowerCase();
  const pathView = (
    candidateCode && candidateCode.length === 3 ? candidateCode :
    (candidateIso3 && candidateIso3.length === 3 ? candidateIso3 :
      (candidateCode || view || '')
    )
  ).toLowerCase();

  // CRITICAL: This useEffect must be called before any early returns to comply with React hooks rules
  useEffect(() => {
    // Only reset loading when the view/org changes (not when auth role resolves)
    setIframeLoading(true);
  }, [orgSlug, pathView]);

  // Auto-redirect to first available view when current view doesn't exist in this organization
  const navigate = useNavigate();
  useEffect(() => {
    // Only redirect if:
    // 1. We have available views to redirect to
    // 2. The current view doesn't exist in this org (no selectedView)
    // 3. Super admin fallback also failed (no fallbackView)
    // 4. We're not still loading the fallback (give it time to resolve)
    if (!selectedView && !fallbackView && availableViews.length > 0 && !isSuperAdmin) {
      const firstView = availableViews[0];
      const targetViewCode = (firstView.code || firstView.iso3 || firstView.name || '').toLowerCase();
      if (targetViewCode && targetViewCode !== view.toLowerCase()) {
        console.log(`🔄 Auto-redirecting from unavailable view "${view}" to first available view "${targetViewCode}"`);
        navigate(`/org/${orgSlug}/${targetViewCode}`, { replace: true });
      }
    }
  }, [selectedView, fallbackView, availableViews, view, orgSlug, navigate, isSuperAdmin]);

  // Force view for Global Workspace OR when user is super admin to include non-visible views
  const forceParam = (orgSlug === 'global' || (isSuperAdmin && !selectedView)) ? '&force=true' : '';

  // STABILIZE iframe URL: only regenerate when org/view changes, NOT when branding (headerTitle) changes.
  // Title updates after initial load are sent via postMessage — changing the URL would cause a full iframe reload.
  // IMPORTANT: When the VIEW changes, use the view's own name for the URL title (not stale branding from previous view).
  const stableUrlRef = useRef({ key: null, url: null });
  const urlKey = effectiveView ? `${orgSlug}:${pathView}` : null;
  if (urlKey && stableUrlRef.current.key !== urlKey) {
    const viewTitle = effectiveView?.name || organizationData?.name || 'Situation Room AI';
    const viewTitleParam = `&headerTitle=${encodeURIComponent(viewTitle)}`;
    const freshUrl = `${window.location.origin}/${pathView}?orgSlug=${orgSlug}&orgIframe=true${forceParam}${viewTitleParam}`;
    stableUrlRef.current = { key: urlKey, url: freshUrl };
  }
  const dashboardUrl = effectiveView ? stableUrlRef.current.url : null;

  // Dev-only: propagate ?test-user=... to iframe URL
  const testUserParam = import.meta.env.DEV ? (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const tu = p.get('test-user');
      return tu ? `&test-user=${encodeURIComponent(tu)}` : '';
    } catch (_e) {
      return '';
    }
  })() : '';
  const dashboardUrlWithAuth = dashboardUrl ? `${dashboardUrl}${testUserParam}` : null;

  useEffect(() => {
    if (!isDev || !dashboardUrl) {
      return;
    }

    const nextLogKey = `${orgSlug}:${view || ''}:${dashboardUrl}`;
    if (lastLoggedDashboardUrlRef.current === nextLogKey) {
      return;
    }

    lastLoggedDashboardUrlRef.current = nextLogKey;
    console.log('🖼️ [OrganizationViewDashboard] Generated clean iframe URL:', {
      dashboardUrl,
      orgSlug,
      view: (view || '').toLowerCase()
    });
  }, [dashboardUrl, isDev, orgSlug, view]);

  // Show "View Not Available" if:
  // 1. No effectiveView at all, OR
  // 2. The view is not assigned to this organization (selectedView is null) - even for super admins
  const viewNotAssignedToOrg = !selectedView && availableViews.length > 0;
  
  if (!effectiveView || viewNotAssignedToOrg) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        flexDirection="column"
        gap={2}
      >
        <Alert severity="warning" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            View Not Available
          </Typography>
          <Typography variant="body2">
            The view "{view}" is not available for organization "{orgSlug}".
            {isSuperAdmin && fallbackView && ' (Super admin: this view exists globally but is not assigned to this organization.)'}
          </Typography>
          {availableViews.length > 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Available views for this organization: {availableViews.map(v => v.name || v.code).join(', ')}
            </Typography>
          )}
        </Alert>
      </Box>
    );
  }

  // Create the iframe URL for the existing dashboard with theme parameters
  const themeParams = organizationData?.branding ? 
    OrganizationThemeGenerator.generateIframeThemeParams(organizationData.branding) : '';
  
  // Create organization branding object for the iframe
  const orgBranding = {
    logo: organizationData?.branding?.logo,
    title: organizationData?.branding?.title || organizationData?.name,
    subtitle: organizationData?.branding?.subtitle,
    viewTitle: organizationData?.branding?.viewTitles?.[view] || effectiveView?.name,
    fontFamily: organizationData?.branding?.fontFamily,
    // Add header-specific parameters with proper fallbacks
    headerTitle: organizationData?.branding?.headerTitle || effectiveView?.name || organizationData?.name || 'Situation Room AI',
    headerSubtitle: organizationData?.branding?.headerSubtitle || 'Humanitarian Dashboard',
    headerLogoStyle: organizationData?.branding?.headerLogoStyle || 'normal'
  };
  
  // Add pillar visibility settings
  const orgPillarSettings = organizationData?.pillarSettings?.[view] || [];

  return (
    <Box sx={{ 
      height: '100%', 
      p: isPublicAccess ? 0 : { xs: 0, md: 2 } // No padding for public views
    }}>
      {/* Dashboard Card */}
      <Card 
        className="organization-dashboard-card"
        sx={{ 
          height: '100%',
          display: 'flex', 
          flexDirection: 'column',
          boxShadow: isPublicAccess ? 0 : undefined, // No shadow for public views
          borderRadius: isPublicAccess ? 0 : undefined // No border radius for public views
        }}
      >
        <CardContent sx={{ flex: 1, p: 0, '&:last-child': { pb: 0 }, position: 'relative' }}>
          {/* Loading Overlay */}
          {iframeLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.50',
                zIndex: 1,
                borderRadius: isPublicAccess ? 0 : 2
              }}
            >
              <Box textAlign="center">
                <CircularProgress size={40} sx={{ mb: 2, color: '#005F94' }} />
                <Typography variant="body2" color="textSecondary">
                  Loading {effectiveView?.name || view} dashboard...
                </Typography>
              </Box>
            </Box>
          )}
          
          {/* Embedded Dashboard */}
          <iframe
            ref={iframeRef}
            src={dashboardUrlWithAuth}
            className="organization-dashboard-iframe dashboard-iframe"
            data-testid="organization-dashboard-iframe"
            data-org={orgSlug}
            data-view={view}
            title={`${organizationData?.name || 'Organization'} Dashboard - ${selectedView?.name || view}`}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              borderRadius: isPublicAccess ? '0' : '4px',
              transition: 'opacity 0.3s ease',
              opacity: iframeLoading ? 0 : 1
            }}
            onLoad={() => setIframeLoading(false)}
            onError={() => setIframeLoading(false)}
          />
        </CardContent>
      </Card>

    </Box>
  );
});

export default OrganizationDashboard; 